import {
  Component,
  inject,
  signal,
  effect,
  computed,
  ElementRef,
  viewChild,
  PLATFORM_ID,
  OnDestroy,
} from "@angular/core";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { CommonModule, Location, isPlatformBrowser } from "@angular/common";
import { ActivatedRoute, RouterModule, Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { animate, stagger } from "motion";
import { AuthService } from "./auth.service";
import { ChatService, Chat } from "./services/chat.service";
import { FollowService } from "./services/follow.service";
import { OrganizationDataService } from "./services/organization-data.service";
import { ProgressService } from "./services/progress.service";
import {
  doc,
  getDoc,
  Timestamp,
  FieldValue,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  addDoc,
  deleteDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
  collectionGroup
} from "firebase/firestore";
import { debounceTime, distinctUntilChanged, switchMap, map, from, of, combineLatest, Observable } from "rxjs";
import { db, storage, handleFirestoreError, OperationType } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { AuthProfile, AccountStatus } from "./models/auth.model";
import { UserImage } from "./models/image.model";
import { ImagePanDirective } from "./directives/image-pan.directive";
import { Header } from "./header";
import { CountFormatPipe } from "./count-format.pipe";
import { AddImage } from "./add_image";
import { ChangeProfilePhoto } from "./changeProfilePhoto";
import { PersonCard } from "./person-card";
import { OrganizationCard } from "./organization-card";
import { CountryCard } from "./country-card";
import { KicgleIdCard } from "./kicgle-id-card";
import { SwipeTabSyncDirective } from "./directives/swipe-tab-sync.directive";

@Component({
  selector: "app-profile",
  imports: [CommonModule, RouterModule, MatIconModule, FormsModule, ReactiveFormsModule, Header, ImagePanDirective, CountFormatPipe, AddImage, ChangeProfilePhoto, PersonCard, OrganizationCard, CountryCard, KicgleIdCard, SwipeTabSyncDirective],
  styles: [
    `
      @reference "../styles.css";
      .profile-header-card {
        @apply bg-white/40 dark:bg-zinc-900/40 backdrop-blur-3xl border border-white/20 dark:border-white/10 rounded-[48px] overflow-hidden shadow-ios relative;
      }
      .profile-header-card::before {
        content: "";
        @apply absolute inset-0 bg-gradient-to-tr from-ios-blue/10 via-transparent to-ios-purple/10 pointer-events-none opacity-50;
      }
      .metric-card {
        @apply bg-black/5 dark:bg-white/5 rounded-3xl p-6 flex flex-col items-center justify-center transition-all hover:scale-[1.02] active:scale-[0.98] border border-black/5 dark:border-white/5 relative overflow-hidden;
      }
      .metric-card .liquid-bg {
        @apply absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-700 pointer-events-none;
      }
      .metric-value {
        @apply text-xl sm:text-2xl font-black tracking-tighter leading-none relative z-10 lowercase;
      }
      .bento-card {
        @apply bg-white/50 dark:bg-zinc-900/50 backdrop-blur-3xl border border-black/5 dark:border-white/5 rounded-[48px] shadow-ios-md overflow-hidden transition-all relative;
      }
      .bento-card-liquid {
        @apply absolute -right-20 -top-20 w-48 h-48 rounded-full blur-[60px] pointer-events-none;
        animation: liquid-pulse 4s infinite ease-in-out;
      }
      .ios-list-group {
        @apply bg-white/60 dark:bg-zinc-900/60 backdrop-blur-2xl rounded-[32px] border border-black/5 dark:border-white/10 overflow-hidden shadow-ios-sm;
      }
      .ios-list-item {
        @apply flex items-center justify-between w-full px-6 transition-all hover:bg-black/5 dark:hover:bg-white/5 border-b border-black/5 dark:border-white/5 last:border-0;
      }
      .section-header {
        @apply px-6 flex items-center gap-3 mb-4;
      }
      .section-title {
        @apply text-[10px] font-black uppercase tracking-[0.35em] opacity-30;
      }
      .ios-chip-container {
        @apply max-w-7xl mx-auto w-full flex items-center gap-4 overflow-x-auto no-scrollbar scroll-smooth p-2;
      }
      .badge-item {
        @apply flex items-center gap-2 px-4 py-2 rounded-2xl border transition-all hover:scale-[1.05] cursor-default bg-white/50 dark:bg-zinc-900/50 backdrop-blur-md;
      }
    `,
  ],
  template: `
    <div class="min-h-screen flex flex-col relative overflow-hidden bg-transparent">

      <!-- Header -->
      <app-header 
        [title]="profile()?.displayName || 'User'" 
        [subtitle]="profile()?.username || 'username'" 
        [imageUrl]="profile()?.photoURL || null" 
        iconName="account_circle"
        [isVerified]="!!profile()?.emailVerified"
        (back)="goBack()">
        <div actions class="relative z-10 flex items-center gap-2 shrink-0">
          <button
            (click)="openConfirmModal('signout')" 
            class="h-9 sm:h-10 px-3.5 sm:px-4 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 backdrop-blur-xl transition-all active:scale-95 flex items-center gap-1.5 text-xs font-bold shadow-sm cursor-pointer"
            title="Sign Out"
          >
            <mat-icon class="!text-[18px]">logout</mat-icon>
            <span class="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </app-header>

      <!-- Sub-Header for Navigation Tabs -->
      <div class="ios-sub-header-liquid">
        <div class="chip-scroll-container" #profileChips>
          @for (chip of chips; track chip.label) {
            <button
              (click)="selectTab(chip.label)"
              class="ios-liquid-chip"
              [class.active]="selectedTab() === chip.label"
              [id]="'tab-' + chip.label"
            >
              <mat-icon
                class="text-[18px] transition-transform group-active:scale-110"
                >{{ chip.icon }}</mat-icon
              >
              <span class="tracking-tight">{{ chip.label }}</span>
            </button>
          }
        </div>
      </div>

      <main
        class="flex-1 pt-[176px] pb-12 px-4 md:px-8 lg:px-12 max-w-7xl mx-auto w-full animate-ios-fade relative z-10"
        #profileContent
        appSwipeTabSync
        [tabs]="chips"
        [activeTab]="selectedTab()"
        [chipContainer]="profileChips"
        chipIdPrefix="tab-"
        (activeTabChange)="selectTab($event)"
      >
        @if (profile()) {
          <div class="max-w-4xl mx-auto space-y-8">
            @if (selectedTab() === "About") {
              <!-- Profile Identity Overview -->
              <div
                class="profile-header-card animate-ios-slide group overflow-hidden"
                style="animation-delay: 0.1s"
              >
                <!-- Atmospheric Header -->
                <div
                  class="h-auto md:h-56 bg-transparent relative flex items-center gap-3 w-full p-6 md:p-8 overflow-hidden pt-12 md:pt-8"
                >
                  <div
                    class="absolute inset-0 bg-white/10 dark:bg-black/20 backdrop-blur-md"
                  ></div>
                  
                  @if (profile()?.coverURL) {
                    <img [src]="profile()?.coverURL" class="absolute inset-0 w-full h-full object-cover" referrerpolicy="no-referrer" alt="Cover" />
                  }

                  <div
                    class="absolute top-[-50%] left-[-20%] w-[140%] h-[200%] bg-ios-blue/50 blur-[80px] rounded-full animate-liquid"
                  ></div>
                  <div
                    class="absolute bottom-[-20%] right-[-10%] w-[100%] h-[150%] bg-ios-purple/50 blur-[80px] rounded-full animate-liquid-pulse"
                    style="animation-duration: 8s"
                  ></div>
                  <div class="absolute inset-0 backdrop-blur-[6px] bg-black/10"></div>

                  @if (false) {
                    <button 
                      (click)="coverPhotoInput.click()"
                      class="absolute top-6 right-6 h-10 w-10 flex items-center justify-center bg-white/20 dark:bg-black/40 backdrop-blur-md border border-white/20 rounded-full text-white hover:bg-white/40 transition-all z-20 shadow-xl active:scale-90"
                      title="Update Cover Photo"
                    >
                      <mat-icon class="!text-xl">camera_enhance</mat-icon>
                    </button>
                    <input #coverPhotoInput type="file" class="hidden" (change)="onCoverPhotoSelected($event)" accept="image/*" />
                  }

                  <div
                    class="relative shrink-0 transform group-hover:scale-105 transition-transform duration-700 z-10"
                    [class.w-36]="profile()?.accountType === 'country'"
                    [class.h-24]="profile()?.accountType === 'country'"
                    [class.md:w-48]="profile()?.accountType === 'country'"
                    [class.md:h-32]="profile()?.accountType === 'country'"
                    [class.w-32]="profile()?.accountType !== 'country'"
                    [class.h-32]="profile()?.accountType !== 'country'"
                    [class.md:w-40]="profile()?.accountType !== 'country'"
                    [class.md:h-40]="profile()?.accountType !== 'country'"
                  >
                    <div
                      class="absolute inset-0 bg-gradient-to-tr from-ios-blue to-ios-purple opacity-70 blur-xl pointer-events-none group-hover:opacity-100 transition-opacity"
                      [class.rounded-none]="profile()?.accountType === 'country'"
                      [class.rounded-full]="profile()?.accountType !== 'country'"
                      [class.animate-liquid]="profile()?.accountType !== 'country'"
                    ></div>
                    <div
                      class="relative w-full h-full bg-white/10 backdrop-blur-2xl p-1 shadow-2xl border border-white/20"
                      [class.rounded-none]="profile()?.accountType === 'country'"
                      [class.rounded-full]="profile()?.accountType !== 'country'"
                    >
                      <div
                        class="w-full h-full overflow-hidden bg-white/10 relative group/avatar"
                        [class.rounded-none]="profile()?.accountType === 'country'"
                        [class.rounded-full]="profile()?.accountType !== 'country'"
                      >
                        @if (profile()?.photoURL) {
                          <img
                            [src]="profile()?.photoURL"
                            alt="Profile"
                            class="w-full h-full object-cover"
                            [class.rounded-none]="profile()?.accountType === 'country'"
                            referrerpolicy="no-referrer"
                          />
                        } @else {
                          <div
                            class="w-full h-full flex items-center justify-center text-4xl font-black text-white bg-gradient-to-tr from-ios-blue to-purple-500"
                            [class.rounded-none]="profile()?.accountType === 'country'"
                          >
                            {{
                              (profile()?.displayName || "U")
                                .charAt(0)
                                .toUpperCase()
                            }}
                          </div>
                        }

                        @if (canManageOrganization() || isOwner()) {
                          <div
                            (click)="isChangeProfilePhotoOpen.set(true)"
                            class="absolute inset-0 bg-black/40 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer"
                          >
                            <mat-icon class="!text-2xl">camera_alt</mat-icon>
                            <span class="text-[8px] font-bold uppercase tracking-widest mt-1">Edit Photo</span>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                  <div
                    class="flex-1 flex flex-col items-start text-left relative z-10 pb-0"
                  >
                    <div
                      class="flex items-start gap-2 text-left w-full max-w-full"
                    >
                      <h2
                        class="text-white font-semibold text-base sm:text-lg leading-tight whitespace-normal break-words w-full max-w-full drop-shadow-md"
                      >
                        {{ formatTitleCase(profile()?.displayName) }}
                      </h2>
                    </div>
                    <p
                      class="text-white/90 font-medium text-xs sm:text-sm leading-normal mt-0.5 overflow-visible pb-1 lowercase drop-shadow-sm"
                    >
                      @{{ profile()?.username || "username" }}
                    </p>
                  </div>
                </div>

                <div
                  class="p-8 sm:p-10 bg-white/50 dark:bg-zinc-900/50 backdrop-blur-3xl"
                >
                  <div class="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
                    <!-- Followers Button -->
                    <button 
                      (click)="openFollowersSheet()" 
                      class="metric-card group cursor-pointer w-full border border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5 rounded-xl p-4 sm:p-5 transition-all hover:scale-[1.02] active:scale-[0.98] relative flex flex-col items-center justify-center gap-2 shadow-sm"
                    >
                      <span class="metric-value text-ios-blue text-2xl sm:text-3xl font-black drop-shadow-sm group-hover:scale-105 transition-transform duration-300 relative z-10">
                        {{ followersCount() }}
                      </span>
                      <div class="flex items-center justify-center text-slate-700 dark:text-zinc-300 relative z-10">
                        <span class="text-[11px] font-black uppercase tracking-[0.2em] opacity-90">
                          Followers
                        </span>
                      </div>
                    </button>

                    <!-- Following Button -->
                    <button 
                      (click)="openFollowingSheet()" 
                      class="metric-card group cursor-pointer w-full border border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5 rounded-xl p-4 sm:p-5 transition-all hover:scale-[1.02] active:scale-[0.98] relative flex flex-col items-center justify-center gap-2 shadow-sm"
                    >
                      <span class="metric-value text-ios-purple text-2xl sm:text-3xl font-black drop-shadow-sm group-hover:scale-105 transition-transform duration-300 relative z-10">
                        {{ followingCount() }}
                      </span>
                      <div class="flex items-center justify-center text-slate-700 dark:text-zinc-300 relative z-10">
                        <span class="text-[11px] font-black uppercase tracking-[0.2em] opacity-90">
                          Following
                        </span>
                      </div>
                    </button>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex flex-row flex-wrap items-center gap-2 sm:gap-3 w-full justify-start">
                    @if (authService.user()?.uid !== profile()?.uid) {
                      <!-- Follow / Following Button -->
                      <button
                        (click)="toggleFollow()"
                        [disabled]="isFollowLoading()"
                        class="h-11 px-5 text-xs sm:text-sm font-extrabold rounded-2xl bg-ios-blue text-white shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 border border-white/20 cursor-pointer"
                      >
                        <mat-icon class="!text-[18px]">{{ isFollowing() ? 'person_remove' : 'person_add' }}</mat-icon>
                        <span class="tracking-tight">{{ isFollowing() ? "Following" : "Follow" }}</span>
                      </button>

                      @if (profile()?.accountType !== 'country') {
                        @if (isInContacts()) {
                          <!-- Chat / Message Button (Active for contacts) -->
                          <button
                            (click)="startChat()"
                            class="w-11 h-11 rounded-2xl bg-[#00a884] hover:bg-[#008f70] text-white border border-[#00a884]/25 transition-all active:scale-95 flex items-center justify-center shadow-md shadow-[#00a884]/20 cursor-pointer"
                            title="Message"
                          >
                            <mat-icon class="!text-[20px]">chat</mat-icon>
                          </button>
                        } @else {
                          <!-- Disabled Chat Button on non-contact's profile: shows Material Liquid Glass popup when clicked -->
                          <button
                            type="button"
                            (click)="openNonContactChatModal()"
                            class="w-11 h-11 rounded-2xl bg-zinc-200/90 dark:bg-zinc-800/90 hover:bg-zinc-300 dark:hover:bg-zinc-700/80 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 border border-black/5 dark:border-white/10 transition-all active:scale-95 flex items-center justify-center cursor-pointer shadow-xs group relative"
                            title="Chat disabled: Add person to contacts to message"
                            aria-label="Chat disabled: Add person to contacts to message"
                          >
                            <mat-icon class="!text-[20px] transition-transform group-hover:scale-110">chat_bubble_outline</mat-icon>
                            <div class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-xs border-2 border-white dark:border-zinc-900">
                              <mat-icon class="!text-[9px] font-black leading-none">lock</mat-icon>
                            </div>
                          </button>
                        }
                      }
                    } @else {
                      @if (profile()?.accountType !== 'country') {
                        <!-- Chat / Self-Note Button for Own Profile (Omitted for Country Profiles) -->
                        <button
                          (click)="startChat()"
                          class="h-11 px-5 rounded-2xl bg-[#00a884]/10 hover:bg-[#00a884]/20 text-[#00a884] dark:text-[#25d366] border border-[#00a884]/20 transition-all active:scale-95 flex items-center gap-2 text-xs sm:text-sm font-bold shadow-sm cursor-pointer"
                          title="Self Chat"
                        >
                          <mat-icon class="!text-[18px]">chat</mat-icon>
                          <span>Message (Self Note)</span>
                        </button>
                      }
                    }

                    @if (isCurrentLoggedInUser() && profile()?.accountType !== 'country') {
                      <!-- ID Card Button (Icon Only - Rendered strictly for authenticated current logged-in user) -->
                      <button
                        type="button"
                        (click)="isIdCardOpen.set(true)"
                        class="h-11 w-11 rounded-2xl bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-slate-800 dark:text-white border border-black/5 dark:border-white/10 transition-all active:scale-95 flex items-center justify-center shadow-sm cursor-pointer"
                        title="View Kicgle ID Card"
                        aria-label="View Kicgle ID Card"
                      >
                        <mat-icon class="!text-[20px]">badge</mat-icon>
                      </button>
                    }
                  </div>
                </div>
              </div>

              <!-- Verification Center (If Pending) -->
              @if (profile()?.status === AccountStatus.PENDING) {
                <div
                  class="bento-card relative p-8 border-transparent bg-zinc-900 overflow-hidden animate-ios-slide group"
                  style="animation-delay: 0.3s"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-br from-ios-blue/20 to-ios-purple/20"
                  ></div>
                  <div
                    class="absolute top-[-50%] left-[-20%] w-[140%] h-[200%] bg-ios-blue/30 blur-[60px] rounded-full animate-liquid"
                  ></div>
                  <div
                    class="absolute bottom-[-20%] right-[-10%] w-[100%] h-[150%] bg-ios-purple/30 blur-[60px] rounded-full animate-liquid-pulse"
                    style="animation-duration: 8s"
                  ></div>
                  <div class="absolute inset-0 backdrop-blur-[12px]"></div>

                  <div class="relative z-10 flex items-center gap-5 mb-8">
                    <div
                      class="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white shadow-ios-lg"
                    >
                      <mat-icon class="!text-[32px]">security</mat-icon>
                    </div>
                    <div class="text-white">
                      <h4
                        class="text-xl font-bold tracking-tight drop-shadow-md"
                      >
                        Account Verification
                      </h4>
                      <p
                        class="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 drop-shadow-sm"
                      >
                        Required Steps
                      </p>
                    </div>
                  </div>

                  <div
                    class="ios-list-group relative z-10 bg-white/5 backdrop-blur-xl border-white/10 text-white divide-white/10"
                  >
                    <div
                      class="ios-list-item h-[72px] bg-transparent border-0 hover:bg-white/5"
                    >
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center shadow-sm"
                          [class.text-ios-green]="
                            authService.user()?.emailVerified
                          "
                          [class.text-white]="
                            !authService.user()?.emailVerified
                          "
                        >
                          <mat-icon class="!text-[20px] drop-shadow-sm">
                            {{
                              authService.user()?.emailVerified
                                ? "check_circle"
                                : "alternate_email"
                            }}
                          </mat-icon>
                        </div>
                        <span
                          class="text-[17px] font-semibold tracking-tight drop-shadow-sm"
                          >Email Address</span
                        >
                      </div>
                      @if (!authService.user()?.emailVerified) {
                        <button
                          (click)="authService.sendVerificationEmail()"
                          class="text-white bg-white/20 hover:bg-white/30 px-5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm border border-white/20 active:scale-95"
                        >
                          Verify
                        </button>
                      } @else {
                        <span
                          class="text-[14px] font-bold text-ios-green drop-shadow-sm"
                          >Verified</span
                        >
                      }
                    </div>

                    <div
                      class="ios-list-item h-[72px] bg-transparent border-0 hover:bg-white/5"
                    >
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center shadow-sm"
                          [class.text-ios-green]="profile()?.panUrl"
                          [class.text-white]="!profile()?.panUrl"
                        >
                          <mat-icon class="!text-[20px] drop-shadow-sm">
                            {{ profile()?.panUrl ? "check_circle" : "badge" }}
                          </mat-icon>
                        </div>
                        <span
                          class="text-[17px] font-semibold tracking-tight drop-shadow-sm"
                          >ID Document</span
                        >
                      </div>
                      @if (!profile()?.panUrl) {
                        <button
                          class="text-white bg-white/20 hover:bg-white/30 px-5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm border border-white/20 active:scale-95"
                        >
                          Upload
                        </button>
                      } @else {
                        <span
                          class="text-[14px] font-bold text-ios-green drop-shadow-sm"
                          >Verified</span
                        >
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Info Sections Grid -->
              <div
                class="grid grid-cols-1 md:grid-cols-2 gap-8 animate-ios-slide"
                style="animation-delay: 0.4s"
              >
                <!-- Personal Details -->
                <div class="space-y-4">
                  <div class="section-header">
                    <div class="w-1 h-4 bg-ios-blue/30 rounded-full"></div>
                    <h3 class="section-title">Account Details</h3>
                  </div>
                  <div class="ios-list-group">
                    @if (profile()?.bio) {
                      <div class="ios-list-item h-auto py-4 items-start">
                        <div class="flex items-start gap-4">
                          <div
                            class="w-10 h-10 rounded-xl bg-ios-purple/10 flex items-center justify-center text-ios-purple flex-shrink-0 shadow-sm mt-1"
                          >
                            <mat-icon class="!text-[20px]">info</mat-icon>
                          </div>
                          <div class="flex flex-col">
                            <span
                              class="text-[15px] font-bold tracking-tight mb-1"
                              >Bio</span
                            >
                            <span
                              class="text-[14px] opacity-70 font-medium leading-relaxed"
                              >{{ profile()?.bio }}</span
                            >
                          </div>
                        </div>
                      </div>
                    }

                    @if (profile()?.email || authService.user()?.email) {
                      <div class="ios-list-item h-[72px]">
                        <div class="flex items-center gap-4">
                          <div
                            class="w-10 h-10 rounded-xl bg-ios-blue/10 flex items-center justify-center text-ios-blue flex-shrink-0 shadow-sm"
                          >
                            <mat-icon class="!text-[20px]">email</mat-icon>
                          </div>
                          <span class="text-[15px] font-bold tracking-tight"
                            >Email</span
                          >
                        </div>
                        <span
                          class="text-[13px] opacity-50 font-bold break-all"
                          >{{
                            maskSensitive(profile()?.email || authService.user()?.email)
                          }}</span
                        >
                      </div>
                    }

                    @if (profile()?.phoneNumber) {
                      <div class="ios-list-item h-[72px]">
                        <div class="flex items-center gap-4">
                          <div
                            class="w-10 h-10 rounded-xl bg-ios-green/10 flex items-center justify-center text-ios-green flex-shrink-0 shadow-sm"
                          >
                            <mat-icon class="!text-[20px]">phone</mat-icon>
                          </div>
                          <span class="text-[15px] font-bold tracking-tight"
                            >Phone</span
                          >
                        </div>
                        <span class="text-[14px] opacity-50 font-bold">{{
                          maskSensitive(profile()?.phoneNumber)
                        }}</span>
                      </div>
                    }

                    @if (profile()?.gender) {
                      <div class="ios-list-item h-[72px]">
                        <div class="flex items-center gap-4">
                          <div
                            class="w-10 h-10 rounded-xl bg-ios-blue/10 flex items-center justify-center text-ios-blue flex-shrink-0 shadow-sm"
                          >
                            <mat-icon class="!text-[20px]">person</mat-icon>
                          </div>
                          <span class="text-[15px] font-bold tracking-tight"
                            >Gender</span
                          >
                        </div>
                        <span
                          class="text-[14px] opacity-50 font-bold capitalize"
                          >{{ profile()?.gender }}</span
                        >
                      </div>
                    }

                    @if (profile()?.accountType === 'country') {
                      @if (profile()?.countryCode) {
                        <div class="ios-list-item h-[72px]">
                          <div class="flex items-center gap-4">
                            <div
                              class="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 flex-shrink-0 shadow-sm"
                            >
                              <mat-icon class="!text-[20px]">public</mat-icon>
                            </div>
                            <span class="text-[15px] font-bold tracking-tight"
                              >Country Code</span
                            >
                          </div>
                          <span class="text-[14px] opacity-50 font-bold uppercase">{{
                            profile()?.countryCode
                          }}</span>
                        </div>
                      }
                      @if (profile()?.capital) {
                        <div class="ios-list-item h-[72px]">
                          <div class="flex items-center gap-4">
                            <div
                              class="w-10 h-10 rounded-xl bg-ios-blue/10 flex items-center justify-center text-ios-blue flex-shrink-0 shadow-sm"
                            >
                              <mat-icon class="!text-[20px]">location_city</mat-icon>
                            </div>
                            <span class="text-[15px] font-bold tracking-tight"
                              >Capital</span
                            >
                          </div>
                          <span class="text-[14px] opacity-50 font-bold">{{
                            profile()?.capital
                          }}</span>
                        </div>
                      }
                      @if (profile()?.region) {
                        <div class="ios-list-item h-[72px]">
                          <div class="flex items-center gap-4">
                            <div
                              class="w-10 h-10 rounded-xl bg-ios-purple/10 flex items-center justify-center text-ios-purple flex-shrink-0 shadow-sm"
                            >
                              <mat-icon class="!text-[20px]">map</mat-icon>
                            </div>
                            <span class="text-[15px] font-bold tracking-tight"
                              >Region</span
                            >
                          </div>
                          <span class="text-[14px] opacity-50 font-bold">{{
                            profile()?.region
                          }}</span>
                        </div>
                      }
                      @if (profile()?.currency) {
                        <div class="ios-list-item h-[72px]">
                          <div class="flex items-center gap-4">
                            <div
                              class="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500 flex-shrink-0 shadow-sm"
                            >
                              <mat-icon class="!text-[20px]">payments</mat-icon>
                            </div>
                            <span class="text-[15px] font-bold tracking-tight"
                              >Currency</span
                            >
                          </div>
                          <span class="text-[14px] opacity-50 font-bold">{{
                            profile()?.currency
                          }}</span>
                        </div>
                      }
                    } @else {
                      <div class="ios-list-item h-[72px]">
                        <div class="flex items-center gap-4">
                          <div
                            class="w-10 h-10 rounded-xl bg-ios-pink/10 flex items-center justify-center text-ios-pink flex-shrink-0 shadow-sm"
                          >
                            <mat-icon class="!text-[20px]">cake</mat-icon>
                          </div>
                          <span class="text-[15px] font-bold tracking-tight"
                            >{{ profile()?.accountType === 'organization' ? 'Founded Date' : 'Date of Birth' }}</span
                          >
                        </div>
                        <span class="text-[14px] opacity-50 font-bold">{{
                          maskSensitive(profile()?.dob ? (toDate(profile()?.dob) | date: "MMM d, y") : "Undisclosed")
                        }}</span>
                      </div>
                    }
                    <div class="ios-list-item h-[72px]">
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-xl bg-ios-blue/10 flex items-center justify-center text-ios-blue flex-shrink-0 shadow-sm"
                        >
                          <mat-icon class="!text-[20px]">fingerprint</mat-icon>
                        </div>
                        <span class="text-[15px] font-bold tracking-tight"
                          >User ID</span
                        >
                      </div>
                      <span
                        class="text-[12px] opacity-50 font-mono font-bold"
                        >{{ maskSensitive(profile()?.uid) }}</span
                      >
                    </div>
                    <div class="ios-list-item h-[72px]">
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-xl bg-ios-green/10 flex items-center justify-center text-ios-green flex-shrink-0 shadow-sm"
                        >
                          <mat-icon class="!text-[20px]"
                            >verified_user</mat-icon
                          >
                        </div>
                        <span class="text-[15px] font-bold tracking-tight"
                          >Joined</span
                        >
                      </div>
                      <span class="text-[14px] opacity-50 font-bold">{{
                        (profile()?.createdAt
                          ? toDate(profile()!.createdAt!)
                          : toDate(authService.user()?.metadata?.creationTime)
                        ) | date: "MMM yyyy"
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Device & Security -->
                <div class="space-y-4">
                  <div class="section-header">
                    <div class="w-1 h-4 bg-ios-purple/30 rounded-full"></div>
                    <h3 class="section-title">Network Info</h3>
                  </div>
                  <div class="ios-list-group">
                    <div class="ios-list-item h-[72px]">
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-xl bg-ios-purple/10 flex items-center justify-center text-ios-purple flex-shrink-0 shadow-sm"
                        >
                          <mat-icon class="!text-[20px]">badge</mat-icon>
                        </div>
                        <span class="text-[15px] font-bold tracking-tight"
                          >Account Type</span
                        >
                      </div>
                      <span
                        class="text-[10px] font-black uppercase tracking-widest text-ios-purple bg-ios-purple/5 px-3 py-1.5 rounded-lg border border-ios-purple/10"
                      >
                        {{ profile()?.accountType || "Person" }}
                      </span>
                    </div>
                    <div class="ios-list-item h-[72px]">
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-xl bg-ios-blue/10 flex items-center justify-center text-ios-blue flex-shrink-0 shadow-sm"
                        >
                          <mat-icon class="!text-[20px]">language</mat-icon>
                        </div>
                        <span class="text-[15px] font-bold tracking-tight"
                          >Language</span
                        >
                      </div>
                      <span
                        class="text-[12px] font-black tracking-[0.2em] text-ios-blue bg-ios-blue/5 px-3 py-1.5 rounded-lg border border-ios-blue/10"
                      >EN</span>
                    </div>
                    <div class="ios-list-item h-[72px]">
                      <div class="flex items-center gap-4">
                        <div
                          class="w-10 h-10 rounded-xl bg-ios-yellow/10 flex items-center justify-center text-ios-yellow flex-shrink-0 shadow-sm"
                        >
                          <mat-icon class="!text-[20px]"
                            >online_prediction</mat-icon
                          >
                        </div>
                        <span class="text-[15px] font-bold tracking-tight"
                          >Status</span
                        >
                      </div>
                      <div
                        class="flex items-center gap-2.5 px-3 py-1.5 bg-black/5 dark:bg-white/5 rounded-lg border border-black/5 dark:border-white/5"
                      >
                        <div
                          class="w-2 h-2 rounded-full shadow-[0_0_10px]"
                          [class.bg-ios-green]="profile()?.isOnline"
                          [class.shadow-ios-green/50]="profile()?.isOnline"
                          [class.bg-slate-400]="!profile()?.isOnline"
                        ></div>
                        <span
                          class="text-[10px] opacity-60 font-black uppercase tracking-widest"
                          >{{
                            profile()?.isOnline ? "Online" : "Offline"
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Authorized Person Section -->
                @if (isOrganizationViewer()) {
                  <div class="space-y-8 md:col-span-2 animate-ios-slide" style="animation-delay: 0.5s">
                    
                    <!-- Authorized Persons -->
                    <div class="space-y-4">
                      <div class="section-header">
                        <div class="w-1 h-4 bg-ios-blue/30 rounded-full"></div>
                        <h3 class="section-title">Authorized Persons</h3>
                      </div>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        @for (member of authorizedPersons(); track member.uid) {
                          <div [routerLink]="['/profile', member.uid]" class="cursor-pointer bg-white/10 dark:bg-black/40 backdrop-blur-lg border border-white/20 dark:border-white/10 shadow-xl rounded-2xl p-6 relative overflow-hidden group hover:scale-[1.01] hover:bg-white/20 dark:hover:bg-white/5 transition-all" [class.opacity-75]="member.end" [class.grayscale]="member.end">
                            <div class="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none"></div>
                            <div class="relative z-10 flex items-center justify-between gap-6">
                              <div class="flex items-center gap-5">
                                <div class="w-16 h-16 rounded-2xl overflow-hidden bg-white/5 border border-white/10 shrink-0 shadow-lg relative">
                                  @if (member.photoURL) {
                                    <img [src]="member.photoURL" class="w-full h-full object-cover" alt="Member" referrerpolicy="no-referrer" />
                                  } @else {
                                    <div class="w-full h-full flex items-center justify-center bg-ios-blue/20 text-ios-blue font-bold">
                                      {{ (member.displayName || member.name || 'U').charAt(0) }}
                                    </div>
                                  }
                                </div>
                                <div class="space-y-1.5">
                                  <div class="flex items-center gap-2">
                                    <h4 class="text-lg font-extrabold tracking-tight text-zinc-900 dark:text-white">{{ member.displayName || member.name }}</h4>
                                    @if (member.end) {
                                      <span class="bg-red-500/20 text-red-500 border border-red-500/30 text-[9px] font-black px-2 py-0.5 rounded-sm uppercase tracking-widest">Revoked</span>
                                    }
                                  </div>
                                  <div class="text-[11px] text-zinc-500 dark:text-white/50 lowercase mt-0.5">@{{ member.username }}</div>
                                  <div class="text-[11px] text-zinc-500 dark:text-white/40 mt-1 flex flex-col gap-1">
                                    <div class="flex items-center gap-1.5">
                                      <mat-icon class="!text-[12px] opacity-40">calendar_today</mat-icon>
                                      <span>Start: {{ toDate(member.start) | date:'MMM d, y' }}</span>
                                    </div>
                                    @if (member.startReason) {
                                      <div class="text-[10px] italic opacity-60">"{{ member.startReason }}"</div>
                                    }
                                    @if (member.end) {
                                      <div class="flex items-center gap-1.5 mt-1">
                                        <mat-icon class="!text-[12px] opacity-40 text-red-400">event_busy</mat-icon>
                                        <span class="text-red-400/80">End: {{ toDate(member.end) | date:'MMM d, y' }}</span>
                                      </div>
                                      @if (member.endReason) {
                                        <div class="text-[10px] italic opacity-60 text-red-400/80">"{{ member.endReason }}"</div>
                                      }
                                    }
                                  </div>
                                  <div class="flex flex-wrap gap-1 mt-1.5">
                                    @if (member.roles && member.roles.length > 0) {
                                      @for (role of member.roles; track role) {
                                        <span class="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                          {{ role }}
                                        </span>
                                      }
                                    } @else {
                                      <span class="text-[10px] text-gray-400 italic">No Roles Assigned</span>
                                    }
                                  </div>
                                </div>
                              </div>
                              @if (!hasProtectedRole(member.roles) && !member.end && (canManageOrganization() || isOwner())) {
                                <button (click)="initRevokeMember(member); $event.stopPropagation()" class="text-red-500 hover:bg-red-500/10 p-2 rounded-xl transition-all z-20 relative">
                                  <mat-icon>delete_outline</mat-icon>
                                </button>
                              }
                            </div>
                            @if (revokingUserId() === member.uid) {
                              <div class="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl relative z-20" (click)="$event.stopPropagation()">
                                <label class="text-[10px] font-black uppercase tracking-widest text-red-400">Revocation Reason</label>
                                <textarea [formControl]="revokeReasonControl" class="ios-input w-full mt-2 text-sm" placeholder="Why is this member being removed?" rows="2" required></textarea>
                                <div class="flex items-center justify-end gap-2 mt-3">
                                  <button (click)="cancelRevoke(); $event.stopPropagation()" class="px-4 py-1.5 rounded-lg text-xs font-bold text-gray-400 hover:text-white transition-all">Cancel</button>
                                  <button (click)="confirmRevoke(member.uid); $event.stopPropagation()" [disabled]="!revokeReasonControl.value?.trim()" class="px-4 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold shadow-lg shadow-red-500/20 hover:scale-105 active:scale-95 disabled:opacity-50 transition-all">Confirm Revoke</button>
                                </div>
                              </div>
                            }
                          </div>
                        } @empty {
                          <div class="p-10 text-center bg-white/5 backdrop-blur-xl rounded-[32px] border border-dashed border-zinc-200 dark:border-white/10">
                            <mat-icon class="text-zinc-300 dark:text-white/20 text-3xl mb-2">admin_panel_settings</mat-icon>
                            <p class="text-[13px] text-zinc-400 dark:text-white/40">No authorized persons listed.</p>
                          </div>
                        }
                      </div>
                    </div>

                    <!-- Members -->
                    <div class="space-y-4">
                      <div class="section-header">
                        <div class="w-1 h-4 bg-ios-purple/30 rounded-full"></div>
                        <h3 class="section-title">Members</h3>
                      </div>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        @for (member of regularMembers(); track member.uid) {
                          <div [routerLink]="['/profile', member.uid]" class="cursor-pointer bg-white/10 dark:bg-black/40 backdrop-blur-lg border border-white/20 dark:border-white/10 shadow-lg rounded-2xl p-4 relative overflow-hidden group hover:scale-[1.01] hover:bg-white/20 dark:hover:bg-white/5 transition-all" [class.opacity-75]="member.end" [class.grayscale]="member.end">
                            <div class="relative z-10 flex items-start justify-between gap-4">
                              <div class="flex items-start gap-4">
                                <div class="w-12 h-12 rounded-xl overflow-hidden bg-white/5 border border-white/10 shrink-0">
                                  @if (member.photoURL) {
                                    <img [src]="member.photoURL" class="w-full h-full object-cover" alt="Member" referrerpolicy="no-referrer" />
                                  } @else {
                                    <div class="w-full h-full flex items-center justify-center bg-ios-purple/20 text-ios-purple font-bold text-xs">
                                      {{ (member.displayName || member.name || 'U').charAt(0) }}
                                    </div>
                                  }
                                </div>
                                <div class="space-y-0.5 mt-[-2px]">
                                  <div class="flex items-center gap-2">
                                    <h4 class="text-sm font-bold text-zinc-900 dark:text-white">{{ member.displayName || member.name }}</h4>
                                    @if (member.end) {
                                      <span class="bg-red-500/20 text-red-500 border border-red-500/30 text-[8px] font-black px-1.5 py-0.5 rounded-sm uppercase tracking-widest">Revoked</span>
                                    }
                                  </div>
                                  <div class="text-[10px] text-zinc-500 dark:text-white/50 lowercase mt-0.5 opacity-75">@{{ member.username }}</div>
                                  <div class="text-[10px] text-zinc-500 dark:text-white/40 pt-1 flex flex-col gap-1">
                                    <div>Start: {{ toDate(member.start) | date }} | End: {{ member.end ? (toDate(member.end) | date) : 'Present' }}</div>
                                    @if (member.startReason) {
                                      <div class="italic opacity-60">"{{ member.startReason }}"</div>
                                    }
                                    @if (member.endReason) {
                                      <div class="italic opacity-60 text-red-400">"{{ member.endReason }}"</div>
                                    }
                                  </div>
                                  <div class="flex flex-wrap gap-1 mt-1.5">
                                    @if (member.roles && member.roles.length > 0) {
                                      @for (role of member.roles; track role) {
                                        <span class="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                          {{ role }}
                                        </span>
                                      }
                                    } @else {
                                      <span class="text-[10px] text-gray-400 italic">No Roles Assigned</span>
                                    }
                                  </div>
                                </div>
                              </div>
                              @if (!hasProtectedRole(member.roles) && !member.end && (canManageOrganization() || isOwner())) {
                                <button (click)="initRevokeMember(member); $event.stopPropagation()" class="text-red-500 hover:bg-red-500/10 p-1.5 rounded-lg transition-all z-20 relative shrink-0">
                                  <mat-icon class="!text-[18px]">close</mat-icon>
                                </button>
                              }
                            </div>
                            @if (revokingUserId() === member.uid) {
                              <div class="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl relative z-20" (click)="$event.stopPropagation()">
                                <label class="text-[10px] font-black uppercase tracking-widest text-red-400">Revocation Reason</label>
                                <textarea [formControl]="revokeReasonControl" class="ios-input w-full mt-2 text-xs" placeholder="Why is this member being removed?" rows="2" required></textarea>
                                <div class="flex items-center justify-end gap-2 mt-3">
                                  <button (click)="cancelRevoke(); $event.stopPropagation()" class="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-400 hover:text-white transition-all">Cancel</button>
                                  <button (click)="confirmRevoke(member.uid); $event.stopPropagation()" [disabled]="!revokeReasonControl.value?.trim()" class="px-3 py-1.5 rounded-lg bg-red-500 text-white text-[10px] font-bold shadow-md shadow-red-500/20 transition-all">Confirm Revoke</button>
                                </div>
                              </div>
                            }
                          </div>
                        } @empty {
                          <div class="md:col-span-2 p-10 text-center bg-white/5 backdrop-blur-xl rounded-3xl border border-dashed border-zinc-200 dark:border-white/10">
                            <p class="text-[13px] text-zinc-400 dark:text-white/40">No regular members listed yet.</p>
                          </div>
                        }
                      </div>
                    </div>

                    <!-- Add Member Section -->
                    @if (canAddMembers()) {
                      <div class="mt-8 p-8 bg-white/5 backdrop-blur-3xl rounded-[40px] border border-white/10 shadow-2xl relative overflow-visible z-10">
                        <div class="relative z-10">
                          <div class="flex items-center gap-4 mb-8">
                            <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 flex items-center justify-center text-ios-blue">
                              <mat-icon>person_add</mat-icon>
                            </div>
                            <div>
                              <h4 class="text-xl font-bold tracking-tight">Add New Member</h4>
                              <p class="text-[10px] font-black uppercase tracking-widest opacity-40">Identity & Role Assignment</p>
                            </div>
                          </div>

                          <form [formGroup]="addMemberForm" (ngSubmit)="onAddMember()" class="space-y-6">
                            @if (!selectedCandidate()) {
                              <div class="space-y-2 relative z-[50]">
                                <label class="text-[11px] font-black uppercase tracking-[0.2em] opacity-40 px-2">Search Person</label>
                                <div class="ios-input-container !h-14 relative z-[51]">
                                  <mat-icon class="mx-4 text-ios-caption opacity-40">search</mat-icon>
                                  <input 
                                    formControlName="searchQuery" 
                                    class="ios-input flex-1 relative z-[52]" 
                                    placeholder="Search by name or @username..."
                                  />
                                  
                                  <!-- Search Results Dropdown -->
                                  @if (isSearchingUsers()) {
                                    <div class="absolute right-4 top-1/2 -translate-y-1/2 z-[60]">
                                      <div class="w-4 h-4 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                                    </div>
                                  }
                                  
                                  @if (searchResults().length > 0 && addMemberForm.get('searchQuery')?.value) {
                                    <div class="absolute left-0 right-0 top-full mt-2 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl rounded-3xl border border-white/10 shadow-2xl overflow-y-auto max-h-64 w-full z-[100]">
                                      @for (user of searchResults(); track user.uid) {
                                        <button 
                                          type="button"
                                          (click)="selectCandidate(user)"
                                          class="w-full p-4 flex items-center gap-4 hover:bg-ios-blue/5 transition-all text-left border-b border-white/5 last:border-0"
                                        >
                                          <img [src]="user.photoURL" class="w-10 h-10 rounded-xl object-cover shrink-0" alt="User" />
                                          <div class="flex-1 min-w-0">
                                            <div class="font-bold truncate">{{ user.displayName }}</div>
                                            <div class="text-[11px] opacity-40 lowercase">@{{ user.username }}</div>
                                          </div>
                                          <mat-icon class="text-ios-blue text-sm opacity-0 group-hover:opacity-100">add_circle</mat-icon>
                                        </button>
                                      }
                                    </div>
                                  }
                                </div>
                              </div>
                            }

                            @if (selectedCandidate()) {
                              <!-- Selected User Card -->
                              <div class="p-4 bg-white/10 dark:bg-black/20 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl flex items-center justify-between shadow-md mb-6">
                                <div class="flex items-center gap-4">
                                  <img [src]="selectedCandidate().photoURL" class="w-12 h-12 rounded-full object-cover shadow-sm" alt="Selected User" />
                                  <div>
                                    <div class="font-bold text-base leading-tight">{{ selectedCandidate().displayName }}</div>
                                    <div class="text-[11px] opacity-60 font-medium">@{{ selectedCandidate().username }}</div>
                                  </div>
                                </div>
                                <button type="button" (click)="clearSelection()" class="px-3 py-1.5 rounded-lg bg-black/5 dark:bg-white/10 hover:bg-red-500 hover:text-white transition-all text-[11px] font-bold tracking-wide flex items-center justify-center gap-1">
                                  <mat-icon class="!text-[14px]">refresh</mat-icon>
                                  <span class="hidden sm:inline">CHANGE</span>
                                </button>
                              </div>

                              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div class="space-y-2">
                                  <label class="text-[11px] font-black uppercase tracking-[0.2em] opacity-40 px-2">Role Title</label>
                                  <div class="ios-input-container">
                                    <select formControlName="roleTitle" class="ios-input bg-transparent w-full outline-none">
                                      <option value="" disabled selected>Select Role</option>
                                      <option value="Admin">Admin</option>
                                      <option value="Managing Trustee">Managing Trustee</option>
                                      <option value="Managing Director">Managing Director</option>
                                      <option value="Founder">Founder</option>
                                      <option value="Member">Member</option>
                                      <option value="Authorized Person">Authorized Person</option>
                                    </select>
                                  </div>
                                </div>
                                <div class="space-y-2">
                                  <label class="text-[11px] font-black uppercase tracking-[0.2em] opacity-40 px-2">Start Date</label>
                                  <div class="ios-input-container">
                                    <input type="date" formControlName="startDate" class="ios-input text-xs" />
                                  </div>
                                </div>
                                <div class="space-y-2">
                                  <label class="text-[11px] font-black uppercase tracking-[0.2em] opacity-40 px-2">End Date (Optional)</label>
                                  <div class="ios-input-container">
                                    <input type="date" formControlName="endDate" class="ios-input text-xs" />
                                  </div>
                                </div>
                              </div>
                              
                              <div class="space-y-2 mt-6">
                                <label class="text-[11px] font-black uppercase tracking-[0.2em] opacity-40 px-2">Start Reason (Required)</label>
                                <div class="ios-input-container">
                                  <textarea formControlName="startReason" class="ios-input text-xs w-full" placeholder="Why is this member being assigned to this role?" rows="2" required></textarea>
                                </div>
                              </div>

                              <button 
                                type="submit" 
                                [disabled]="addMemberForm.invalid || !selectedCandidate() || isSavingMember()"
                                class="w-full h-14 rounded-2xl bg-ios-blue text-white font-bold tracking-tight shadow-ios-lg shadow-ios-blue/20 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-40 flex items-center justify-center gap-3 mt-4"
                              >
                                @if (isSavingMember()) {
                                  <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                }
                                <span>Commit Assignment</span>
                              </button>
                            }
                          </form>
                        </div>
                      </div>
                    }
                  </div>
                }

                @if (affiliations().length > 0) {
                  <div class="space-y-4 md:col-span-2 animate-ios-slide" style="animation-delay: 0.6s">
                    <div class="section-header">
                      <div class="w-1 h-4 bg-ios-purple/30 rounded-full"></div>
                      <h3 class="section-title">Roles & Affiliations</h3>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-4">
                      @for (item of affiliations(); track item.orgId) {
                        <div class="bg-white/10 dark:bg-black/40 backdrop-blur-lg border border-white/20 dark:border-white/10 shadow-xl rounded-[32px] p-6 relative overflow-hidden group">
                          <a [routerLink]="['/profile', item.orgId]" class="relative z-10 flex items-start gap-4 group/link cursor-pointer">
                            <div class="w-14 h-14 rounded-2xl overflow-hidden bg-white/5 border border-white/10 shrink-0 shadow-lg group-hover/link:scale-110 transition-transform">
                              @if (item.orgPhotoURL) {
                                <img [src]="item.orgPhotoURL" class="w-full h-full object-cover" referrerpolicy="no-referrer" alt="Org Logo" />
                              } @else {
                                <div class="w-full h-full flex items-center justify-center bg-ios-purple/20 text-ios-purple font-bold">
                                  {{ item.orgName[0] }}
                                </div>
                              }
                            </div>
                            <div class="flex-1 space-y-1">
                              <h4 class="text-[17px] font-bold text-zinc-900 dark:text-white leading-tight group-hover/link:text-ios-blue transition-colors">{{ item.orgName }}</h4>
                              <div class="text-[11px] text-zinc-500 dark:text-white/50 lowercase mt-0.5">@{{ item.orgUsername }}</div>
                              <div class="flex flex-wrap gap-1.5 pt-2">
                                @for (role of item.roles; track role) {
                                  <span class="text-[10px] font-bold uppercase tracking-wider bg-ios-purple/10 dark:bg-ios-purple/20 text-ios-purple px-2 py-0.5 rounded-full border border-ios-purple/20">
                                    {{ role }}
                                  </span>
                                }
                              </div>
                              <div class="text-[11px] text-zinc-500 dark:text-white/40 mt-2 flex items-center gap-1.5">
                                <mat-icon class="!text-xs opacity-40">event</mat-icon>
                                <span>{{ toDate(item.startDate) | date:'MMM yyyy' }} — {{ item.endDate ? (toDate(item.endDate) | date:'MMM yyyy') : 'Present' }}</span>
                              </div>
                            </div>
                          </a>
                        </div>
                      }
                    </div>
                  </div>
                }

                <!-- Security & Account Management (Redesign) -->
                @if (authService.user()?.uid === profile()?.uid) {
                  <div class="space-y-4 md:col-span-2">
                    <div class="section-header">
                      <div class="w-1 h-4 bg-ios-red/30 rounded-full"></div>
                      <h3 class="section-title">Account Settings</h3>
                    </div>
                    <div class="ios-list-group">
                      @if (!authService.user()?.emailVerified) {
                        <button
                          (click)="openConfirmModal('verify')"
                          class="ios-list-item h-[80px] group w-full text-left"
                        >
                          <div class="flex items-center gap-5">
                            <div
                              class="w-12 h-12 rounded-full bg-ios-yellow/10 flex items-center justify-center text-ios-yellow shadow-sm"
                            >
                              <mat-icon class="!text-[24px]"
                                >verified_user</mat-icon
                              >
                            </div>
                            <div class="flex flex-col">
                              <span class="text-[17px] font-bold tracking-tight"
                                >Verify Email</span
                              >
                              <span class="text-[13px] opacity-50"
                                >Account verification needed</span
                              >
                            </div>
                          </div>
                          <mat-icon
                            class="text-ios-caption opacity-30 text-[24px] group-hover:translate-x-1 transition-transform"
                            >chevron_right</mat-icon
                          >
                        </button>
                      }
                      <button
                        (click)="openConfirmModal('email')"
                        class="ios-list-item h-[80px] group w-full text-left"
                      >
                        <div class="flex items-center gap-5">
                          <div
                            class="w-12 h-12 rounded-full bg-ios-blue/10 flex items-center justify-center text-ios-blue shadow-sm"
                          >
                            <mat-icon class="!text-[24px]"
                              >alternate_email</mat-icon
                            >
                          </div>
                          <div class="flex flex-col">
                            <span class="text-[17px] font-bold tracking-tight"
                              >Email & Phone</span
                            >
                            <span class="text-[13px] opacity-50"
                              >Update contact information</span
                            >
                          </div>
                        </div>
                        <mat-icon
                          class="text-ios-caption opacity-30 text-[24px] group-hover:translate-x-1 transition-transform"
                          >chevron_right</mat-icon
                        >
                      </button>

                      <button
                        (click)="openConfirmModal('password')"
                        class="ios-list-item h-[80px] group w-full text-left"
                      >
                        <div class="flex items-center gap-5">
                          <div
                            class="w-12 h-12 rounded-full bg-ios-purple/10 flex items-center justify-center text-ios-purple shadow-sm"
                          >
                            <mat-icon class="!text-[24px]">lock_reset</mat-icon>
                          </div>
                          <div class="flex flex-col">
                            <span class="text-[17px] font-bold tracking-tight"
                              >Change Password</span
                            >
                            <span class="text-[13px] opacity-50"
                              >Update your security credentials</span
                            >
                          </div>
                        </div>
                        <mat-icon
                          class="text-ios-caption opacity-30 text-[24px] group-hover:translate-x-1 transition-transform"
                          >chevron_right</mat-icon
                        >
                      </button>

                    </div>
                  </div>
                }
              </div>
            } @else if (selectedTab() === "Chats") {
              <!-- Chats Feed -->
              @if (isLoadingChats()) {
                <div class="flex justify-center p-20">
                  <div
                    class="w-10 h-10 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"
                  ></div>
                </div>
              } @else if (chats().length === 0) {
                <div
                  class="bento-card p-16 flex flex-col items-center justify-center text-center animate-ios-slide relative overflow-hidden group"
                  style="animation-delay: 0.3s"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-tr from-ios-blue/5 to-transparent"
                  ></div>
                  <div
                    class="relative z-10 w-24 h-24 rounded-3xl bg-white/50 dark:bg-zinc-800/50 backdrop-blur-md flex items-center justify-center mb-8 shadow-ios-sm border border-black/5 dark:border-white/5 group-hover:scale-110 transition-transform duration-700"
                  >
                    <mat-icon class="text-5xl text-ios-blue opacity-50"
                      >chat_bubble</mat-icon
                    >
                  </div>
                  <h3
                    class="text-xl font-bold tracking-tight mb-2 relative z-10"
                  >
                    Empty Inbox
                  </h3>
                  <p
                    class="text-[15px] font-medium opacity-40 max-w-xs relative z-10"
                  >
                    Your encrypted conversations will manifest here once
                    established.
                  </p>
                </div>
              } @else {
                <div
                  class="ios-list-group animate-ios-slide"
                  style="animation-delay: 0.3s"
                >
                  @for (chat of chats(); track chat.id) {
                    <div
                      (click)="navigateToChat(chat)"
                      (keydown.enter)="navigateToChat(chat)"
                      tabindex="0"
                      class="ios-list-item h-[100px] !px-4 cursor-pointer group hover:bg-ios-blue/5"
                    >
                      <div class="flex items-center gap-5 w-full">
                        <div class="relative">
                          <div
                            class="w-16 h-16 rounded-3xl overflow-hidden bg-ios-blue/10 flex-shrink-0 border border-ios-blue/20 shadow-sm transition-transform group-hover:scale-105"
                          >
                            @if (chat.participantPhoto) {
                              <img
                                [src]="chat.participantPhoto"
                                class="w-full h-full object-cover"
                                referrerpolicy="no-referrer"
                                alt="Participant"
                              />
                            } @else {
                              <div
                                class="w-full h-full flex items-center justify-center text-ios-blue font-bold text-2xl"
                              >
                                {{
                                  chat.participantName
                                    ? chat.participantName
                                        .charAt(0)
                                        .toUpperCase()
                                    : "U"
                                }}
                              </div>
                            }
                          </div>
                          <div
                            class="absolute -bottom-1 -right-1 w-5 h-5 bg-ios-green border-4 border-white dark:border-zinc-900 rounded-full shadow-sm"
                          ></div>
                        </div>
                        <div
                          class="flex-1 min-w-0 flex flex-col justify-center"
                        >
                          <div class="flex items-center justify-between mb-1">
                            <h4
                              class="font-bold text-[18px] tracking-tight truncate"
                            >
                              {{ chat.participantName }}
                            </h4>
                            <span
                              class="text-[12px] opacity-40 font-black uppercase tracking-widest"
                              >{{
                                toDate(chat.updatedAt) | date: "h:mm a"
                              }}</span
                            >
                          </div>
                          <p
                            class="text-[15px] opacity-50 truncate font-medium group-hover:opacity-100 transition-opacity"
                          >
                            {{ chat.lastMessage || "No messages yet" }}
                          </p>
                        </div>
                        <mat-icon
                          class="text-ios-caption opacity-20 text-[26px] group-hover:text-ios-blue group-hover:opacity-100 transition-all group-hover:translate-x-1"
                          >chevron_right</mat-icon
                        >
                      </div>
                    </div>
                  }
                </div>
              }
            } @else if (selectedTab() === "Images" || selectedTab() === "Music" || selectedTab() === "Voice") {
              <!-- Specially Redesigned Images / Music / Voice Feed (Instagram Style) -->
              <div class="flex items-center justify-between mb-8 px-4">
                <div class="flex items-center gap-3">
                  <div class="w-1.5 h-6 bg-ios-blue rounded-full"></div>
                  <h3 class="text-xl font-bold tracking-tight">{{ selectedTab() === 'Music' ? 'Music' : selectedTab() === 'Voice' ? 'Voice' : 'Images' }}</h3>
                </div>
                @if (canManageOrganization() || isOwner()) {
                  <button
                    (click)="openAddAssetModal()"
                    class="h-12 px-6 rounded-2xl bg-ios-blue text-white font-bold text-sm shadow-ios shadow-ios-blue/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <mat-icon class="!text-[20px]">{{ selectedTab() === 'Music' ? 'library_music' : selectedTab() === 'Voice' ? 'mic' : 'add_photo_alternate' }}</mat-icon>
                    <span class="tracking-tight">{{ selectedTab() === 'Music' ? 'Add Music' : selectedTab() === 'Voice' ? 'Add Voice' : 'Add Images' }}</span>
                  </button>
                }
              </div>

              @if (isLoadingUserImages()) {
                <div class="flex justify-center p-24">
                  <div class="w-10 h-10 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"></div>
                </div>
              } @else if (userImages().length === 0) {
                <div class="bento-card p-16 flex flex-col items-center justify-center text-center animate-ios-slide relative overflow-hidden group">
                  <div class="absolute inset-0 bg-gradient-to-tr from-ios-blue/5 to-transparent"></div>
                  <div class="relative z-10 w-24 h-24 rounded-3xl bg-white/50 dark:bg-zinc-805 backdrop-blur-md flex items-center justify-center mb-8 shadow-ios-sm border border-black/5 dark:border-white/5 group-hover:scale-110 transition-transform duration-700">
                    <mat-icon class="text-5xl text-ios-blue opacity-50">{{ selectedTab() === 'Music' ? 'library_music' : selectedTab() === 'Voice' ? 'mic' : 'photo' }}</mat-icon>
                  </div>
                  <h3 class="text-xl font-bold tracking-tight mb-2 relative z-10">{{ selectedTab() === 'Music' ? 'No Music Yet' : selectedTab() === 'Voice' ? 'No Voice Yet' : 'No Images Yet' }}</h3>
                  <p class="text-[15px] font-medium opacity-40 max-w-xs relative z-10">
                    {{ selectedTab() === 'Music' ? 'Upload and display beautifully rendered audio and music tracks in an Instagram styled gallery.' : selectedTab() === 'Voice' ? 'Upload and display beautifully rendered voice clips and audio recordings in an Instagram styled gallery.' : 'Upload and display beautifully rendered approved or review state images in an Instagram styled gallery.' }}
                  </p>
                </div>
              } @else {
                <!-- Instagram profile-like grid: grid-cols-2 sm:grid-cols-3 gap-1.5 md:gap-4 -->
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-1.5 md:gap-4 px-4 animate-ios-slide font-sans">
                  @for (img of userImages(); track img.id) {
                    <div 
                      (click)="viewImage(img)"
                      (keydown.enter)="viewImage(img)"
                      tabindex="0"
                      class="group relative aspect-square overflow-hidden rounded-xl md:rounded-2xl border border-black/5 dark:border-white/5 bg-gray-100 dark:bg-zinc-805 shadow-sm cursor-pointer hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ios-blue"
                    >
                      <img 
                        [src]="img.imageUrl" 
                        alt="User uploaded asset" 
                        class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        referrerpolicy="no-referrer"
                      />
                      <!-- Overlay with info and actions on hover -->
                      <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-2 md:p-4 text-white">
                        <div class="flex items-center justify-between">
                          <!-- Premium Status & Verification Badges -->
                          <div class="flex items-center gap-1 flex-wrap">
                            @if (img.visibility) {
                              <span 
                                [class]="
                                  img.visibility === 'public'
                                    ? 'px-2 py-0.5 rounded-full bg-emerald-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                    : 'px-2 py-0.5 rounded-full bg-zinc-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                "
                              >
                                {{ img.visibility }}
                              </span>
                            }
                            @if (img.verificationStatus) {
                              <span 
                                [class]="
                                  img.verificationStatus === 'approved'
                                    ? 'px-2 py-0.5 rounded-full bg-emerald-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                    : img.verificationStatus === 'rejected'
                                      ? 'px-2 py-0.5 rounded-full bg-rose-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                      : 'px-2 py-0.5 rounded-full bg-amber-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                "
                              >
                                {{ img.verificationStatus }}
                              </span>
                            }
                          </div>
                          
                          <!-- Edit & Delete Icons if authorized -->
                          @if (canManageOrganization() || isOwner()) {
                            <div class="flex items-center gap-1.5">
                              <button 
                                (click)="$event.stopPropagation(); openEditUserImageModal(img)" 
                                class="w-7 h-7 bg-black/40 hover:bg-ios-blue text-white flex items-center justify-center rounded-full transition-all active:scale-90"
                                title="Edit Details"
                              >
                                <mat-icon class="!text-[14px]">edit</mat-icon>
                              </button>
                              <button 
                                (click)="$event.stopPropagation(); confirmDeleteUserImage(img)" 
                                class="w-7 h-7 bg-black/40 hover:bg-ios-red text-white flex items-center justify-center rounded-full transition-all active:scale-90"
                                title="Delete Asset"
                              >
                                <mat-icon class="!text-[14px]">delete</mat-icon>
                              </button>
                            </div>
                          }
                        </div>
                        
                        <div class="truncate">
                          @if (img.remark || img.remarks) {
                            <p class="text-[10px] md:text-xs font-bold truncate">{{ img.remark || img.remarks }}</p>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            } @else if (selectedTab() === "Videos") {
              <!-- Specially Redesigned Videos Feed (Gallery Style) -->
              <div class="flex items-center justify-between mb-8 px-4">
                <div class="flex items-center gap-3">
                  <div class="w-1.5 h-6 bg-ios-blue rounded-full"></div>
                  <h3 class="text-xl font-bold tracking-tight">Videos</h3>
                </div>
                @if (canManageOrganization() || isOwner()) {
                  <button
                    (click)="openAddAssetModal()"
                    class="h-12 px-6 rounded-2xl bg-ios-blue text-white font-bold text-sm shadow-ios shadow-ios-blue/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                  >
                    <mat-icon class="!text-[20px]">movie</mat-icon>
                    <span class="tracking-tight">Add Video</span>
                  </button>
                }
              </div>

              @if (isLoadingAssets()) {
                <div class="flex justify-center p-24">
                  <div class="w-10 h-10 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"></div>
                </div>
              } @else if (assets().length === 0) {
                <div class="bento-card p-16 flex flex-col items-center justify-center text-center animate-ios-slide relative overflow-hidden group">
                  <div class="absolute inset-0 bg-gradient-to-tr from-ios-blue/5 to-transparent"></div>
                  <div class="relative z-10 w-24 h-24 rounded-3xl bg-white/50 dark:bg-zinc-805 backdrop-blur-md flex items-center justify-center mb-8 shadow-ios-sm border border-black/5 dark:border-white/5 group-hover:scale-110 transition-transform duration-700">
                    <mat-icon class="text-5xl text-ios-blue opacity-50">smart_display</mat-icon>
                  </div>
                  <h3 class="text-xl font-bold tracking-tight mb-2 relative z-10">No Videos Yet</h3>
                  <p class="text-[15px] font-medium opacity-40 max-w-xs relative z-10">
                    Share high-quality video content here. Videos will manifest in this gallery grid.
                  </p>
                </div>
              } @else {
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-4 animate-ios-slide">
                  @for (vid of assets(); track vid.id) {
                    <div 
                      (click)="viewAssetDetail(vid)"
                      (keydown.enter)="viewAssetDetail(vid)"
                      tabindex="0"
                      class="group relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black shadow-sm cursor-pointer hover:shadow-xl transition-all duration-500"
                    >
                      <img 
                        [src]="$any(vid).thumbnailUrl" 
                        alt="Video thumbnail" 
                        class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-70 group-hover:opacity-100"
                        referrerpolicy="no-referrer"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
                      
                      <div class="absolute inset-0 flex items-center justify-center">
                        <div class="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white border border-white/30 transform group-hover:scale-125 transition-all duration-500 shadow-2xl">
                          <mat-icon class="!text-3xl">play_arrow</mat-icon>
                        </div>
                      </div>

                      <div class="absolute bottom-4 left-4 right-4">
                        <h4 class="text-white font-bold text-xs truncate group-hover:text-ios-blue transition-colors">{{ vid.title }}</h4>
                        <div class="flex items-center gap-2 mt-1 flex-wrap">
                          <span class="text-[8px] text-white/50 uppercase tracking-widest font-black">{{ $any(vid).videoCategory || 'Video' }}</span>
                          @if (vid.status) {
                            <span 
                              [class]="
                                vid.status === 'published'
                                  ? 'px-1.5 py-0.5 rounded-full bg-emerald-500/80 text-white text-[7px] font-black uppercase tracking-wider'
                                  : 'px-1.5 py-0.5 rounded-full bg-zinc-500/80 text-white text-[7px] font-black uppercase tracking-wider'
                              "
                            >
                              {{ vid.status }}
                            </span>
                          }
                          @if (vid.verificationStatus) {
                            <span 
                              [class]="
                                vid.verificationStatus === 'approved'
                                  ? 'px-1.5 py-0.5 rounded-full bg-emerald-500/80 text-white text-[7px] font-black uppercase tracking-wider'
                                  : vid.verificationStatus === 'rejected'
                                    ? 'px-1.5 py-0.5 rounded-full bg-rose-500/80 text-white text-[7px] font-black uppercase tracking-wider'
                                    : 'px-1.5 py-0.5 rounded-full bg-amber-500/80 text-white text-[7px] font-black uppercase tracking-wider'
                              "
                            >
                              {{ vid.verificationStatus }}
                            </span>
                          }
                        </div>
                      </div>

                      @if (canManageOrganization() || isOwner()) {
                        <div class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          <button (click)="$event.stopPropagation(); openEditAssetModal(vid)" class="w-7 h-7 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-ios-blue">
                            <mat-icon class="!text-[14px]">edit</mat-icon>
                          </button>
                          <button (click)="$event.stopPropagation(); deleteAsset(vid)" class="w-7 h-7 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-ios-red">
                            <mat-icon class="!text-[14px]">delete</mat-icon>
                          </button>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            } @else {
              <!-- Assets Feed -->
              <div class="flex items-center justify-between mb-8 px-4">
                <div class="flex items-center gap-3">
                  <div class="w-1.5 h-6 bg-ios-blue rounded-full"></div>
                  <h3 class="text-xl font-bold tracking-tight">
                    {{ selectedTab() }}
                  </h3>
                </div>
                @if (canManageOrganization() || isOwner()) {
                  <button
                    (click)="openAddAssetModal()"
                    class="h-12 px-6 rounded-2xl bg-ios-blue text-white font-bold text-sm shadow-ios shadow-ios-blue/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                  >
                    <mat-icon class="!text-[20px]">add</mat-icon>
                    <span class="tracking-tight">
                      @switch (selectedTab()) {
                        @case ('News') { Add News }
                        @case ('Jobs') { Add Job }
                        @case ('Videos') { Add Video }
                        @case ('Products') { Add Product }
                        @case ('Images') { Add Image }
                        @default { Add {{ selectedTab() }} }
                      }
                    </span>
                  </button>
                }
              </div>

              @if (isLoadingAssets()) {
                <div class="flex justify-center p-24">
                  <div
                    class="w-10 h-10 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"
                  ></div>
                </div>
              } @else if (assets().length === 0) {
                <div
                  class="bento-card p-16 flex flex-col items-center justify-center text-center animate-ios-slide relative overflow-hidden group"
                  style="animation-delay: 0.3s"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-tr from-ios-purple/5 to-transparent"
                  ></div>
                  <div
                    class="relative z-10 w-24 h-24 rounded-3xl bg-white/50 dark:bg-zinc-800/50 backdrop-blur-md flex items-center justify-center mb-8 shadow-ios-sm border border-black/5 dark:border-white/5 group-hover:scale-110 transition-transform duration-700"
                  >
                    <mat-icon class="text-5xl text-ios-purple opacity-50"
                      >grid_view</mat-icon
                    >
                  </div>
                  <h3
                    class="text-xl font-bold tracking-tight mb-2 relative z-10"
                  >
                    No {{ selectedTab() }} Available
                  </h3>
                  <p
                    class="text-[15px] font-medium opacity-60 max-w-xs relative z-10"
                  >
                    When you share {{ selectedTab().toLowerCase() }}, they will
                    manifest here.
                  </p>
                </div>
              } @else {
                <div
                  class="grid grid-cols-1 md:grid-cols-2 gap-8 animate-ios-slide"
                  style="animation-delay: 0.3s"
                >
                  @for (asset of assets(); track asset.id) {
                    <div
                      (click)="viewAssetDetail(asset)"
                      (keydown.enter)="viewAssetDetail(asset)"
                      tabindex="0"
                      class="cursor-pointer bento-card overflow-hidden group hover:shadow-ios-md flex flex-col transform hover:-translate-y-1 transition-all duration-500"
                    >
                      @if (
                        selectedTab() === "Images" ||
                        selectedTab() === "Products" ||
                        selectedTab() === "Videos" ||
                        selectedTab() === "News"
                      ) {
                        <div
                          class="aspect-video relative overflow-hidden bg-black/5"
                        >
                          <img
                            [src]="
                              selectedTab() === 'Images'
                                ? $any(asset).imageUrl
                                : selectedTab() === 'Products'
                                  ? $any(asset).imageUrl
                                  : selectedTab() === 'News'
                                    ? $any(asset).images?.[0]
                                    : $any(asset).thumbnailUrl
                            "
                            class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                            referrerpolicy="no-referrer"
                            alt="Asset preview"
                          />
                          <div
                            class="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors"
                          ></div>
                          @if (selectedTab() === "Videos") {
                            <div
                              class="absolute inset-0 flex items-center justify-center"
                            >
                              <div
                                class="w-16 h-16 rounded-full bg-white/20 backdrop-blur-xl flex items-center justify-center text-white shadow-2xl border border-white/30 transform group-hover:scale-115 transition-transform duration-500"
                              >
                                <mat-icon class="text-4xl drop-shadow-lg"
                                  >play_arrow</mat-icon
                                >
                              </div>
                            </div>
                          }
                          @if (selectedTab() === "Products") {
                            <div
                              class="absolute top-5 right-5 px-4 py-2 bg-ios-blue/90 backdrop-blur-xl text-white text-sm font-bold rounded-2xl shadow-ios border border-white/20"
                            >
                              {{
                                $any(asset).price
                                  | currency: $any(asset).currency
                              }}
                            </div>
                          }
                        </div>
                      }
                      <div
                        class="p-6 flex-1 flex flex-col relative bg-white/50 dark:bg-zinc-900/50"
                      >
                        <div class="flex items-center justify-between mb-4">
                          <div class="flex flex-wrap items-center gap-2">
                            <span
                              class="px-3 py-1 rounded-lg bg-ios-blue/10 text-ios-blue text-[10px] font-black uppercase tracking-widest border border-ios-blue/20"
                            >
                              {{ asset.category }}
                            </span>
                            @if (asset.status) {
                              <span
                                [class]="
                                  asset.status === 'published'
                                    ? 'px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider'
                                    : 'px-2 py-1 rounded-lg bg-zinc-505/10 text-zinc-400 border border-zinc-500/20 text-[9px] font-black uppercase tracking-wider'
                                "
                              >
                                {{ asset.status }}
                              </span>
                            }
                            @if (asset.verificationStatus) {
                              <span
                                [class]="
                                  asset.verificationStatus === 'approved'
                                    ? 'px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider'
                                    : asset.verificationStatus === 'rejected'
                                      ? 'px-2 py-1 rounded-lg bg-rose-500/10 text-rose-500 border border-rose-500/20 text-[9px] font-black uppercase tracking-wider'
                                      : 'px-2 py-1 rounded-lg bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[9px] font-black uppercase tracking-wider'
                                "
                              >
                                {{ asset.verificationStatus }}
                              </span>
                            }
                            @if (selectedTab() === "Jobs") {
                              <span
                                class="text-[10px] font-black uppercase tracking-widest opacity-60 text-zinc-900 dark:text-white/60 flex items-center gap-1.5"
                              >
                                <mat-icon class="text-[14px]"
                                  >business</mat-icon
                                >
                                {{ $any(asset).company }}
                              </span>
                            }
                          </div>
                          @if (canManageOrganization() || isOwner()) {
                            <div class="flex items-center gap-2">
                              <button
                                (click)="$event.stopPropagation(); openEditAssetModal(asset)"
                                class="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-ios-blue/10 hover:text-ios-blue transition-all active:scale-90 border border-black/5 dark:border-white/5"
                              >
                                <mat-icon class="text-[18px]">edit</mat-icon>
                              </button>
                              <button
                                (click)="$event.stopPropagation(); deleteAsset(asset)"
                                class="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-ios-red/10 hover:text-ios-red transition-all active:scale-90 border border-black/5 dark:border-white/5"
                              >
                                <mat-icon class="text-[18px]">delete</mat-icon>
                              </button>
                            </div>
                          }
                        </div>
                        <h4
                          class="font-bold text-xl tracking-tight mb-3 group-hover:text-ios-blue transition-colors"
                        >
                          {{ asset.title }}
                        </h4>
                        <p
                          class="text-[14px] opacity-50 line-clamp-2 mb-6 font-medium leading-relaxed"
                        >
                          {{ asset.description }}
                        </p>

                        <div
                          class="mt-auto pt-5 border-t border-black/5 dark:border-white/5 flex items-center justify-between"
                        >
                          <div
                            class="flex items-center gap-5 text-[11px] font-black uppercase tracking-widest opacity-30"
                          >
                            <span class="flex items-center gap-1.5"
                              ><mat-icon class="text-[16px]"
                                >visibility</mat-icon
                              >
                              {{ (asset.metadata?.viewedBy?.length || asset.metadata?.views?.length || 0) | countFormat }}</span
                            >
                            <span class="flex items-center gap-1.5"
                              ><mat-icon class="text-[16px]">favorite</mat-icon>
                              {{ (asset.metadata?.likedBy?.length || asset.metadata?.likes?.length || 0) | countFormat }}</span
                            >
                          </div>
                          <span
                            class="text-[11px] font-black uppercase tracking-widest opacity-20"
                            >{{
                              toDate(asset.createdAt) | date: "MMM d, y"
                            }}</span
                          >
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            }
          </div>
        } @else {
          <div
            class="flex flex-col items-center justify-center h-[60vh] space-y-6 relative"
          >
            <div
              class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-ios-blue/10 blur-[40px] rounded-full animate-liquid"
            ></div>
            <div class="relative w-24 h-24 z-10">
              <div
                class="absolute inset-0 border-4 border-ios-blue/20 rounded-full"
              ></div>
              <div
                class="absolute inset-0 border-4 border-ios-blue border-t-transparent rounded-full animate-spin"
              ></div>
            </div>
            <p
              class="text-[14px] font-bold tracking-widest uppercase opacity-40 animate-pulse relative z-10"
            >
              Loading Profile...
            </p>
          </div>
        }
      </main>

      <!-- Followers Bottom Sheet -->
      @if (isFollowersSheetOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-ios-fade">
          <div class="bg-white dark:bg-zinc-900 w-full sm:max-w-md rounded-t-[32px] sm:rounded-[32px] shadow-2xl overflow-hidden max-h-[85vh] sm:max-h-[75vh] flex flex-col transform transition-transform duration-500 animate-ios-slide border border-black/5 dark:border-white/5">
            <!-- Header -->
            <div class="p-5 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
              <h3 class="text-lg font-bold text-ios-title">Followers</h3>
              <button (click)="closeFollowersSheet()" class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center text-gray-500 hover:bg-black/10 dark:hover:bg-white/10 transition-all active:scale-95" title="Close">
                <mat-icon class="!text-lg">close</mat-icon>
              </button>
            </div>
            
            <!-- Search Bar -->
            <div class="px-5 py-3 border-b border-black/5 dark:border-white/5">
              <div class="flex items-center gap-2 bg-black/5 dark:bg-white/5 rounded-2xl px-3 py-2 border border-black/5 dark:border-white/5 focus-within:ring-1 focus-within:ring-ios-blue transition-all">
                <mat-icon class="text-gray-400 !text-lg flex items-center justify-center">search</mat-icon>
                <input 
                  type="text" 
                  [ngModel]="followersSheetSearchText()" 
                  (ngModelChange)="followersSheetSearchText.set($event)"
                  placeholder="Search followers..." 
                  class="bg-transparent border-none outline-none text-sm w-full font-medium"
                />
                @if (followersSheetSearchText()) {
                  <button (click)="followersSheetSearchText.set('')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-250">
                    <mat-icon class="!text-sm flex items-center justify-center">close</mat-icon>
                  </button>
                }
              </div>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-5 space-y-3 min-h-[250px] scrollbar-thin">
              @if (isFollowersLoading()) {
                <div class="flex flex-col items-center justify-center py-10 gap-3">
                  <div class="w-8 h-8 border-2 border-ios-blue border-t-transparent rounded-full animate-spin"></div>
                  <p class="text-xs text-gray-500 font-medium">Loading followers...</p>
                </div>
              } @else {
                @let followers = filteredFollowers;
                @if (followers.length === 0) {
                  <div class="flex flex-col items-center justify-center py-10 text-center">
                    <mat-icon class="text-gray-350 dark:text-gray-700 !text-4xl h-auto w-auto mb-2">groups</mat-icon>
                    <p class="text-sm font-medium text-gray-400">No followers found</p>
                  </div>
                } @else {
                  @for (acc of followers; track acc.uid) {
                    <ng-container *ngTemplateOutlet="accountCardTpl; context: { $implicit: acc }"></ng-container>
                  }
                }
              }
            </div>
          </div>
        </div>
      }

      <!-- Following Bottom Sheet -->
      @if (isFollowingSheetOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-ios-fade">
          <div class="bg-white dark:bg-zinc-900 w-full sm:max-w-md rounded-t-[32px] sm:rounded-[32px] shadow-2xl overflow-hidden max-h-[85vh] sm:max-h-[75vh] flex flex-col transform transition-transform duration-500 animate-ios-slide border border-black/5 dark:border-white/5">
            <!-- Header -->
            <div class="p-5 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
              <h3 class="text-lg font-bold text-ios-title">Following</h3>
              <button (click)="closeFollowingSheet()" class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center text-gray-500 hover:bg-black/10 dark:hover:bg-white/10 transition-all active:scale-95" title="Close">
                <mat-icon class="!text-lg">close</mat-icon>
              </button>
            </div>
            
            <!-- Search Bar -->
            <div class="px-5 py-3 border-b border-black/5 dark:border-white/5">
              <div class="flex items-center gap-2 bg-black/5 dark:bg-white/5 rounded-2xl px-3 py-2 border border-black/5 dark:border-white/5 focus-within:ring-1 focus-within:ring-ios-blue transition-all">
                <mat-icon class="text-gray-400 !text-lg flex items-center justify-center">search</mat-icon>
                <input 
                  type="text" 
                  [ngModel]="followingSheetSearchText()" 
                  (ngModelChange)="followingSheetSearchText.set($event)"
                  placeholder="Search following..." 
                  class="bg-transparent border-none outline-none text-sm w-full font-medium"
                />
                @if (followingSheetSearchText()) {
                  <button (click)="followingSheetSearchText.set('')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-250">
                    <mat-icon class="!text-sm flex items-center justify-center">close</mat-icon>
                  </button>
                }
              </div>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-5 space-y-3 min-h-[250px] scrollbar-thin">
              @if (isFollowingLoading()) {
                <div class="flex flex-col items-center justify-center py-10 gap-3">
                  <div class="w-8 h-8 border-2 border-ios-blue border-t-transparent rounded-full animate-spin"></div>
                  <p class="text-xs text-gray-500 font-medium">Loading following...</p>
                </div>
              } @else {
                @let following = filteredFollowing;
                @if (following.length === 0) {
                  <div class="flex flex-col items-center justify-center py-10 text-center">
                    <mat-icon class="text-gray-350 dark:text-gray-700 !text-4xl h-auto w-auto mb-2">person_add</mat-icon>
                    <p class="text-sm font-medium text-gray-400">No followed users found</p>
                  </div>
                } @else {
                  @for (acc of following; track acc.uid) {
                    <ng-container *ngTemplateOutlet="accountCardTpl; context: { $implicit: acc }"></ng-container>
                  }
                }
              }
            </div>
          </div>
        </div>
      }

      <!-- Reusable Card Template for Person vs Organization vs Country in Sheets -->
      <ng-template #accountCardTpl let-acc>
        @if (acc.accountType === 'organization') {
          <app-organization-card [account]="acc" (click)="closeSheets()" (cardClick)="closeSheets()" />
        } @else if (acc.accountType === 'country') {
          <app-country-card [account]="acc" (click)="closeSheets()" (cardClick)="closeSheets()" />
        } @else {
          <app-person-card [account]="acc" (click)="closeSheets()" (cardClick)="closeSheets()" />
        }
      </ng-template>

      <!-- Confirmation Modal -->
      @if (modal.isOpen()) {
        <div
          class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade"
        >
          <div
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="closeModal()"
            (keydown.enter)="closeModal()"
            tabindex="0"
          ></div>
          <div
            class="relative w-full max-w-sm bg-white/90 dark:bg-zinc-900/90 backdrop-blur-3xl rounded-[40px] p-10 shadow-2xl border border-black/5 dark:border-white/10 animate-ios-slide overflow-hidden"
          >
            <div
              class="absolute top-[-50%] left-[-20%] w-[140%] h-[200%] blur-[60px] rounded-full animate-liquid pointer-events-none opacity-20"
              [class.bg-ios-blue]="
                modal.type() !== 'signout' &&
                modal.type() !== 'verify' &&
                modal.type() !== 'deleteAsset'
              "
              [class.bg-ios-red]="
                modal.type() === 'signout' || modal.type() === 'deleteAsset'
              "
              [class.bg-ios-yellow]="modal.type() === 'verify'"
            ></div>

            <div class="relative z-10 flex flex-col items-center">
              <div
                class="w-20 h-20 rounded-3xl flex items-center justify-center mb-8 shadow-ios"
                [class.bg-ios-blue]="
                  modal.type() !== 'signout' &&
                  modal.type() !== 'verify' &&
                  modal.type() !== 'deleteAsset'
                "
                [class.bg-ios-red]="
                  modal.type() === 'signout' || modal.type() === 'deleteAsset'
                "
                [class.bg-ios-yellow]="modal.type() === 'verify'"
              >
                <mat-icon class="text-white text-[36px]">
                  {{
                    modal.type() === "email"
                      ? "alternate_email"
                      : modal.type() === "password"
                        ? "lock_reset"
                        : modal.type() === "verify"
                          ? "verified_user"
                          : modal.type() === "deleteAsset"
                            ? "delete_forever"
                            : "logout"
                  }}
                </mat-icon>
              </div>

              <h3 class="text-2xl font-bold tracking-tight text-center mb-3">
                {{ modal.title() }}
              </h3>
              <p
                class="text-[14px] font-medium opacity-50 text-center mb-10 leading-relaxed max-w-[90%]"
              >
                {{ modal.message() }}
              </p>

              @if (modal.type() === "email") {
                <div class="mb-10 w-full">
                  <div
                    class="ios-input-container !h-14 bg-black/5 dark:bg-white/5 border-transparent"
                  >
                    <input
                      type="email"
                      [(ngModel)]="modalInput"
                      class="ios-input text-center text-lg font-semibold"
                      placeholder="new@kicgle.node"
                    />
                  </div>
                </div>
              }

              <div class="flex flex-col gap-3 w-full">
                <button
                  (click)="confirmAction()"
                  class="w-full h-14 rounded-2xl font-bold text-[15px] tracking-tight text-white transition-all shadow-ios-lg active:scale-95"
                  [class.bg-ios-blue]="
                    modal.type() !== 'signout' &&
                    modal.type() !== 'verify' &&
                    modal.type() !== 'deleteAsset'
                  "
                  [class.bg-ios-red]="
                    modal.type() === 'signout' || modal.type() === 'deleteAsset'
                  "
                  [class.bg-ios-yellow]="modal.type() === 'verify'"
                  [class.shadow-ios-blue/30]="
                    modal.type() !== 'signout' &&
                    modal.type() !== 'verify' &&
                    modal.type() !== 'deleteAsset'
                  "
                  [class.shadow-ios-red/30]="
                    modal.type() === 'signout' || modal.type() === 'deleteAsset'
                  "
                  [class.shadow-ios-yellow/30]="modal.type() === 'verify'"
                >
                  {{ modal.confirmText() }}
                </button>
                <button
                  (click)="closeModal()"
                  class="w-full h-14 rounded-2xl font-bold text-[15px] tracking-tight hover:bg-black/5 dark:hover:bg-white/5 transition-all text-ios-title opacity-40"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Sleek Instagram-style Image Upload Modal -->
      @if (isUploadingImageModalOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div 
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="closeAddImageModal()"
            (keydown.enter)="closeAddImageModal()"
            tabindex="0"
          ></div>
          
          <div class="bg-white dark:bg-zinc-900 w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl relative z-10 border border-black/5 dark:border-white/5 animate-ios-slide flex flex-col max-h-[90vh]">
            <!-- Header -->
            <div class="p-5 border-b border-black/5 dark:border-white/5 flex items-center justify-between bg-gray-50/50 dark:bg-zinc-800/30 font-semibold text-ios-title">
              <h3 class="text-lg font-bold">Create New Image Post</h3>
              <button 
                (click)="closeAddImageModal()" 
                class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center text-gray-500 hover:bg-black/10 dark:hover:bg-white/10 transition-all active:scale-95"
                title="Close"
              >
                <mat-icon class="!text-lg">close</mat-icon>
              </button>
            </div>
            
            <div class="p-6 space-y-5 flex-1 overflow-y-auto">
              <!-- Drag and Drop upload area / Preview -->
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <label for="image-file-input" class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">Image Asset</label>
                  @if (imagePreviewUrl()) {
                    <button 
                      (click)="selectedImageFile.set(null); imagePreviewUrl.set(null)"
                      class="text-xs text-ios-red font-semibold hover:underline"
                    >
                      Clear Image
                    </button>
                  }
                </div>
                
                @if (imagePreviewUrl()) {
                  <!-- Strict 1:1 Instagram style crop preview container -->
                  <div class="relative w-full aspect-square overflow-hidden bg-gray-900 rounded-lg max-w-sm mx-auto group">
                    <img 
                      appImagePan
                      (panEnd)="onSingleImagePanEnd($event)"
                      [src]="imagePreviewUrl()" 
                      alt="Preview" 
                      class="absolute inset-0 w-full h-full object-cover origin-center cursor-grab"
                      draggable="false"
                      referrerpolicy="no-referrer"
                    />
                    <!-- Overlay indicator showing square framing -->
                    <div class="absolute inset-0 border-4 border-white/20 pointer-events-none rounded-lg z-10"></div>
                    <!-- Zoom indicator -->
                    <div class="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] text-white/90 font-medium z-10 flex items-center gap-1 pointer-events-none">
                      <mat-icon class="!text-[12px] h-3 w-3">aspect_ratio</mat-icon>
                      <span>1:1 Square Crop (Instagram Style)</span>
                    </div>
                  </div>
                } @else {
                  <div 
                    class="relative group cursor-pointer border-2 border-dashed border-ios-blue/30 rounded-2xl hover:border-ios-blue transition-all bg-ios-blue/5 h-64 flex flex-col items-center justify-center overflow-hidden"
                    (dragover)="$event.preventDefault()"
                    (drop)="$event.preventDefault(); onImageDropped($event)"
                  >
                    <input 
                      id="image-file-input"
                      type="file" 
                      accept="image/*" 
                      (change)="onImageFileSelected($event)" 
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                    />
                    
                    <div class="flex flex-col items-center justify-center text-center p-4">
                      <mat-icon class="text-ios-blue text-4xl mb-2">add_photo_alternate</mat-icon>
                      <span class="text-sm font-bold text-ios-blue">Drag & drop photo or click to browse</span>
                      <span class="text-[10px] opacity-50 mt-1">Supports JPEG, PNG, WEBP, GIF (Max 10MB)</span>
                    </div>
                  </div>
                }
              </div>
              
              <!-- License Type & Visibility Options -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label for="img-license" class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">License</label>
                  <div class="ios-input-container">
                    <select 
                      id="img-license" 
                      [(ngModel)]="imageToUploadMetadata().license"
                      class="ios-input bg-transparent w-full outline-none"
                    >
                      <option value="standard">Standard</option>
                      <option value="commercial">Commercial</option>
                      <option value="open-source">Open Source</option>
                    </select>
                  </div>
                </div>
                
                <div class="space-y-2">
                  <label for="img-status" class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">Post Status</label>
                  <div class="ios-input-container">
                    <select 
                      id="img-status" 
                      [(ngModel)]="imageToUploadMetadata().status"
                      class="ios-input bg-transparent w-full outline-none"
                    >
                      <option value="published">Published</option>
                      <option value="unpublished">Unpublished</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <!-- Review Auto-Status workflow note -->
              <div class="p-3 bg-gray-50 dark:bg-zinc-805 rounded-xl border border-black/5 dark:border-white/5 text-[11px] text-gray-500 flex items-start gap-2">
                <mat-icon class="text-ios-blue !text-sm mt-0.5 shrink-0">info</mat-icon>
                <span>Images are uploaded as <strong>uploaded</strong> status. Redesigned flows automatically auto-approve approved images instantly for public view, or mark them with administrative workflows!</span>
              </div>
            </div>
            
            <!-- Actions -->
            <div class="p-5 border-t border-black/5 dark:border-white/5 flex items-center justify-between gap-4 bg-gray-50/30 dark:bg-zinc-805/20">
              <button 
                (click)="closeAddImageModal()" 
                class="flex-1 h-12 rounded-xl font-bold hover:bg-black/5 dark:hover:bg-white/5 transition-all text-xs text-ios-title opacity-40"
              >
                Cancel
              </button>
              <button 
                (click)="uploadAndSaveImage()" 
                [disabled]="isUploadingImage() || !selectedImageFile()"
                class="flex-[2] h-12 rounded-xl font-bold bg-ios-blue text-white shadow-ios shadow-ios-blue/20 hover:scale-[1.02] active:scale-98 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                @if (isUploadingImage()) {
                  <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Uploading...</span>
                } @else {
                  <mat-icon class="!text-lg">cloud_upload</mat-icon>
                  <span>Upload Post</span>
                }
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Deletion Confirmation Modal -->
      @if (isDeletingImageModalOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div 
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="cancelDeleteUserImage()"
            (keydown.enter)="cancelDeleteUserImage()"
            tabindex="0"
          ></div>
          
          <div class="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[32px] overflow-hidden shadow-2xl relative z-10 border border-black/5 dark:border-white/5 p-6 md:p-8 animate-ios-slide flex flex-col text-center">
            <div class="w-16 h-16 rounded-full bg-ios-red/10 text-ios-red flex items-center justify-center mx-auto mb-4">
              <mat-icon class="!text-3xl h-8 w-8">delete_forever</mat-icon>
            </div>
            
            <h4 class="text-xl font-bold tracking-tight text-ios-title mb-2">Delete Confirmation</h4>
            <p class="text-[15px] font-medium opacity-60 px-2 leading-normal mb-8 text-center text-gray-500 text-sm">
              Are you sure you want to delete this photo?
            </p>
            
            <div class="flex flex-col gap-3">
              <button 
                (click)="executeDeleteUserImage()"
                [disabled]="isDeletingImageProgress()"
                class="w-full h-12 rounded-xl bg-ios-red hover:bg-red-600 text-white font-bold text-[15px] shadow-lg shadow-ios-red/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
              >
                @if (isDeletingImageProgress()) {
                  <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                } @else {
                  <span>Delete</span>
                }
              </button>
              
              <button 
                (click)="cancelDeleteUserImage()"
                [disabled]="isDeletingImageProgress()"
                class="w-full h-12 rounded-xl font-bold hover:bg-black/5 dark:hover:bg-white/5 transition-all text-[15px] text-gray-500 dark:text-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Metadata Edit Modal -->
      @if (isEditingImageModalOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div 
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="closeEditUserImageModal()"
            (keydown.enter)="closeEditUserImageModal()"
            tabindex="0"
          ></div>
          
          <div class="bg-white dark:bg-zinc-900 w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl relative z-10 border border-black/5 dark:border-white/5 animate-ios-slide flex flex-col max-h-[90vh]">
            <!-- Header -->
            <div class="p-5 border-b border-black/5 dark:border-white/5 flex items-center justify-between bg-gray-50/50 dark:bg-zinc-800/30 font-semibold text-ios-title">
              <h3 class="text-lg font-bold">Edit Photo Settings</h3>
              <button 
                (click)="closeEditUserImageModal()" 
                class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center text-gray-500 hover:bg-black/10 dark:hover:bg-white/10 transition-all active:scale-95"
                title="Close"
              >
                <mat-icon class="!text-lg">close</mat-icon>
              </button>
            </div>
            
            <div class="p-6 space-y-6 flex-1 overflow-y-auto">
              <!-- Non-editable Image Preview -->
              <div class="space-y-2">
                <span class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">Selected Photo</span>
                @if (imageToEdit()) {
                  <div class="aspect-video w-full overflow-hidden bg-gray-100 dark:bg-zinc-805 rounded-2xl border border-black/5 dark:border-white/5 relative">
                    <img 
                      [src]="imageToEdit()?.imageUrl" 
                      alt="Current Image" 
                      class="object-cover object-center w-full h-full"
                      referrerpolicy="no-referrer"
                    />
                    <div class="absolute inset-0 bg-black/20 pointer-events-none rounded-2xl"></div>
                    <div class="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] text-white/90 font-medium flex items-center gap-1">
                      <mat-icon class="!text-[12px] h-3 w-3">lock</mat-icon>
                      <span>Image file cannot be changed or replaced</span>
                    </div>
                  </div>
                }
              </div>
              
              <!-- License Type & Visibility Options -->
              <div class="space-y-4">
                <div class="space-y-2">
                  <label for="edit-img-license" class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">License Type</label>
                  <div class="ios-input-container">
                    <select 
                      id="edit-img-license" 
                      [(ngModel)]="imageEditMetadata().license"
                      class="ios-input bg-transparent w-full outline-none"
                    >
                      <option value="standard">Standard</option>
                      <option value="commercial">Commercial</option>
                      <option value="open-source font-sans">Open Source</option>
                    </select>
                  </div>
                </div>
                
                <div class="space-y-2">
                  <label for="edit-img-status" class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">Post Status</label>
                  <div class="ios-input-container">
                    <select 
                      id="edit-img-status" 
                      [(ngModel)]="imageEditMetadata().status"
                      class="ios-input bg-transparent w-full outline-none"
                    >
                      <option value="published">Published</option>
                      <option value="unpublished">Unpublished</option>
                    </select>
                  </div>
                  <p class="text-[10px] text-gray-400 dark:text-gray-500 px-1 mt-1 leading-normal">
                    Setting the status to Published makes it visible in search clusters. Unpublished or Review hides it from public streams.
                  </p>
                </div>

                <!-- Post Verification Status Workflow -->
                <div class="space-y-2">
                  <label for="edit-img-verification-status" class="text-[11px] font-bold uppercase tracking-widest opacity-40 px-1">Verification Status</label>
                  <div class="ios-input-container">
                    <select 
                      id="edit-img-verification-status" 
                      [(ngModel)]="imageEditMetadata().verificationStatus"
                      class="ios-input bg-transparent w-full outline-none"
                    >
                      <option value="review">Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Actions -->
            <div class="p-5 border-t border-black/5 dark:border-white/5 flex items-center justify-between gap-4 bg-gray-50/30 dark:bg-zinc-805/20">
              <button 
                (click)="closeEditUserImageModal()" 
                class="flex-1 h-12 rounded-xl font-bold hover:bg-black/5 dark:hover:bg-white/5 transition-all text-sm text-ios-title opacity-40"
              >
                Cancel
              </button>
              <button 
                (click)="saveUserImageMetadata()" 
                [disabled]="isSavingImageMetadataProgress()"
                class="flex-[2] h-12 rounded-xl font-bold bg-ios-blue text-white shadow-ios shadow-ios-blue/20 hover:scale-[1.02] active:scale-98 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                @if (isSavingImageMetadataProgress()) {
                  <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Saving...</span>
                } @else {
                  <mat-icon class="!text-lg">save</mat-icon>
                  <span>Save Changes</span>
                }
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Unified Creative modal (Instagram Style) -->
      @if (isUnifiedModalOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div
            class="absolute inset-0 bg-black/70 backdrop-blur-md"
            (click)="closeUnifiedModal()"
            (keydown.enter)="closeUnifiedModal()"
            tabindex="0"
          ></div>
          <div class="bg-white dark:bg-zinc-900 w-full max-w-lg rounded-[32px] overflow-hidden shadow-2xl relative z-10 border border-black/5 dark:border-white/5 animate-ios-slide flex flex-col max-h-[92vh]">
            
            <!-- Header -->
            <div class="p-5 border-b border-black/5 dark:border-white/5 flex items-center justify-between bg-gray-50/50 dark:bg-zinc-800/30 font-semibold text-ios-title">
              <span class="text-sm font-black uppercase tracking-wider text-ios-blue flex items-center gap-2">
                <mat-icon class="!text-lg font-black">{{ isEditingUnified() ? 'edit' : 'add_circle' }}</mat-icon>
                {{ isEditingUnified() ? 'Edit Existing' : 'Add New' }}{{ activeModalType() === 'image' ? ' Image/Photo' : ' ' + activeModalType() }}
              </span>
              <button
                (click)="closeUnifiedModal()"
                class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center text-gray-500 hover:bg-black/10 dark:hover:bg-white/10 transition-all active:scale-95"
                title="Close"
              >
                <mat-icon class="!text-lg">close</mat-icon>
              </button>
            </div>

            <form [formGroup]="unifiedForm" (ngSubmit)="handleUnifiedSubmit()" class="flex-1 overflow-y-auto scrollbar-hide flex flex-col">
              
              <!-- Image selection, viewing & real-time drag-and-drop / panning frame (Instagram Style - CRITICAL) -->
              <div class="p-6 pb-0">
                <div class="space-y-3">
                  <span class="text-[10px] font-extrabold uppercase tracking-widest opacity-40 px-1">
                    {{ activeModalType() === 'video' ? 'Video Thumbnail Picture (Standard cover)' : 'Frame Crop Preview (Click & dynamic drag to pan / scroll wheel to zoom)' }}
                  </span>
                  
                  <div class="relative w-full overflow-hidden bg-black/90 rounded-3xl border border-black/5 dark:border-white/10 group shadow-ios-md flex items-center justify-center select-none"
                       [class.aspect-square]="activeModalType() === 'image' || activeModalType() === 'product' || activeModalType() === 'job'"
                       [class.aspect-video]="activeModalType() === 'news' || activeModalType() === 'video'"
                       [class.pointer-events-none]="isEditingUnified() && ((activeModalType() === 'image' || activeModalType() === 'video') ? !isUserAuthorizedToEditMedia() : activeModalType() === 'product')">
                    
                    @if (isEditingUnified() && (activeModalType() === 'image' || activeModalType() === 'video') && !isUserAuthorizedToEditMedia()) {
                      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center z-30 select-none">
                        <mat-icon class="text-white/60 mb-2 !text-2xl h-6 w-6">lock</mat-icon>
                        <span class="text-[10px] uppercase tracking-widest font-extrabold text-white">Immutable Media Resource</span>
                        <span class="text-[9px] text-white/50 mt-1 max-w-[200px]">Standard creators are restricted from modifying source files. Update status and license settings below.</span>
                      </div>
                    }

                    @if (isEditingUnified() && activeModalType() === 'product') {
                      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center z-30 select-none">
                        <mat-icon class="text-white/60 mb-2 !text-2xl h-6 w-6">photo_library</mat-icon>
                        <span class="text-[10px] uppercase tracking-widest font-extrabold text-white">Media Files Protected</span>
                        <span class="text-[9px] text-white/50 mt-1 max-w-[200px]">Product media library is updated separately. Alter details and category metadata below.</span>
                      </div>
                    }

                    @if (selectedImagesList().length > 0) {
                      <img
                        appImagePan
                        (panEnd)="onUnifiedImagePanEnd($event, activeImageIndex())"
                        [panState]="selectedImagesList()[activeImageIndex()].cropPos"
                        [src]="selectedImagesList()[activeImageIndex()].previewUrl"
                        alt="Creative Canvas"
                        class="absolute inset-0 w-full h-full object-cover origin-center cursor-grab"
                        draggable="false"
                        referrerpolicy="no-referrer"
                      />
                      
                      <!-- Remove image -->
                      @if (!isEditingUnified() || (activeModalType() !== 'image' && activeModalType() !== 'video' && activeModalType() !== 'product') || isUserAuthorizedToEditMedia()) {
                        <button type="button" 
                                (click)="removeUnifiedImage(activeImageIndex(), $event)" 
                                class="absolute top-3 right-3 bg-red-500/90 text-white rounded-full p-2 hover:scale-110 hover:bg-red-600 transition-all z-20 shadow-lg">
                          <mat-icon class="!text-sm w-4.5 h-4.5">delete</mat-icon>
                        </button>
                      }

                      <!-- Frame metadata indicator -->
                      <div class="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-xl text-[10px] text-white/90 z-10 font-bold tracking-wider pointer-events-none">
                        Frame {{ activeImageIndex() + 1 }} / {{ selectedImagesList().length }}
                      </div>
                    } @else {
                      <div class="absolute inset-0 flex flex-col items-center justify-center p-8 text-center cursor-pointer hover:bg-white/5 transition-colors" 
                           (click)="unifiedFileInput.click()" (keydown.enter)="unifiedFileInput.click()" tabindex="0">
                        <div class="w-14 h-14 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center mb-4 sm:scale-110">
                          <mat-icon class="!text-3xl">add_photo_alternate</mat-icon>
                        </div>
                        <span class="text-sm font-extrabold text-white/90">
                          {{ activeModalType() === 'video' ? 'Upload Thumbnail Picture *' : 'Add Core Image Frame' }}
                        </span>
                        <span class="text-[10px] text-white/40 mt-1 max-w-[260px]">
                          {{ activeModalType() === 'video' ? 'Select an image file (JPEG/PNG) to serve as the poster/thumbnail.' : 'Upload file. Double scroll, pinch, or click & drag to position inside the border frame.' }}
                        </span>
                      </div>
                    }
                  </div>

                  <!-- Gallery/Frames carousel for multiple images (news, products) -->
                  @if (selectedImagesList().length > 0) {
                    <div class="flex items-center gap-3 overflow-x-auto py-1 scrollbar-hide">
                      @for (img of selectedImagesList(); track $index) {
                        <div class="relative w-12 h-12 rounded-xl overflow-hidden border-2 cursor-pointer shrink-0 transition-all hover:scale-105"
                             [class.border-ios-blue]="activeImageIndex() === $index"
                             [class.border-transparent]="activeImageIndex() !== $index"
                             (click)="activeImageIndex.set($index)"
                             (keydown.enter)="activeImageIndex.set($index)"
                             tabindex="0"
                        >
                          <img [src]="img.previewUrl" alt="Thumbnail" class="w-full h-full object-cover" />
                        </div>
                      }
                      
                      <!-- Multi-file support add-more trigger button -->
                      @if (!isEditingUnified() && ((activeModalType() === 'news' && selectedImagesList().length < 4) || (activeModalType() === 'product' && selectedImagesList().length < 10))) {
                        <button type="button" 
                                (click)="unifiedFileInput.click()" 
                                class="w-12 h-12 rounded-xl border border-dashed border-black/20 dark:border-white/20 hover:border-ios-blue flex flex-col items-center justify-center bg-black/5 dark:bg-white/5 text-ios-blue cursor-pointer shrink-0">
                          <mat-icon class="!text-base">add_a_photo</mat-icon>
                        </button>
                      }
                    </div>
                  }
                  
                  <input #unifiedFileInput 
                         type="file" 
                         accept="image/*" 
                         [multiple]="activeModalType() === 'news' || activeModalType() === 'product'" 
                         class="hidden" 
                         (change)="onUnifiedFileSelected($event)" />
                </div>
              </div>

              <!-- Form Dynamic Switch fields -->
              <div class="p-6 space-y-4 flex-1">
                
                @if (activeModalType() !== 'image') {
                  <!-- Title & Description (Hidden for Images) -->
                  <div class="space-y-1.5">
                    <label for="unified-title" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">
                      Title *
                    </label>
                    <div class="ios-input-container">
                      <input id="unified-title" type="text" formControlName="title" class="ios-input" placeholder="Title of the post" />
                    </div>
                    @if (unifiedForm.get('title')?.touched && unifiedForm.get('title')?.invalid) {
                      <p class="text-xs text-red-500 px-1 font-bold">Title is required (min 2 characters).</p>
                    }
                  </div>

                  <div class="space-y-1.5">
                    <label for="unified-description" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">
                      Description *
                    </label>
                    <div class="ios-input-container !h-auto">
                      <textarea id="unified-description" formControlName="description" class="ios-input py-2.5 h-20" placeholder="Detail description..."></textarea>
                    </div>
                    @if (unifiedForm.get('description')?.touched && unifiedForm.get('description')?.invalid) {
                      <p class="text-xs text-red-500 px-1 font-bold">Description is required (min 5 characters).</p>
                    }
                  </div>
                }

                <!-- Dynamic elements using native @switch as per Angular 21 requirements -->
                @switch (activeModalType()) {

                  <!-- CASE: IMAGE MODEL -->
                  @case ('image') {
                    <div class="space-y-4">
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5 col-span-2 md:col-span-1">
                          <label for="img-license" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">License Type</label>
                          <div class="ios-input-container">
                            <select id="img-license" formControlName="license" class="ios-input bg-transparent w-full outline-none">
                              <option value="Standard">Standard</option>
                              <option value="Open Source">Open Source</option>
                            </select>
                          </div>
                        </div>
                        <div class="space-y-1.5 col-span-2 md:col-span-1">
                          <span class="block text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Format Type</span>
                          <div class="p-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl flex items-center justify-between h-12">
                            <span class="text-xs font-bold text-ios-blue uppercase font-mono bg-ios-blue/10 px-2.5 py-0.5 rounded-lg border border-ios-blue/20">
                              {{ unifiedForm.get('format')?.value || 'JPEG' }}
                            </span>
                            <span class="text-[9px] text-gray-450 dark:text-gray-500 font-medium tracking-normal">Auto Detected</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  <!-- CASE: NEWS MODEL -->
                  @case ('news') {
                    <div class="space-y-4">
                      <div class="space-y-1.5">
                        <label for="news-category" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Category</label>
                        <div class="ios-input-container">
                          <select id="news-category" formControlName="category" class="ios-input bg-transparent w-full outline-none">
                            <option value="Technology">Technology</option>
                            <option value="Business">Business</option>
                            <option value="Education">Education</option>
                            <option value="Entertainment">Entertainment</option>
                            <option value="Sports">Sports</option>
                            <option value="Local">Local</option>
                            <option value="Global">Global</option>
                          </select>
                        </div>
                      </div>
                      <div class="flex items-center gap-3 p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10">
                        <input type="checkbox" id="news-breaking" formControlName="isBreaking" class="w-5 h-5 rounded-lg accent-ios-red" />
                        <label for="news-breaking" class="text-xs font-bold tracking-tight cursor-pointer">Set as Breaking News alert banner</label>
                      </div>
                    </div>
                  }

                  <!-- CASE: JOB MODEL -->
                  @case ('job') {
                    <div class="space-y-4">
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <label for="job-company" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Hiring Institution / Company</label>
                          <div class="ios-input-container">
                            <input id="job-company" type="text" formControlName="company" class="ios-input" placeholder="Organization name" />
                          </div>
                        </div>
                        <div class="space-y-1.5">
                          <label for="job-location" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Location / Office</label>
                          <div class="ios-input-container">
                            <input id="job-location" type="text" formControlName="location" class="ios-input" placeholder="City, State, or Country" />
                          </div>
                        </div>
                      </div>
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <label for="job-type" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Employment Type</label>
                          <div class="ios-input-container">
                            <select id="job-type" formControlName="jobType" class="ios-input bg-transparent w-full outline-none">
                              <option value="full-time">Full-time</option>
                              <option value="part-time">Part-time</option>
                              <option value="contract">Contractor</option>
                              <option value="freelance">Freelance</option>
                              <option value="internship">Internship</option>
                            </select>
                          </div>
                        </div>
                        <div class="space-y-1.5">
                          <label for="job-level" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Experience Tier</label>
                          <div class="ios-input-container">
                            <select id="job-level" formControlName="experienceLevel" class="ios-input bg-transparent w-full outline-none">
                              <option value="entry">Entry Level</option>
                              <option value="mid">Mid Career</option>
                              <option value="senior">Senior Executive</option>
                              <option value="lead">Lead / Architect</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <span class="block text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Job Category Type</span>
                          <div class="flex items-center gap-1 p-1 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl h-12 select-none">
                            <button type="button" 
                                    (click)="unifiedForm.get('jobCategory')?.setValue('Private')"
                                    [class]="unifiedForm.get('jobCategory')?.value === 'Private' ? 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold bg-ios-blue text-white shadow-ios h-full flex items-center justify-center' : 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white transition-all h-full flex items-center justify-center'"
                            >
                              Private
                            </button>
                            <button type="button" 
                                    (click)="unifiedForm.get('jobCategory')?.setValue('Government')"
                                    [class]="unifiedForm.get('jobCategory')?.value === 'Government' ? 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold bg-ios-blue text-white shadow-ios h-full flex items-center justify-center' : 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white transition-all h-full flex items-center justify-center'"
                            >
                              Govt
                            </button>
                          </div>
                        </div>
                        <div class="space-y-1.5">
                          <label for="job-salary" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Salary Range (INR / ₹)</label>
                          <div class="ios-input-container flex items-center px-3 gap-2">
                            <span class="text-sm font-bold text-ios-blue mb-0.5">₹</span>
                            <input id="job-salary" type="number" formControlName="price" class="bg-transparent border-0 outline-none w-full text-black dark:text-white h-full" placeholder="salary per month" />
                          </div>
                        </div>
                      </div>

                      <div class="flex items-center gap-3 p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10">
                        <input type="checkbox" id="job-remote" formControlName="isRemote" class="w-5 h-5 rounded-lg accent-ios-blue" />
                        <label for="job-remote" class="text-xs font-bold tracking-tight cursor-pointer">Support 100% Remote / Distributed role</label>
                      </div>
                    </div>
                  }

                  <!-- CASE: PRODUCT MODEL -->
                  @case ('product') {
                    <div class="space-y-4">
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <label for="prod-price" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Price Cost (INR Locked)</label>
                          <div class="ios-input-container flex items-center px-3 gap-2">
                            <span class="text-sm font-bold text-ios-blue mb-0.5">₹</span>
                            <input id="prod-price" type="number" formControlName="price" class="bg-transparent border-0 outline-none w-full text-black dark:text-white h-full" placeholder="0.00" />
                          </div>
                        </div>
                        <div class="space-y-1.5">
                          <label for="prod-brand" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Brand Manufacturer</label>
                          <div class="ios-input-container">
                            <input id="prod-brand" type="text" formControlName="brand" class="ios-input" placeholder="Company Brand" />
                          </div>
                        </div>
                      </div>
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <label for="prod-qty" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Stock Quantity</label>
                          <div class="ios-input-container">
                            <input id="prod-qty" type="number" formControlName="stockQuantity" class="ios-input" placeholder="Available units" />
                          </div>
                        </div>
                        <div class="space-y-1.5">
                          <label for="prod-category" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Category *</label>
                          <div class="ios-input-container">
                            <select id="prod-category" formControlName="category" class="ios-input bg-transparent w-full outline-none">
                              <option value="General">General</option>
                              <option value="Electronics">Electronics</option>
                              <option value="Fashion">Fashion</option>
                              <option value="Home & Kitchen">Home & Kitchen</option>
                              <option value="Books">Books</option>
                              <option value="Sports">Sports</option>
                              <option value="Beauty & Personal Care">Beauty & Personal Care</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  <!-- CASE: VIDEO MODEL -->
                  @case ('video') {
                    <div class="space-y-4">
                      <!-- Segmented Chip Framework on Top -->
                      <div class="space-y-1.5">
                        <span class="block text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Video Segment Type</span>
                        <div class="flex items-center gap-1 p-1 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl select-none">
                          @for (seg of segmentTypes; track seg) {
                            <button type="button" 
                                    (click)="activeVideoSegment.set(seg)"
                                    [class]="activeVideoSegment() === seg ? 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold bg-ios-blue text-white shadow-ios flex items-center justify-center' : 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold text-gray-450 hover:text-gray-950 dark:hover:text-white transition-all flex items-center justify-center'"
                            >
                              {{ seg }}
                            </button>
                          }
                        </div>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500 px-1 mt-0.5 leading-normal">
                          @if (activeVideoSegment() === 'Reels') {
                            Reels: Tailored frame for portrait video formats.
                          } @else if (activeVideoSegment() === 'Shorts') {
                            Shorts: Configured frame structure for video files spanning 1 to 5 minutes duration.
                          } @else {
                            Movies: Expanded widescreen video structure supporting multi-hour content.
                          }
                        </p>
                      </div>

                      <!-- Actual Video File Upload to Firebase Storage -->
                      <div class="space-y-1.5 relative">
                        <span class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Video Core Source File</span>
                        <div class="relative w-full rounded-2xl border-2 border-dashed border-black/10 dark:border-white/10 hover:border-ios-blue flex flex-col items-center justify-center p-6 bg-black/5 dark:bg-white/5 transition-colors cursor-pointer select-none"
                             [class.pointer-events-none]="isEditingUnified() && !isUserAuthorizedToEditMedia()"
                             (click)="videoFileInput.click()" (keydown.enter)="videoFileInput.click()" tabindex="0">
                          
                          @if (isEditingUnified() && !isUserAuthorizedToEditMedia()) {
                            <div class="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center p-4 text-center z-20 select-none rounded-[14px]">
                              <mat-icon class="text-white/60 mb-1.5 !text-xl h-5 w-5">lock</mat-icon>
                              <span class="text-[10px] uppercase tracking-widest font-extrabold text-white">Source Video Unalterable</span>
                              <span class="text-[8px] text-white/40 mt-0.5">Media resource locked for standard creators.</span>
                            </div>
                          }

                          @if (selectedVideoFile()) {
                            <mat-icon class="text-emerald-500 !text-3xl h-8 w-8 mb-2">videocam</mat-icon>
                            <span class="text-xs font-bold text-gray-900 dark:text-white text-center line-clamp-1 max-w-[280px]">
                              {{ selectedVideoFile()?.file?.name }}
                            </span>
                            <span class="text-[10px] text-gray-400 mt-1 font-mono">
                              {{ ((selectedVideoFile()?.file?.size || 0) / (1024 * 1024)) || 0 | number:'1.1-2' }} MB
                            </span>
                          } @else if (isEditingUnified() && editingAsset()?.videoUrl) {
                            <mat-icon class="text-ios-blue !text-3xl h-8 w-8 mb-2 font-black">cloud_done</mat-icon>
                            <span class="text-xs font-bold text-gray-900 dark:text-white text-center line-clamp-1 max-w-[280px]">
                              Existing Video Resource Associated
                            </span>
                            <span class="text-[10px] text-gray-400 mt-1 font-mono font-bold uppercase tracking-widest text-emerald-500">
                              Active / Saved
                            </span>
                          } @else {
                            <mat-icon class="text-gray-400 dark:text-gray-505 !text-3xl h-8 w-8 mb-2">video_library</mat-icon>
                            <span class="text-xs font-bold text-gray-500 dark:text-gray-450">Upload Local Video File *</span>
                            <span class="text-[9px] text-gray-400 mt-0.5 text-center max-w-[240px]">
                              Select MP4/MOV/WEBM files. Specs detected natively.
                            </span>
                          }
                        </div>
                        <input #videoFileInput type="file" accept="video/*" class="hidden" (change)="onUnifiedVideoFileSelected($event)" />
                      </div>

                      <!-- Video Quality Read Only Specifications Tag -->
                      <div class="space-y-1.5">
                        <span class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Dynamic Video Quality Specs</span>
                        <div class="p-3.5 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl flex items-center justify-between">
                          <div class="flex items-center gap-1.5">
                            <span class="px-2.5 py-1 rounded-lg bg-ios-blue/15 text-ios-blue text-[11px] font-bold font-mono">
                              {{ detectedVideoQuality() || 'PENDING' }}
                            </span>
                            @if (detectedVideoDuration() > 0) {
                              <span class="px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-500 text-[11px] font-bold font-mono">
                                {{ detectedVideoDuration() | number:'1.0-0' }}s
                              </span>
                            }
                            <!-- Automatically Extracted File Format Badge -->
                            <span class="px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-500 text-[11px] font-bold font-mono uppercase">
                              {{ detectedVideoFormat() }}
                            </span>
                          </div>
                          <span class="text-[9px] text-gray-400 font-medium">Automatic extraction</span>
                        </div>
                      </div>

                      <!-- Video License Type Assignment selector -->
                      <div class="space-y-1.5">
                        <span class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">License Assignment</span>
                        <div class="flex items-center gap-1 p-1 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl select-none">
                          <button type="button" 
                                  (click)="unifiedForm.get('license')?.setValue('Standard')"
                                  [class]="unifiedForm.get('license')?.value === 'Standard' ? 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold bg-ios-blue text-white shadow-ios flex items-center justify-center' : 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold text-gray-450 hover:text-gray-950 dark:hover:text-white transition-all flex items-center justify-center'"
                          >
                            Standard
                          </button>
                          <button type="button" 
                                  (click)="unifiedForm.get('license')?.setValue('Open Source')"
                                  [class]="unifiedForm.get('license')?.value === 'Open Source' ? 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold bg-ios-blue text-white shadow-ios flex items-center justify-center' : 'flex-1 py-1.5 px-3 rounded-xl text-center text-xs font-bold text-gray-450 hover:text-gray-950 dark:hover:text-white transition-all flex items-center justify-center'"
                          >
                            Open Source
                          </button>
                        </div>
                      </div>
                    </div>
                  }
                }

                <!-- Status & Workflow Optionals -->
                <div class="grid grid-cols-2 gap-4 pt-2">
                  <div class="space-y-1.5">
                    <label for="uf-status" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Post Visibility Status</label>
                    <div class="ios-input-container">
                      <select id="uf-status" formControlName="status" class="ios-input bg-transparent w-full outline-none">
                        <option value="published">Published</option>
                        <option value="unpublished">Unpublished</option>
                      </select>
                    </div>
                  </div>
                  <div class="space-y-1.5">
                    <label for="uf-verification" class="text-[10px] font-extrabold uppercase tracking-wider opacity-40 px-1">Verification Status</label>
                    <div class="ios-input-container">
                      <select id="uf-verification" formControlName="verificationStatus" [disabled]="!(canManageOrganization() || isOwner() || authService.isMasterAdmin())" class="ios-input bg-transparent w-full outline-none disabled:opacity-40">
                        <option value="review">Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Messages -->
                @if (unifiedErrorMessage()) {
                  <p class="text-xs text-red-500 font-bold px-1 bg-red-100/50 dark:bg-red-900/10 p-3 rounded-2xl border border-red-500/20">
                    {{ unifiedErrorMessage() }}
                  </p>
                }
                @if (unifiedSuccessMessage()) {
                  <p class="text-xs text-emerald-500 font-bold px-1 bg-emerald-100/50 dark:bg-emerald-950/10 p-3 rounded-2xl border border-emerald-500/20">
                    {{ unifiedSuccessMessage() }}
                  </p>
                }
              </div>

              <!-- Footer Actions -->
              <div class="p-5 border-t border-black/5 dark:border-white/5 flex items-center justify-between gap-4 bg-gray-50/30 dark:bg-zinc-805/20 sticky bottom-0">
                <button 
                  type="button"
                  (click)="closeUnifiedModal()" 
                  class="flex-1 h-12 rounded-xl font-bold hover:bg-black/5 dark:hover:bg-white/5 transition-all text-xs text-ios-title opacity-60 animate-ios-fade"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  [disabled]="isSubmittingUnified() || (!isEditingUnified() && ((activeModalType() !== 'video' && selectedImagesList().length === 0) || (activeModalType() === 'video' && !selectedVideoFile())))"
                  class="flex-[2] h-12 rounded-xl font-bold bg-ios-blue text-white shadow-ios shadow-ios-blue/20 hover:scale-[1.02] active:scale-98 transition-all disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 cursor-pointer animate-ios-fade"
                >
                  @if (isSubmittingUnified()) {
                    <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{{ isEditingUnified() ? 'Publishing...' : 'Uploading...' }}</span>
                  } @else {
                    <mat-icon class="!text-lg">{{ isEditingUnified() ? 'save' : 'send' }}</mat-icon>
                    <span>Publish</span>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      }

      <!-- Beautiful, Centered Unified Item Deletion Confirmation Modal -->
      @if (isDeleteModalOpen()) {
        <div id="unified-delete-modal" class="fixed inset-0 z-[150] flex items-center justify-center p-4 animate-ios-fade">
          <!-- Backdrop -->
          <div 
            id="unified-delete-modal-backdrop"
            class="absolute inset-0 bg-black/60 backdrop-blur-md cursor-pointer"
            (click)="!isDeletingProgress() && closeDeleteModal()"
            (keydown.enter)="!isDeletingProgress() && closeDeleteModal()"
            tabindex="0"
          ></div>
          
          <!-- Modal Card -->
          <div id="unified-delete-modal-card" class="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[32px] overflow-hidden shadow-2xl relative z-10 border border-black/5 dark:border-white/5 p-6 md:p-8 animate-ios-slide flex flex-col text-center">
            <div id="delete-icon-container" class="w-16 h-16 rounded-full bg-ios-red/10 text-ios-red flex items-center justify-center mx-auto mb-4 animate-pulse">
              <mat-icon id="delete-alert-icon" class="!text-3xl h-8 w-8">delete_forever</mat-icon>
            </div>
            
            <h4 id="delete-modal-title" class="text-xl font-bold tracking-tight text-ios-title mb-2">Delete Confirmation</h4>
            <p id="delete-modal-message" class="text-[15px] font-medium opacity-60 px-2 leading-normal mb-8 text-center text-gray-500 text-sm">
              Are you sure you want to delete this {{ deleteItemType() === 'images' ? 'photo' : 'item' }}? This action cannot be undone.
            </p>
            
            <div id="delete-modal-buttons" class="flex flex-col gap-3">
              <button 
                id="delete-button-confirm"
                (click)="confirmDelete()"
                [disabled]="isDeletingProgress()"
                class="w-full h-12 rounded-xl bg-ios-red hover:bg-red-650 text-white font-bold text-[15px] shadow-lg shadow-ios-red/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                @if (isDeletingProgress()) {
                  <div id="delete-btn-spinner" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                } @else {
                  <span>Delete</span>
                }
              </button>
              
              <button 
                id="delete-button-cancel"
                (click)="closeDeleteModal()"
                [disabled]="isDeletingProgress()"
                class="w-full h-12 rounded-xl font-bold hover:bg-black/5 dark:hover:bg-white/5 transition-all text-[15px] text-gray-500 dark:text-gray-400 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Beautiful, Interactive iOS-style Elegant Toast Alert -->
      @if (toastMessage()) {
        <div id="ios-toast-container" class="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] animate-ios-slide flex items-center gap-2.5 px-4.5 py-3 rounded-2xl border bg-black/80 dark:bg-white/95 backdrop-blur-md shadow-2xl border-white/10 dark:border-black/5 text-white dark:text-black font-semibold text-xs tracking-wide">
          <mat-icon id="toast-icon" [class]="toastType() === 'success' ? 'text-emerald-400 dark:text-emerald-500' : 'text-rose-400 dark:text-rose-500'">
            {{ toastType() === 'success' ? 'check_circle' : 'error' }}
          </mat-icon>
          <span id="toast-text" class="whitespace-nowrap">{{ toastMessage() }}</span>
        </div>
      }

      <!-- Liquid Glass Add Image Modal -->
      <app-add-image
        [isOpen]="isAddImageComponentOpen()"
        (closed)="isAddImageComponentOpen.set(false)"
        (imageUploaded)="onImageComponentUploaded($event)"
      ></app-add-image>

      <!-- Change Profile Photo Modal -->
      <app-change-profile-photo
        [isOpen]="isChangeProfilePhotoOpen()"
        [currentProfile]="profile()"
        (closed)="isChangeProfilePhotoOpen.set(false)"
        (photoUpdated)="onPhotoUpdated()"
      ></app-change-profile-photo>

      <!-- Kicgle ID Card Pop-up Modal (Strictly for authenticated current logged-in user) -->
      @if (isCurrentLoggedInUser()) {
        <app-kicgle-id-card
          [isOpen]="isIdCardOpen()"
          [profile]="profile()"
          (closed)="isIdCardOpen.set(false)"
        ></app-kicgle-id-card>
      }

      <!-- Material Design Liquid Glass Non-Contact Chat Modal -->
      @if (isNonContactModalOpen()) {
        <div 
          class="fixed inset-0 z-[1200] flex items-center justify-center p-4 sm:p-6 animate-ios-fade"
          role="dialog"
          aria-modal="true"
          aria-labelledby="non-contact-modal-title"
        >
          <!-- Backdrop with liquid blur effect -->
          <div 
            class="absolute inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-xl transition-opacity cursor-pointer"
            (click)="closeNonContactChatModal()"
            tabindex="0"
            (keydown.enter)="closeNonContactChatModal()"
          ></div>

          <!-- Liquid Glass Material Dialog Surface -->
          <div class="relative w-full max-w-sm sm:max-w-md rounded-[36px] bg-white/85 dark:bg-zinc-900/85 backdrop-blur-2xl border border-white/60 dark:border-white/10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.35)] dark:shadow-[0_32px_64px_-16px_rgba(0,0,0,0.7)] p-6 sm:p-8 overflow-hidden z-10 animate-ios-slide">
            
            <!-- Ambient Liquid Glow Orbs inside the card -->
            <div class="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br from-ios-blue/20 to-teal-400/20 blur-3xl pointer-events-none"></div>
            <div class="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-gradient-to-tr from-amber-400/15 to-purple-500/15 blur-3xl pointer-events-none"></div>

            <!-- Top Action Row with Close Button -->
            <div class="flex items-center justify-between mb-5 relative z-10">
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                <mat-icon class="!text-[14px]">lock</mat-icon>
                <span>Chat Locked</span>
              </span>
              
              <!-- Close Button to dismiss -->
              <button
                type="button"
                (click)="closeNonContactChatModal()"
                class="w-9 h-9 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-zinc-600 dark:text-zinc-300 flex items-center justify-center transition-all active:scale-95 cursor-pointer"
                title="Close"
                aria-label="Close"
              >
                <mat-icon class="!text-[18px]">close</mat-icon>
              </button>
            </div>

            <!-- Hero Icon Area -->
            <div class="relative z-10 text-center mb-5">
              <div class="relative w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <div class="absolute inset-0 rounded-3xl bg-gradient-to-tr from-ios-blue/25 to-teal-400/25 blur-xl"></div>
                <div class="relative w-20 h-20 rounded-3xl bg-gradient-to-b from-white/70 to-white/30 dark:from-white/10 dark:to-white/5 border border-white/80 dark:border-white/15 shadow-ios-md flex items-center justify-center">
                  <mat-icon class="!text-[40px] text-ios-blue">chat_bubble_outline</mat-icon>
                  <div class="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-md border-2 border-white dark:border-zinc-900">
                    <mat-icon class="!text-[14px] font-black">lock</mat-icon>
                  </div>
                </div>
              </div>

              <h3 id="non-contact-modal-title" class="text-xl font-black text-zinc-900 dark:text-white tracking-tight">
                Add to Contacts to Chat
              </h3>
              
              <!-- Required Instruction -->
              <p class="text-xs sm:text-[13px] text-zinc-600 dark:text-zinc-300 mt-2 leading-relaxed px-1 font-medium">
                To start chatting, please add <strong class="text-zinc-900 dark:text-white font-bold">{{ profile()?.displayName || 'this person' }}'s mobile number</strong> to your contacts directory.
              </p>
            </div>

            <!-- Person Card UI (Matching contacts list: photo, name, username; no mobile number) -->
            <div class="relative z-10 w-full mb-6 text-left">
              <div class="glass-card !rounded-2xl border border-black/5 dark:border-white/10 flex flex-col shadow-xs bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl relative overflow-hidden">
                
                <!-- Top Row: Contact Info -->
                <div class="p-3.5 flex items-center justify-between gap-3 relative">
                  
                  <!-- Contact Avatar & Details -->
                  <div class="flex items-center gap-3.5 min-w-0 flex-1">
                    <div class="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-tr from-ios-blue/15 to-ios-purple/15 dark:from-ios-blue/25 dark:to-ios-purple/25 shrink-0 border border-white/20 dark:border-white/10 relative shadow-xs">
                      @if (profile()?.photoURL) {
                        <img [src]="profile()?.photoURL" [alt]="profile()?.displayName || 'User'" class="w-full h-full object-cover" referrerpolicy="no-referrer" />
                      } @else {
                        <div class="w-full h-full flex items-center justify-center text-ios-blue font-black text-base">
                          {{ (profile()?.displayName || 'U').charAt(0).toUpperCase() }}
                        </div>
                      }
                      @if (profile()?.isOnline) {
                        <div class="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-zinc-900 z-10"></div>
                      }
                    </div>

                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1.5 min-w-0">
                        <h4 class="text-sm font-bold text-zinc-900 dark:text-white truncate leading-tight">
                          {{ profile()?.displayName || 'User' }}
                        </h4>
                      </div>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5 font-medium">
                        {{ profile()?.username ? '@' + profile()?.username : (profile()?.email || '@user') }}
                      </p>
                    </div>
                  </div>

                </div>

                <!-- Bottom Layer Action Row: Single Line with Voice, Video, and Chat (matching contacts list) -->
                <div class="px-3 py-2 bg-black/5 dark:bg-white/5 border-t border-black/5 dark:border-white/5 grid grid-cols-3 gap-1 text-center select-none">
                  <!-- 1. Voice (Inactive) -->
                  <button
                    type="button"
                    disabled
                    class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl text-zinc-400 dark:text-zinc-500 opacity-50 cursor-not-allowed text-[11px] font-medium"
                    title="Voice feature inactive"
                  >
                    <mat-icon class="!text-[16px]">call</mat-icon>
                    <span>Voice</span>
                  </button>

                  <!-- 2. Video (Inactive) -->
                  <button
                    type="button"
                    disabled
                    class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl text-zinc-400 dark:text-zinc-500 opacity-50 cursor-not-allowed text-[11px] font-medium"
                    title="Video feature inactive"
                  >
                    <mat-icon class="!text-[16px]">videocam</mat-icon>
                    <span>Video</span>
                  </button>

                  <!-- 3. Chat (Locked until in contacts) -->
                  <button
                    type="button"
                    disabled
                    class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl bg-ios-blue/10 text-ios-blue font-bold text-[11px] opacity-60 cursor-not-allowed shadow-xs"
                    title="Chat locked"
                  >
                    <mat-icon class="!text-[16px]">chat</mat-icon>
                    <span>Chat</span>
                  </button>
                </div>

              </div>
            </div>

            <!-- Modal Action Buttons: Contact icon navigation to contact page & Dismiss button -->
            <div class="relative z-10 flex flex-col gap-2.5">
              <button
                type="button"
                (click)="navigateToContacts()"
                class="w-full py-3.5 px-6 rounded-2xl bg-ios-blue hover:bg-blue-600 text-white font-extrabold text-xs sm:text-sm shadow-lg shadow-ios-blue/25 hover:shadow-ios-blue/40 hover:scale-[1.01] active:scale-[0.98] transition-all flex items-center justify-center gap-2.5 cursor-pointer"
                title="Go to Contacts"
              >
                <mat-icon class="!text-[18px]">contacts</mat-icon>
                <span>Go to Contacts</span>
              </button>

              <button
                type="button"
                (click)="closeNonContactChatModal()"
                class="w-full py-2.5 rounded-2xl text-xs font-bold text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer"
              >
                Dismiss
              </button>
            </div>

          </div>
        </div>
      }
    </div>
  `,
})
export class ProfileView implements OnDestroy {
  AccountStatus = AccountStatus;
  isIdCardOpen = signal(false);
  isChangeProfilePhotoOpen = signal(false);
  isNonContactModalOpen = signal(false);
  isCopiedPhone = signal(false);
  userContactUids = signal<Set<string>>(new Set<string>());
  private unsubscribeContactsList: (() => void) | null = null;

  isInContacts = computed(() => {
    const p = this.profile();
    const currentUser = this.authService.user();
    if (!p?.uid || !currentUser?.uid) return false;
    return this.userContactUids().has(p.uid);
  });

  openNonContactChatModal() {
    this.isNonContactModalOpen.set(true);
  }

  closeNonContactChatModal() {
    this.isNonContactModalOpen.set(false);
  }

  navigateToContacts() {
    this.closeNonContactChatModal();
    this.router.navigate(['/contacts']);
  }

  copyPhoneNumber(phone: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(phone).then(() => {
        this.isCopiedPhone.set(true);
        setTimeout(() => this.isCopiedPhone.set(false), 2000);
      }).catch(err => {
        console.error('Failed to copy phone number', err);
      });
    }
  }

  onPhotoUpdated() {
    this.isChangeProfilePhotoOpen.set(false);
  }
  public authService = inject(AuthService);
  public progressService = inject(ProgressService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private chatService = inject(ChatService);
  private followService = inject(FollowService);
  private orgDataService = inject(OrganizationDataService);
  private unsubscribeChatsList: (() => void) | null = null;

  profile = signal<AuthProfile | null>(null);

  isOwner = computed(() => {
    const currentUser = this.authService.user();
    if (this.authService.isMasterAdmin()) return true;
    const profile = this.profile();
    return !!currentUser && !!profile && currentUser.uid === profile.uid;
  });

  isCurrentLoggedInUser = computed(() => {
    const currentUser = this.authService.user();
    const profile = this.profile();
    return !!currentUser && !!profile && currentUser.uid === profile.uid;
  });
  
  // RBAC signals
  rawMembersData$ = toObservable(this.profile).pipe(
    map(p => p?.uid),
    distinctUntilChanged(),
    switchMap(orgId => {
      const profile = this.profile();
      if (!orgId || (profile?.accountType !== 'organization' && profile?.accountType !== 'country')) return of([]);
      
      const rolesRef = collection(db, 'accounts', orgId, 'roles');
      return new Observable<any[]>(subscriber => {
        return onSnapshot(rolesRef, snap => {
          const docs = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
          console.log('1. RAW ROLES FROM SERVER:', docs);
          subscriber.next(docs);
        });
      }).pipe(
        switchMap(roleDocs => {
          if (roleDocs.length === 0) return of([]);
          const userObservables = roleDocs.map(roleSettings => {
            const userUid = roleSettings.uid || roleSettings._id;
            console.log('2. FETCHING USER FOR UID:', userUid, 'from role settings', roleSettings);
            if (!userUid) return of(roleSettings);
            const userRef = doc(db, 'accounts', userUid);
            return new Observable<any>(userSub => {
              return onSnapshot(userRef, uSnap => {
                const uData = uSnap.data() || {};
                userSub.next({
                  ...roleSettings,
                  roles: Array.isArray(roleSettings.roles) ? roleSettings.roles : [],
                  start: roleSettings.startDate || roleSettings.start,
                  end: roleSettings.endDate || roleSettings.end,
                  displayName: uData['displayName'] || uData['name'],
                  username: uData['username'],
                  photoURL: uData['photoURL']
                });
              });
            });
          });
          return combineLatest(userObservables);
        })
      );
    })
  );

  allMembers = toSignal(this.rawMembersData$, { initialValue: [] as any[] });

  private readonly SUPER_USER_ROLES = ['admin', 'managing trustee', 'managing director', 'director', 'trustee'];

  authorizedPersons = computed(() => {
    const list = this.allMembers().filter((m: any) => {
      if (!Array.isArray(m.roles)) return false;
      return m.roles.some((userRole: string) => {
        if (!userRole) return false;
        return this.SUPER_USER_ROLES.includes(userRole.toLowerCase().trim());
      });
    });
    console.log('3. FINAL AUTH PERSONS ARRAY:', list);
    return list;
  });

  regularMembers = computed(() => {
    const list = this.allMembers().filter((m: any) => {
      if (!Array.isArray(m.roles)) return true;
      const isAuthorized = m.roles.some((userRole: string) => {
        if (!userRole) return false;
        return this.SUPER_USER_ROLES.includes(userRole.toLowerCase().trim());
      });
      return !isAuthorized;
    });
    console.log('4. FINAL MEMBERS ARRAY:', list);
    return list;
  });

  canManageOrganization = computed(() => {
    const p = this.profile();
    if (!p || (p.accountType !== 'organization' && p.accountType !== 'country')) return false;
    const currentUser = this.authService.user();
    if (!currentUser) return false;
    // Owner or Global Admin
    if (currentUser.uid === p.uid || this.authService.isAdmin()) return true;
    
    // Check if current user has any super-user role in this organization/country and is not revoked
    return this.authorizedPersons().some((m: any) => {
      if (m.uid !== currentUser.uid) return false;
      const isActive = !m.end || new Date(m.end).getTime() > Date.now();
      return isActive;
    });
  });

  hasAdminRights = computed(() => this.canManageOrganization());

  isOrganizationViewer = computed(() => {
    const p = this.profile();
    return !!p && (p.accountType === 'organization' || p.accountType === 'country');
  });

  isCountryViewer = computed(() => {
    const p = this.profile();
    return !!p && p.accountType === 'country';
  });

  affiliations$ = toObservable(this.profile).pipe(
    map(p => p?.uid),
    distinctUntilChanged(),
    switchMap(viewingUid => {
      const profile = this.profile();
      if (!viewingUid || !profile || profile.accountType === 'organization' || profile.accountType === 'country') return of([]);
      
      const q = query(collectionGroup(db, 'roles'), where('uid', '==', viewingUid));
      return from(getDocs(q)).pipe(
        switchMap(snap => {
          if (snap.empty) return of([]);
          const orgTasks = snap.docs.map(roleDoc => {
            const orgId = roleDoc.ref.parent.parent?.id;
            if (!orgId) return of(null);
            const roleData = roleDoc.data();
            return from(getDoc(doc(db, 'accounts', orgId))).pipe(
              map(orgSnap => {
                if (!orgSnap.exists()) return null;
                const orgData = orgSnap.data() as AuthProfile;
                return {
                  orgId,
                  orgName: orgData.displayName,
                  orgPhotoURL: orgData.photoURL,
                  orgUsername: orgData.username,
                  roles: roleData['roles'] || roleData['role'] || [],
                  startDate: roleData['startDate'],
                  endDate: roleData['endDate']
                };
              })
            );
          });
          return combineLatest(orgTasks).pipe(
             map(results => results.filter(r => !!r))
          );
        })
      );
    })
  );

  affiliations = toSignal(this.affiliations$, { initialValue: [] as any[] });

  // Member Management signals
  addMemberForm: FormGroup = new FormGroup({
    searchQuery: new FormControl(''),
    roleTitle: new FormControl('', Validators.required),
    startDate: new FormControl(new Date().toISOString().split('T')[0], Validators.required),
    endDate: new FormControl(''),
    startReason: new FormControl('', Validators.required)
  });

  revokingUserId = signal<string | null>(null);
  revokeReasonControl = new FormControl('', Validators.required);

  initRevokeMember(member: any) {
    this.revokingUserId.set(member.uid);
    this.revokeReasonControl.reset('');
  }

  cancelRevoke() {
    this.revokingUserId.set(null);
    this.revokeReasonControl.reset('');
  }

  confirmRevoke(uid: string) {
    if (this.revokeReasonControl.invalid) return;
    this.removeMember(uid, this.revokeReasonControl.value?.trim() || '');
  }

  searchResults = signal<AuthProfile[]>([]);
  isSearchingUsers = signal(false);
  selectedCandidate = signal<any | null>(null);
  isSavingMember = signal(false);
  isAddMemberModalOpen = signal<boolean>(false);

  hasManagementPermission = false;
  followersCount = signal<number>(0);
  followingCount = signal<number>(0);
  isFollowing = signal<boolean>(false);

  formatTitleCase(str: string | null | undefined): string {
    if (!str) return 'User';
    return str
      .trim()
      .split(/\s+/)
      .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '')
      .join(' ');
  }

  maskSensitive(val: any): string {
    if (!val) return '••••••••';
    const str = String(val);
    if (str.includes('@')) {
      const [user, domain] = str.split('@');
      return `${user.charAt(0)}***@${domain}`;
    }
    if (str.length > 4) {
      return `***${str.slice(-4)}`;
    }
    return '••••••••';
  }

  isFollowLoading = signal<boolean>(false);
  isOrgAdmin = signal<boolean>(false);

  canPublishNews = signal<boolean>(false);
  canDeleteNews = signal<boolean>(false);
  canEditProfile = signal<boolean>(false);
  canModifyOrgDetails = signal<boolean>(false);

  userHasPermission(action: string): boolean {
    const currentUser = this.authService.user();
    if (!currentUser) return false;
    const orgId = this.profile()?.uid;
    if (!orgId) return false;

    // 1. If hasAdminRights (Owner, Global Admin, or Org Admin in roles subcollection)
    if (this.hasAdminRights()) {
      return true;
    }

    // 2. Individual permission signals
    if (action === 'edit' || action === 'delete' || action === 'publish') {
      return this.canPublishNews() || this.canDeleteNews() || this.canEditProfile() || this.canModifyOrgDetails();
    }
    return false;
  }

  selectedTab = signal<string>("About");
  assets = signal<any[]>([]);
  isLoadingAssets = signal<boolean>(false);
  // WhatsApp Business Bot State
  isBotModalOpen = signal<boolean>(false);

  // Dedicated user uploaded images signals and state
  userImages = signal<UserImage[]>([]);
  isLoadingUserImages = signal<boolean>(false);

  isUploadingImageModalOpen = signal<boolean>(false);
  selectedImageFile = signal<File | null>(null);
  imageCropPosition = signal<{x: number, y: number, scale: number}>({x: 0, y: 0, scale: 1});
  imagePreviewUrl = signal<string | null>(null);
  isUploadingImage = signal<boolean>(false);

  imageToUploadMetadata = signal<{
    status: 'published' | 'unpublished';
    license: 'standard' | 'commercial' | 'open-source' | string;
  }>({
    status: 'published',
    license: 'standard'
  });

  // Deletion Confirmation Modal State
  isDeletingImageModalOpen = signal<boolean>(false);
  imageToDelete = signal<UserImage | null>(null);
  isDeletingImageProgress = signal<boolean>(false);

  // Metadata Edit Modal State
  isEditingImageModalOpen = signal<boolean>(false);
  imageToEdit = signal<UserImage | null>(null);
  isSavingImageMetadataProgress = signal<boolean>(false);
  imageEditMetadata = signal<{
    status: 'published' | 'unpublished';
    verificationStatus?: string;
    license: 'standard' | 'commercial' | 'open-source' | string;
  }>({
    status: 'published',
    verificationStatus: 'review',
    license: 'standard'
  });

  chats = signal<Chat[]>([]);
  isLoadingChats = signal<boolean>(false);

  isAddingAsset = signal<boolean>(false);
  isAddingNews = signal<boolean>(false);
  
  uploadedAssetImages = signal<string[]>([]);
  assetImageCropPositions = signal<{x: number, y: number, scale: number}[]>([]);
  activeAssetImageIndex = signal<number>(0);

  // Add Image Liquid Glass Modal State
  isAddImageComponentOpen = signal<boolean>(false);

  // New Unified Modal State & Signals (Instagram Style - CRITICAL)
  isUnifiedModalOpen = signal<boolean>(false);
  activeModalType = signal<'image' | 'news' | 'job' | 'product' | 'video'>('image');
  selectedImagesList = signal<{ previewUrl: string, file: File, cropPos: { x: number, y: number, scale: number } }[]>([]);
  activeImageIndex = signal<number>(0);
  isSubmittingUnified = signal<boolean>(false);
  unifiedErrorMessage = signal<string | null>(null);
  unifiedSuccessMessage = signal<string | null>(null);

  // New Video Upload and Quality Core Signals
  selectedVideoFile = signal<{ file: File, previewUrl: string } | null>(null);
  detectedVideoQuality = signal<string>("1080p");
  detectedVideoDuration = signal<number>(0);
  activeVideoSegment = signal<'Reels' | 'Shorts' | 'Movies'>('Reels');
  readonly segmentTypes: ('Reels' | 'Shorts' | 'Movies')[] = ['Reels', 'Shorts', 'Movies'];
  detectedVideoFormat = signal<string>(".mp4");
  isEditingUnified = signal<boolean>(false);
  editingDocId = signal<string | null>(null);
  editingAsset = signal<any>(null);

  isUserAuthorizedToEditMedia(): boolean {
    return this.canManageOrganization() || this.isOwner() || this.authService.isMasterAdmin();
  }

  unifiedForm = new FormGroup({
    id: new FormControl(""),
    title: new FormControl("", [Validators.required, Validators.minLength(2)]),
    description: new FormControl("", [Validators.required, Validators.minLength(5)]),
    status: new FormControl("published", [Validators.required]),
    verificationStatus: new FormControl("review", [Validators.required]),
    license: new FormControl("standard"),
    format: new FormControl("jpeg"),
    newsUrl: new FormControl(""),
    source: new FormControl(""),
    isBreaking: new FormControl(false),
    category: new FormControl("General"),
    company: new FormControl(""),
    location: new FormControl(""),
    jobType: new FormControl("full-time"),
    experienceLevel: new FormControl("entry"),
    isRemote: new FormControl(false),
    currency: new FormControl("INR"),
    price: new FormControl<number | null>(null),
    brand: new FormControl(""),
    stockQuantity: new FormControl<number | null>(null),
    productUrl: new FormControl(""),
    resolution: new FormControl("1080p"),
    director: new FormControl(""),
    videoUrl: new FormControl(""),
    jobCategory: new FormControl<'Government' | 'Private'>("Private"),
  });

  onUnifiedImagePanEnd(pos: { x: number, y: number, scale: number }, index: number) {
    this.selectedImagesList.update((list) => {
      const newList = [...list];
      if (newList[index]) {
        newList[index] = {
          ...newList[index],
          cropPos: pos
        };
      }
      return newList;
    });
  }

  onSingleImagePanEnd(pos: { x: number, y: number, scale: number }) {
    // For single image modal used in Images Tab (selectedImageFile)
    this.imageCropPosition.set(pos);
  }

  triggerAddAssetFile(inputRef: HTMLInputElement) {
    inputRef.click();
  }

  openUnifiedModal(type: 'image' | 'news' | 'job' | 'product' | 'video') {
    this.isEditingUnified.set(false);
    this.editingDocId.set(null);
    this.editingAsset.set(null);
    this.activeModalType.set(type);
    this.selectedImagesList.set([]);
    this.activeImageIndex.set(0);
    this.unifiedErrorMessage.set(null);
    this.unifiedSuccessMessage.set(null);
    
    // Reset core video signals
    this.selectedVideoFile.set(null);
    this.detectedVideoQuality.set("1080p");
    this.detectedVideoDuration.set(0);
    this.activeVideoSegment.set('Reels');
    this.detectedVideoFormat.set(".mp4");

    // Reset Form
    this.unifiedForm.reset({
      id: "",
      title: "",
      description: "",
      status: "published",
      verificationStatus: "review",
      license: "standard",
      format: "jpeg",
      newsUrl: "",
      source: "",
      isBreaking: false,
      category: "General",
      company: "",
      location: "",
      jobType: "full-time",
      experienceLevel: "entry",
      isRemote: false,
      currency: "INR",
      price: null,
      brand: "",
      stockQuantity: null,
      productUrl: "",
      resolution: "1080p",
      director: "",
      videoUrl: "",
      jobCategory: "Private",
    });

    if (type === 'image') {
      this.unifiedForm.get('title')?.clearValidators();
      this.unifiedForm.get('description')?.clearValidators();
    } else {
      this.unifiedForm.get('title')?.setValidators([Validators.required, Validators.minLength(2)]);
      this.unifiedForm.get('description')?.setValidators([Validators.required, Validators.minLength(5)]);
    }
    this.unifiedForm.get('title')?.updateValueAndValidity();
    this.unifiedForm.get('description')?.updateValueAndValidity();

    this.isUnifiedModalOpen.set(true);
  }

  openUnifiedModalForEdit(type: 'image' | 'news' | 'job' | 'product' | 'video', asset: any) {
    this.isEditingUnified.set(true);
    this.editingDocId.set(asset.id || null);
    this.editingAsset.set(asset);
    this.activeModalType.set(type);
    
    this.unifiedErrorMessage.set(null);
    this.unifiedSuccessMessage.set(null);

    // Populate form fields
    this.unifiedForm.reset({
      id: asset.id || "",
      title: type === 'product' ? (asset.productName || "") : (type === 'job' ? (asset.jobTitle || "") : (asset.title || "")),
      description: type === 'news' ? (asset.content || asset.description || "") : (asset.description || ""),
      status: asset.status || "published",
      verificationStatus: asset.verificationStatus || "review",
      license: asset.license || "Standard",
      format: asset.format || "jpeg",
      newsUrl: asset.newsUrl || "",
      source: asset.source || "",
      isBreaking: asset.isBreaking || false,
      category: asset.category || "General",
      company: asset.company || "",
      location: asset.location || "",
      jobType: asset.jobType || "full-time",
      experienceLevel: asset.experienceLevel || "entry",
      isRemote: asset.isRemote || false,
      currency: asset.currency || "INR",
      price: asset.price || null,
      brand: asset.brand || "",
      stockQuantity: asset.stockQuantity || asset.stock || null,
      productUrl: asset.productUrl || "",
      resolution: asset.resolution || "1080p",
      director: asset.director || "",
      videoUrl: asset.videoUrl || "",
      jobCategory: asset.jobCategory || "Private",
    });

    if (type === 'image') {
      this.unifiedForm.get('title')?.clearValidators();
      this.unifiedForm.get('description')?.clearValidators();
      
      this.selectedImagesList.set([
        { previewUrl: asset.imageUrl || "", file: null as any, cropPos: { x: 0, y: 0, scale: 1 } }
      ]);
      this.activeImageIndex.set(0);
    } else if (type === 'video') {
      this.unifiedForm.get('title')?.setValidators([Validators.required, Validators.minLength(2)]);
      this.unifiedForm.get('description')?.setValidators([Validators.required, Validators.minLength(5)]);
      
      this.detectedVideoQuality.set(asset.resolution || "1080p");
      this.detectedVideoDuration.set(asset.duration || 0);
      const matchedCategory = asset.videoCategory === 'Videos' ? 'Movies' : (asset.videoCategory || 'Reels');
      this.activeVideoSegment.set(matchedCategory as 'Reels' | 'Shorts' | 'Movies');
      this.detectedVideoFormat.set(asset.format || ".mp4");

      if (asset.posterUrl || asset.thumbnailUrl) {
        this.selectedImagesList.set([
          { previewUrl: asset.posterUrl || asset.thumbnailUrl || "", file: null as any, cropPos: { x: 0, y: 0, scale: 1 } }
        ]);
        this.activeImageIndex.set(0);
      } else {
        this.selectedImagesList.set([]);
      }
    } else {
      this.unifiedForm.get('title')?.setValidators([Validators.required, Validators.minLength(2)]);
      this.unifiedForm.get('description')?.setValidators([Validators.required, Validators.minLength(5)]);
      
      const images: string[] = asset.galleryImages || asset.images || [];
      const singleImg = asset.imageUrl || asset.bannerImage || "";
      let resolvedList: string[] = [];
      if (images && images.length > 0) {
        resolvedList = images;
      } else if (singleImg) {
        resolvedList = [singleImg];
      }
      
      this.selectedImagesList.set(
        resolvedList.map(url => ({ previewUrl: url, file: null as any, cropPos: { x: 0, y: 0, scale: 1 } }))
      );
      this.activeImageIndex.set(0);
    }

    this.unifiedForm.get('title')?.updateValueAndValidity();
    this.unifiedForm.get('description')?.updateValueAndValidity();

    this.isUnifiedModalOpen.set(true);
  }

  closeUnifiedModal() {
    this.isUnifiedModalOpen.set(false);
  }

  onUnifiedFileSelected(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    if (inputEl.files && inputEl.files.length > 0) {
      const modalType = this.activeModalType();
      const filesLimit = (modalType === 'news') ? 4 : (modalType === 'product' ? 10 : 1);
      
      const filesArray = Array.from(inputEl.files).slice(0, filesLimit - this.selectedImagesList().length);
      
      if (filesArray.length > 0 && modalType === 'image') {
        const file = filesArray[0];
        let formatVal = 'jpeg';
        const mimeType = file.type.toLowerCase();
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (mimeType.includes('png') || ext === '.png') formatVal = 'png';
        else if (mimeType.includes('webp') || ext === '.webp') formatVal = 'webp';
        else if (mimeType.includes('heic') || mimeType.includes('heif') || ext === '.heic' || ext === '.heif') formatVal = 'heic';
        else formatVal = 'jpeg';
        this.unifiedForm.patchValue({ format: formatVal });
      }

      filesArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (result) {
            this.selectedImagesList.update((prev) => [
              ...prev,
              {
                previewUrl: result,
                file: file,
                cropPos: { x: 0, y: 0, scale: 1 }
              }
            ]);
            // Highlight latest uploaded frame
            this.activeImageIndex.set(this.selectedImagesList().length - 1);
          }
        };
        reader.readAsDataURL(file);
      });
      inputEl.value = "";
    }
  }

  onUnifiedVideoFileSelected(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    if (inputEl.files && inputEl.files.length > 0) {
      const file = inputEl.files[0];
      const previewUrl = URL.createObjectURL(file);
      this.selectedVideoFile.set({ file, previewUrl });

      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase() || ".mp4";
      this.detectedVideoFormat.set(ext);

      // Detect resolution and duration programmatically
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.src = previewUrl;
      videoEl.onloadedmetadata = () => {
        const width = videoEl.videoWidth;
        const height = videoEl.videoHeight;
        const duration = videoEl.duration; // in seconds
        
        // Quality detection
        let quality = '1080p';
        if (width >= 3840 || height >= 2160) {
          quality = '4K';
        } else if (width >= 1920 || height >= 1080) {
          quality = '1080p';
        } else if (width >= 1280 || height >= 720) {
          quality = '720p';
        } else {
          quality = '480p';
        }
        
        this.detectedVideoQuality.set(quality);
        this.detectedVideoDuration.set(duration);
        this.unifiedForm.patchValue({ resolution: quality });
        URL.revokeObjectURL(videoEl.src);
      };
      
      videoEl.onerror = () => {
        URL.revokeObjectURL(videoEl.src);
      };
    }
  }

  removeUnifiedImage(index: number, event: Event) {
    event.stopPropagation();
    this.selectedImagesList.update((prev) => {
      const newList = [...prev];
      newList.splice(index, 1);
      return newList;
    });
    const len = this.selectedImagesList().length;
    if (this.activeImageIndex() >= len) {
      this.activeImageIndex.set(Math.max(0, len - 1));
    }
  }

  cropImage(file: File, panX = 0, panY = 0, scale = 1, aspectRatio: '1:1' | '16:9' = '1:1'): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let destWidth = 1080;
        let destHeight = 1080;
        if (aspectRatio === '16:9') {
          destWidth = 1920;
          destHeight = 1080;
        }
        canvas.width = destWidth;
        canvas.height = destHeight;
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        const width = img.width;
        const height = img.height;
        const previewWidth = 384;
        let cropWidth = width;
        let cropHeight = height;
        let cropX = 0;
        let cropY = 0;

        if (aspectRatio === '1:1') {
          cropWidth = Math.min(width, height) / scale;
          cropHeight = cropWidth;
          if (width > height) {
            const naturalOffsetX = (width - cropWidth) / 2;
            const pixScale = height / previewWidth;
            cropX = naturalOffsetX - (panX * pixScale);
            cropX = Math.max(0, Math.min(cropX, width - cropWidth));
          } else {
            const naturalOffsetY = (height - cropHeight) / 2;
            const pixScale = width / previewWidth;
            cropY = naturalOffsetY - (panY * pixScale);
            cropY = Math.max(0, Math.min(cropY, height - cropHeight));
          }
        } else {
          const targetRatio = 16 / 9;
          const currentRatio = width / height;
          if (currentRatio > targetRatio) {
            cropHeight = height / scale;
            cropWidth = cropHeight * targetRatio;
            const naturalOffsetX = (width - cropWidth) / 2;
            const pixScale = height / previewWidth;
            cropX = naturalOffsetX - (panX * pixScale);
            cropX = Math.max(0, Math.min(cropX, width - cropWidth));
          } else {
            cropWidth = width / scale;
            cropHeight = cropWidth / targetRatio;
            const naturalOffsetY = (height - cropHeight) / 2;
            const pixScale = width / previewWidth;
            cropY = naturalOffsetY - (panY * pixScale);
            cropY = Math.max(0, Math.min(cropY, height - cropHeight));
          }
        }

        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, destWidth, destHeight);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(img.src);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas crop failed"));
          }
        }, "image/jpeg", 0.9);
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(img.src);
        reject(err);
      };
    });
  }

  async handleUnifiedSubmit() {
    const profile = this.profile();
    if (!profile) return;

    if (this.unifiedForm.invalid) {
      this.unifiedForm.markAllAsTouched();
      return;
    }

    this.isSubmittingUnified.set(true);
    this.unifiedErrorMessage.set(null);
    this.unifiedSuccessMessage.set(null);

    try {
      const type = this.activeModalType();
      const currentUserId = profile.uid;
      
      const activeTypePluralMap: Record<string, string> = {
        'news': 'news',
        'job': 'jobs',
        'image': 'images',
        'product': 'products',
        'video': 'videos'
      };
      const pluralType = activeTypePluralMap[type] || type;

      const collectionPath = `accounts/${currentUserId}/${pluralType}`;
      const docId = this.isEditingUnified() ? this.editingDocId()! : doc(collection(db, collectionPath)).id;
      const extAsset = this.isEditingUnified() ? this.editingAsset() : null;

      const uploadedUrls: string[] = [];
      const isSquare = (type === 'image' || type === 'product' || type === 'job');
      const ratio = isSquare ? '1:1' : '16:9';

      const imagesToProcess = this.selectedImagesList();
      for (let i = 0; i < imagesToProcess.length; i++) {
        const item = imagesToProcess[i];
        if (item.file) {
          const croppedBlob = await this.cropImage(item.file, item.cropPos.x, item.cropPos.y, item.cropPos.scale, ratio);
          
          const fileExt = item.file.name.substring(item.file.name.lastIndexOf('.')) || '.jpg';
          const cleanName = item.file.name.replace(/\.[^/.]+$/, "");
          const croppedFile = new File([croppedBlob], `${cleanName}_cropped_${i}${fileExt}`, { type: 'image/jpeg' });
          
          const filename = `${docId}_frame_${i}_${Date.now()}${fileExt}`;
          const storagePath = `accounts/${currentUserId}/${pluralType}/${filename}`;
          const storageRef = ref(storage, storagePath);

          await uploadBytes(storageRef, croppedFile);
          const downloadUrl = await getDownloadURL(storageRef);
          uploadedUrls.push(downloadUrl);
        } else {
          uploadedUrls.push(item.previewUrl);
        }
      }

      const fValue = this.unifiedForm.value;
      const finalStatus = fValue.status || "published";
      let finalVerificationStatus = fValue.verificationStatus || "review";

      if (!this.canManageOrganization() && !this.isOwner() && !this.authService.isMasterAdmin() && finalStatus !== 'unpublished') {
        finalVerificationStatus = 'review';
      }

      // Handle video uploading to Firebase Storage if type is video
      let finalVideoUrl = extAsset?.videoUrl || "";
      if (type === 'video' && this.selectedVideoFile()) {
        const vidObj = this.selectedVideoFile();
        if (vidObj) {
          const file = vidObj.file;
          const fileExt = file.name.substring(file.name.lastIndexOf('.')) || '.mp4';
          const filename = `${docId}_video_${Date.now()}${fileExt}`;
          const storagePath = `accounts/${currentUserId}/${pluralType}/${filename}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, file);
          finalVideoUrl = await getDownloadURL(storageRef);
        }
      }

      const prevAsset = this.editingAsset();
      let verifiedByValue = (this.isEditingUnified() ? prevAsset?.verifiedBy : "") || "";
      if (this.isEditingUnified() && prevAsset) {
        const prevStatus = prevAsset.verificationStatus || "review";
        if (prevStatus === 'review' && (finalVerificationStatus === 'approved' || finalVerificationStatus === 'rejected')) {
          verifiedByValue = this.authService.user()?.uid || "";
        }
      }

      const basePayload: Record<string, unknown> = this.isEditingUnified() ? {
        ...extAsset,
        status: finalStatus,
        verificationStatus: finalVerificationStatus,
        verifiedBy: verifiedByValue,
        updatedAt: serverTimestamp(),
      } : {
        id: docId,
        status: finalStatus,
        verificationStatus: finalVerificationStatus,
        verificationRemarks: "",
        verifiedBy: "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        authorUid: this.authService.user()?.uid || currentUserId,
        ownerUid: currentUserId,
        ownedBy: currentUserId,
        uploadedBy: this.authService.user()?.uid || currentUserId,
        viewedBy: [],
        likedBy: [],
        likes: [],
        views: []
      };

      if (type === 'image') {
        if (!this.isEditingUnified()) {
          basePayload['status'] = 'pending';
          basePayload['verificationStatus'] = 'pending';
          basePayload['remark'] = '';
          basePayload['remarks'] = '';
        }
        basePayload['title'] = "";
        basePayload['description'] = "";
        basePayload['caption'] = "";
        basePayload['tags'] = extAsset?.tags || [];
        basePayload['imageUrl'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.imageUrl || "");
        basePayload['license'] = (fValue.license === 'open-source' || fValue.license === 'Open Source' || fValue.license === 'openSource') ? 'Open Source' : 'Standard';
        basePayload['format'] = fValue.format || extAsset?.format || "jpeg";
      } else if (type === 'news') {
        basePayload['title'] = fValue.title || "";
        basePayload['content'] = fValue.description || "";
        basePayload['description'] = fValue.description || "";
        basePayload['bannerImage'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.bannerImage || extAsset?.imageUrl || "");
        basePayload['galleryImages'] = uploadedUrls;
        basePayload['imageUrl'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.imageUrl || "");
        basePayload['category'] = fValue.category || "General";
        basePayload['source'] = fValue.source || extAsset?.source || "";
        basePayload['url'] = fValue.newsUrl || extAsset?.url || "";
        basePayload['isBreaking'] = fValue.isBreaking || false;
      } else if (type === 'job') {
        basePayload['jobTitle'] = fValue.title || "";
        basePayload['description'] = fValue.description || "";
        basePayload['requirements'] = extAsset?.requirements || [];
        basePayload['companyLogoUrl'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.companyLogoUrl || "");
        basePayload['company'] = fValue.company || "";
        basePayload['companyInfo'] = fValue.company || "";
        basePayload['location'] = fValue.location || "";
        basePayload['jobType'] = fValue.jobType || "full-time";
        basePayload['experienceLevel'] = fValue.experienceLevel || "entry";
        basePayload['isRemote'] = fValue.isRemote || false;
        basePayload['jobCategory'] = fValue.jobCategory || "Private";
        basePayload['currency'] = "INR";
        basePayload['salaryRange'] = fValue.price ? "INR ₹ " + Number(fValue.price).toLocaleString('en-IN') + " / Month" : "Not Disclosed";
        basePayload['deadline'] = extAsset?.deadline || serverTimestamp();
      } else if (type === 'product') {
        basePayload['productName'] = fValue.title || "";
        basePayload['description'] = fValue.description || "";
        basePayload['imageUrl'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.imageUrl || "");
        basePayload['images'] = uploadedUrls.length > 0 ? uploadedUrls : (extAsset?.images || []);
        basePayload['currency'] = "INR";
        basePayload['price'] = fValue.price || 0;
        basePayload['brand'] = fValue.brand || "";
        basePayload['stockQuantity'] = fValue.stockQuantity || 0;
        basePayload['stock'] = fValue.stockQuantity || 0;
        basePayload['discount'] = extAsset?.discount || 0;
        basePayload['category'] = fValue.category || "General";
        basePayload['variants'] = extAsset?.variants || [];
        basePayload['productUrl'] = fValue.productUrl || extAsset?.productUrl || ""; 
      } else if (type === 'video') {
        basePayload['title'] = fValue.title || "";
        basePayload['description'] = fValue.description || "";
        basePayload['posterUrl'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.posterUrl || extAsset?.thumbnailUrl || "");
        basePayload['thumbnailUrl'] = uploadedUrls.length > 0 ? uploadedUrls[0] : (extAsset?.thumbnailUrl || extAsset?.posterUrl || "");
        basePayload['videoUrl'] = finalVideoUrl || "";
        basePayload['resolution'] = this.detectedVideoQuality() || extAsset?.resolution || "1080p";
        basePayload['duration'] = this.detectedVideoDuration() || extAsset?.duration || 0;
        basePayload['videoCategory'] = (this.activeVideoSegment() === 'Movies' ? 'Videos' : (this.activeVideoSegment() as 'Reels' | 'Shorts' | 'Videos')) || 'Reels';
        basePayload['license'] = (fValue.license === 'open-source' || fValue.license === 'Open Source' || fValue.license === 'openSource' || fValue.license === 'Open Source') ? 'Open Source' : 'Standard';
        basePayload['director'] = fValue.director || extAsset?.director || ""; 
        basePayload['format'] = this.detectedVideoFormat() || extAsset?.format || ".mp4";
      }

      const saveDocRef = doc(db, `accounts/${currentUserId}/${pluralType}`, docId);
      await setDoc(saveDocRef, basePayload);

      this.unifiedSuccessMessage.set(this.isEditingUnified() ? `${type.toUpperCase()} Successfully Updated!` : `${type.toUpperCase()} Successfully Published!`);
      setTimeout(() => {
        this.closeUnifiedModal();
        this.loadSelectedTabAssets();
        if (this.selectedTab() === "Images") {
          this.loadUserImages();
        }
      }, 1500);

    } catch (error) {
      console.error("Error creating/editing unified asset", error);
      handleFirestoreError(error, OperationType.WRITE, null);
      this.unifiedErrorMessage.set("Failed to publish post. Please check permissions.");
    } finally {
      this.isSubmittingUnified.set(false);
    }
  }

  onAddAssetFileSelected(event: Event, maxCount: number) {
    const inputEl = event.target as HTMLInputElement;
    if (inputEl.files && inputEl.files.length > 0) {
      if (this.uploadedAssetImages().length >= maxCount) return;
      
      const file = inputEl.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          this.uploadedAssetImages.update(imgs => {
            const updated = [...imgs, result];
            this.activeAssetImageIndex.set(updated.length - 1);
            return updated;
          });
          this.assetImageCropPositions.update(pos => [...pos, { x: 0, y: 0, scale: 1 }]);
        }
      };
      reader.readAsDataURL(file);
      inputEl.value = "";
    }
  }

  removeAssetImage(index: number, event: Event) {
    event.stopPropagation();
    this.uploadedAssetImages.update(imgs => {
      const updated = imgs.filter((_, i) => i !== index);
      if (this.activeAssetImageIndex() >= updated.length) {
        this.activeAssetImageIndex.set(Math.max(0, updated.length - 1));
      }
      return updated;
    });
    this.assetImageCropPositions.update(pos => pos.filter((_, i) => i !== index));
  }
  assetToCreate = signal<any>({});
  assetToDelete = signal<any | null>(null);
  deleteItemId = signal<string | null>(null);
  deleteItemUrls = signal<string[]>([]);
  deleteItemType = signal<'images' | 'news' | 'jobs' | 'products' | 'videos' | null>(null);
  isDeletingProgress = signal<boolean>(false);
  isDeleteModalOpen = signal<boolean>(false);
  toastMessage = signal<string | null>(null);
  toastType = signal<'success' | 'error'>('success');
  toastTimeoutId = signal<ReturnType<typeof setTimeout> | null>(null);
  isCreatingAsset = signal<boolean>(false);
  isEditingBio = signal<boolean>(false);
  bioInput = signal<string>("");

  isFollowersSheetOpen = signal<boolean>(false);
  isFollowingSheetOpen = signal<boolean>(false);
  followersSheetSearchText = signal<string>("");
  followingSheetSearchText = signal<string>("");
  followersList = signal<AuthProfile[]>([]);
  followingList = signal<AuthProfile[]>([]);
  isFollowersLoading = signal<boolean>(false);
  isFollowingLoading = signal<boolean>(false);

  async openFollowersSheet() {
    this.isFollowersSheetOpen.set(true);
    this.followersSheetSearchText.set("");
    const p = this.profile();
    if (!p) return;
    this.isFollowersLoading.set(true);
    try {
      const colRef = collection(db, `accounts/${p.uid}/followers`);
      const snap = await getDocs(colRef);
      const uids = snap.docs.map(doc => doc.id);
      
      if (uids.length === 0) {
        this.followersList.set([]);
        return;
      }
      
      const profiles: AuthProfile[] = [];
      const userFetchPromises = uids.map(uid => getDoc(doc(db, 'accounts', uid)));
      const docSnaps = await Promise.all(userFetchPromises);
      for (const d of docSnaps) {
        if (d.exists()) {
          profiles.push(d.data() as AuthProfile);
        }
      }
      this.followersList.set(profiles);
    } catch (err) {
      console.error('Error fetching followers profiles', err);
    } finally {
      this.isFollowersLoading.set(false);
    }
  }

  async openFollowingSheet() {
    this.isFollowingSheetOpen.set(true);
    this.followingSheetSearchText.set("");
    const p = this.profile();
    if (!p) return;
    this.isFollowingLoading.set(true);
    try {
      const colRef = collection(db, `accounts/${p.uid}/following`);
      const snap = await getDocs(colRef);
      const uids = snap.docs.map(doc => doc.id);
      
      if (uids.length === 0) {
        this.followingList.set([]);
        return;
      }
      
      const profiles: AuthProfile[] = [];
      const userFetchPromises = uids.map(uid => getDoc(doc(db, 'accounts', uid)));
      const docSnaps = await Promise.all(userFetchPromises);
      for (const d of docSnaps) {
        if (d.exists()) {
          profiles.push(d.data() as AuthProfile);
        }
      }
      this.followingList.set(profiles);
    } catch (err) {
      console.error('Error fetching following profiles', err);
    } finally {
      this.isFollowingLoading.set(false);
    }
  }

  closeFollowersSheet() {
    this.isFollowersSheetOpen.set(false);
  }

  closeFollowingSheet() {
    this.isFollowingSheetOpen.set(false);
  }

  closeSheets() {
    this.closeFollowersSheet();
    this.closeFollowingSheet();
  }

  get filteredFollowers() {
    const list = this.followersList();
    const queryStr = this.followersSheetSearchText().trim().toLowerCase();
    if (!queryStr) return list;
    return list.filter(u => 
      (u.displayName && u.displayName.toLowerCase().includes(queryStr)) || 
      (u.username && u.username.toLowerCase().includes(queryStr))
    );
  }

  get filteredFollowing() {
    const list = this.followingList();
    const queryStr = this.followingSheetSearchText().trim().toLowerCase();
    if (!queryStr) return list;
    return list.filter(u => 
      (u.displayName && u.displayName.toLowerCase().includes(queryStr)) || 
      (u.username && u.username.toLowerCase().includes(queryStr))
    );
  }

  navigateToUser(uid: string) {
    this.closeFollowersSheet();
    this.closeFollowingSheet();
    this.router.navigate(['/profile', uid]);
  }

  modalInput = "";
  modal = {
    isOpen: signal(false),
    type: signal<"email" | "password" | "signout" | "verify" | "deleteAsset">(
      "email",
    ),
    title: signal(""),
    message: signal(""),
    confirmText: signal(""),
  };

  profileContent = viewChild<ElementRef>("profileContent");

  get chips() {
    const p = this.profile();
    if (!p) return [];
    const base = [
      { label: "About", icon: "person" },
      { label: "Images", icon: "image" },
      { label: "Videos", icon: "play_circle" },
      { label: "Voice", icon: "mic" }
    ];

    if (p.accountType === "organization" || p.accountType === "country") {
      base.push(
        { label: "News", icon: "article" },
        { label: "Jobs", icon: "work" },
        { label: "Products", icon: "shopping_bag" },
        { label: "Music", icon: "library_music" }
      );
    }
    return base;
  }

  getTabIcon(tabLabel: string): string {
    return this.chips.find((c) => c.label === tabLabel)?.icon || "add";
  }

  toDate(
    ts: Timestamp | FieldValue | Date | string | number | null | undefined,
  ): Date | null {
    if (!ts) return null;
    if (
      typeof ts === "object" &&
      "toDate" in ts &&
      typeof ts.toDate === "function"
    ) {
      return ts.toDate();
    }
    if (ts instanceof Date) return ts;
    try {
      return new Date(ts as string | number);
    } catch {
      return null;
    }
  }

  toggleBioEdit() {
    this.isEditingBio.set(!this.isEditingBio());
    if (this.isEditingBio()) {
      this.bioInput.set(this.profile()?.bio || "");
    }
  }

  async saveBio() {
    const user = this.authService.user();
    if (!user) return;

    try {
      const bio = this.bioInput();
      await this.authService.updateProfile(user.uid, { bio });
      this.isEditingBio.set(false);
    } catch (error) {
      console.error("Error saving bio:", error);
    }
  }

  currentRouteId = signal<string | null>(null);

  constructor() {
    // Real-time contact list subscription for currently logged-in user
    effect(() => {
      const currentUser = this.authService.user();

      if (this.unsubscribeContactsList) {
        this.unsubscribeContactsList();
        this.unsubscribeContactsList = null;
      }

      if (!currentUser?.uid) {
        this.userContactUids.set(new Set());
        return;
      }

      const contactsRef = collection(db, 'accounts', currentUser.uid, 'contacts');
      this.unsubscribeContactsList = onSnapshot(
        contactsRef,
        (snapshot) => {
          const uids = new Set<string>();
          snapshot.docs.forEach((docSnap) => {
            uids.add(docSnap.id);
            const data = docSnap.data();
            if (data && data['uid']) {
              uids.add(data['uid']);
            }
          });
          this.userContactUids.set(uids);
        },
        (error) => {
          console.warn('Error loading contacts for chat button visibility:', error);
          this.userContactUids.set(new Set());
        }
      );
    });


    // 1. Subscribe to route param and update signal
    this.route.paramMap.subscribe((params) => {
      this.closeSheets();
      this.currentRouteId.set(params.get("profileId") || params.get("id"));
    });

    // 1b. Subscribe to query param tab
    this.route.queryParamMap.subscribe((queryParams) => {
      const tab = queryParams.get("tab");
      if (tab) {
        this.selectedTab.set(tab);
      }
    });

    // 2. Member search logic
    this.addMemberForm.get('searchQuery')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(queryStr => {
        if (!queryStr || queryStr.length < 3) return of([]);
        this.isSearchingUsers.set(true);
        const colRef = collection(db, 'accounts');
        const q = query(
          colRef, 
          where('accountType', '==', 'person')
        );
        return from(getDocs(q)).pipe(
          map(snap => {
            const users = snap.docs.map(d => d.data() as AuthProfile);
            const lowerQuery = queryStr.toLowerCase();
            return users.filter(u => 
              u.displayName.toLowerCase().includes(lowerQuery) || 
              (u.username && u.username.toLowerCase().includes(lowerQuery))
            ).slice(0, 5);
          })
        );
      })
    ).subscribe((results: AuthProfile[]) => {
      this.searchResults.set(results);
      this.isSearchingUsers.set(false);
    });

    // 3. React to profile changes for roles
    // Logic moved to rolesList$ observable

    // 4. React to route ID changes and user profile changes
    effect(() => {
      const id = this.currentRouteId();
      if (id) {
        // Only trigger loadUserProfile if we have an explicit ID
        this.loadUserProfile(id);
      } else {
        // We are on our own profile /profile route without an ID
        const user = this.authService.userProfile();
        if (user) {
          this.profile.set(user);
          this.loadFollowData(user.uid);
          this.loadSelectedTabAssets();
          this.loadUserImages();
          this.loadChats();
        } else {
          if (this.unsubscribeChatsList) {
            this.unsubscribeChatsList();
            this.unsubscribeChatsList = null;
          }
        }
      }
    });

    effect(() => {
      const p = this.profile();
      if (this.isBrowser && p && this.profileContent()) {
        const items =
          this.profileContent()?.nativeElement.querySelectorAll(
            ".animate-ios-slide",
          );
        if (items && items.length > 0) {
          animate(
            items,
            { opacity: [0, 1], y: [20, 0], scale: [0.98, 1] },
            { delay: stagger(0.05), duration: 0.5, ease: [0.16, 1, 0.3, 1] },
          );
        }
      }
    });
  }

  async loadUserProfile(uid: string) {
    try {
      const userDoc = await getDoc(doc(db, "accounts", uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data() as AuthProfile;
        this.profile.set(profileData);
        this.loadFollowData(uid);
        this.loadSelectedTabAssets();
        this.loadUserImages();

        if (profileData.accountType === "organization" || profileData.accountType === "country") {
          this.checkOrgAdmin(uid);
          this.verifyOrganizationRole(uid);
        } else {
          this.isOrgAdmin.set(false);
          const currentUser = this.authService.user();
          const isOwn = currentUser ? currentUser.uid === uid : false;
          this.hasManagementPermission = isOwn;
          this.canPublishNews.set(isOwn);
          this.canDeleteNews.set(isOwn);
          this.canEditProfile.set(isOwn);
          this.canModifyOrgDetails.set(isOwn);
        }
      } else {
        this.profile.set(null);
      }
    } catch (error) {
      console.error("Error loading profile", error);
      this.profile.set(null);
    }
  }

  async verifyOrganizationRole(orgUid: string) {
    const currentUser = this.authService.user();
    if (!currentUser) {
      this.hasManagementPermission = false;
      this.canPublishNews.set(false);
      this.canDeleteNews.set(false);
      this.canEditProfile.set(false);
      this.canModifyOrgDetails.set(false);
      return;
    }

    const userUid = currentUser.uid;

    // Default permission settings if owner
    if (userUid === orgUid) {
      this.hasManagementPermission = true;
      this.canPublishNews.set(true);
      this.canDeleteNews.set(true);
      this.canEditProfile.set(true);
      this.canModifyOrgDetails.set(true);
      return;
    }

    // Default permission settings if global admin
    if (this.authService.isAdmin()) {
      this.hasManagementPermission = true;
      this.canPublishNews.set(true);
      this.canDeleteNews.set(true);
      this.canEditProfile.set(true);
      this.canModifyOrgDetails.set(true);
      return;
    }

    try {
      // Perform a 'Read' operation on the roles subcollection: accounts/{orgId}/roles/{uid}
      const roleDocRef = doc(db, "accounts", orgUid, "roles", userUid);
      const roleDoc = await getDoc(roleDocRef);

      if (roleDoc.exists()) {
        const roleData = roleDoc.data();
        const roleList = roleData["roles"] || roleData["role"] || [];
        const normalizedRoles = roleList.map((r: string) => r.toLowerCase());

        // Check if role is present in this list and has "Managing Trustee" or "admin" or other standard authorized roles
        const isAuthorized = normalizedRoles.some((r: string) => 
          r === "managing trustee" || 
          r === "admin" || 
          r === "founder" || 
          r === "authorized person" || 
          r === "authorized_person" || 
          r === "owner" || 
          r === "managing director" || 
          r === "managing partner"
        );

        if (isAuthorized) {
          this.hasManagementPermission = true;
          this.canPublishNews.set(true);
          this.canDeleteNews.set(true);
          this.canEditProfile.set(true);
          this.canModifyOrgDetails.set(true);
          return;
        }
      }

      // If they didn't have subcollection roles, fallback check on org account roles map (if exists)
      const orgDocRef = doc(db, "accounts", orgUid);
      const orgDoc = await getDoc(orgDocRef);
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        const rolesMap = orgData["roles"];
        if (rolesMap && typeof rolesMap === "object" && !Array.isArray(rolesMap)) {
          const userRole = rolesMap[userUid];
          if (userRole) {
            const roleLower = userRole.toLowerCase();
            if (roleLower === "founder" || roleLower === "admin" || roleLower === "owner" || roleLower === "managing director" || roleLower === "managing trustee" || roleLower === "managing partner") {
              this.hasManagementPermission = true;
              this.canPublishNews.set(true);
              this.canDeleteNews.set(true);
              this.canEditProfile.set(true);
              this.canModifyOrgDetails.set(true);
              return;
            }
          }
        }
      }

      this.hasManagementPermission = false;
      this.canPublishNews.set(false);
      this.canDeleteNews.set(false);
      this.canEditProfile.set(false);
      this.canModifyOrgDetails.set(false);
    } catch (err) {
      console.error("Error verifying organization roles mapping:", err);
      this.hasManagementPermission = false;
      this.canPublishNews.set(false);
      this.canDeleteNews.set(false);
      this.canEditProfile.set(false);
      this.canModifyOrgDetails.set(false);
    }
  }

  selectCandidate(user: any) {
    this.selectedCandidate.set(user);
    this.searchResults.set([]);
    this.addMemberForm.get('searchQuery')?.setValue('', { emitEvent: false });
  }

  clearSelection() {
    this.selectedCandidate.set(null);
  }

  hasProtectedRole(roles: string[] | undefined): boolean {
    if (!Array.isArray(roles)) return false;
    return roles.some(r => {
      const lower = r.toLowerCase().trim();
      return lower === 'managing director' || lower === 'managing trustee' || lower === 'founder';
    });
  }

  canAddMembers = computed(() => {
    const p = this.profile();
    if (!p || (p.accountType !== 'organization' && p.accountType !== 'country')) return false;
    const currentUser = this.authService.user();
    if (!currentUser) return false;
    if (this.isOwner()) return true;

    return this.authorizedPersons().some((m: any) => {
      if (m.uid !== currentUser.uid) return false;
      const isActive = !m.end || new Date(m.end).getTime() > Date.now();
      if (!isActive) return false;
      return m.roles?.some((r: string) => {
        const lower = r.toLowerCase().trim();
        return lower === 'managing director' || lower === 'managing trustee' || lower === 'founder';
      });
    });
  });

  async onAddMember() {
    const user = this.selectedCandidate();
    const org = this.profile();
    const currentUser = this.authService.user();
    if (!user || !org || !currentUser || this.addMemberForm.invalid) return;

    this.isSavingMember.set(true);
    try {
      const formVal = this.addMemberForm.value;
      const roleDocRef = doc(db, `accounts/${org.uid}/roles`, user.uid);
      
      const payload = {
        uid: user.uid,
        role: org.uid,
        roles: [formVal.roleTitle],
        start: formVal.startDate,
        startedBy: currentUser.uid,
        startReason: formVal.startReason,
        startDate: formVal.startDate, // legacy
        endDate: formVal.endDate || null, // legacy
        updatedAt: serverTimestamp()
      };

      await setDoc(roleDocRef, payload, { merge: true });
      
      this.addMemberForm.reset({
        startDate: new Date().toISOString().split('T')[0]
      });
      this.selectedCandidate.set(null);
    } catch (err) {
      console.error('Error adding member:', err);
    } finally {
      this.isSavingMember.set(false);
    }
  }

  async removeMember(uid: string, endReason = '') {
    const org = this.profile();
    const currentUser = this.authService.user();
    if (!org || !currentUser) return;
    
    try {
      await updateDoc(doc(db, `accounts/${org.uid}/roles`, uid), {
        end: new Date().toISOString(),
        endedBy: currentUser.uid,
        endReason: endReason,
        roles: []
      });
      this.revokingUserId.set(null);
    } catch (err) {
      console.error('Error removing member:', err);
    }
  }



  async checkOrgAdmin(orgUid: string) {
    const currentUser = this.authService.user();
    if (!currentUser) {
      this.isOrgAdmin.set(false);
      return;
    }

    if (currentUser.uid === orgUid) {
      this.isOrgAdmin.set(true);
      return;
    }

    try {
      const rolesRef = collection(db, `accounts/${orgUid}/roles`);
      const q = query(
        rolesRef,
        where("uid", "==", currentUser.uid),
        where("role", "array-contains", "admin"),
      );
      const snap = await getDocs(q);

      this.isOrgAdmin.set(!snap.empty);
    } catch (error) {
      console.error("Error checking org admin role", error);
      this.isOrgAdmin.set(false);
    }
  }

  selectTab(tab: string) {
    this.selectedTab.set(tab);

    // Auto-scroll the tab into view
    setTimeout(() => {
      const element = document.getElementById("tab-" + tab);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    });

    if (tab === "Chats") {
      this.loadChats();
    } else if (tab === "Images" || tab === "Music") {
      this.loadUserImages();
    } else if (tab !== "About") {
      this.loadSelectedTabAssets();
    }
  }

  async onProfilePhotoSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file && this.profile()) {
      const p = this.profile()!;
      try {
        const path = `accounts/${p.uid}/profile_${Date.now()}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await this.authService.updateProfile(p.uid, { photoURL: url });
        this.profile.update(prev => prev ? { ...prev, photoURL: url } : null);
      } catch (err) {
        console.error('Error uploading profile photo', err);
      }
    }
  }

  async onCoverPhotoSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file && this.profile()) {
      const p = this.profile()!;
      try {
        const path = `accounts/${p.uid}/cover_${Date.now()}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await this.authService.updateProfile(p.uid, { coverURL: url });
        this.profile.update(prev => prev ? { ...prev, coverURL: url } : null);
      } catch (err) {
        console.error('Error uploading cover photo', err);
      }
    }
  }

  async loadSelectedTabAssets() {
    const p = this.profile();
    if (!p) return;

    const tab = this.selectedTab();
    if (tab === 'About' || tab === 'Chats' || tab === 'Images') {
      return;
    }

    const currentTabCollection = tab.toLowerCase();

    this.isLoadingAssets.set(true);
    try {
      const items = await this.orgDataService.fetchTabAssets(p.uid, currentTabCollection, this.canManageOrganization());
      this.assets.set(items);
    } catch (error) {
      console.error(`Error loading ${currentTabCollection}`, error);
      this.assets.set([]);
    } finally {
      this.isLoadingAssets.set(false);
    }
  }

  // New Dedicated UserImage and uploads architecture methods
  viewImage(img: UserImage) {
    const ownerId = img.ownerUid || this.profile()?.uid;
    if (ownerId && img.id) {
      this.router.navigate(['/image', ownerId, img.id]);
    } else if (img.id) {
      this.router.navigate(['/image', img.id]);
    }
  }

  openAddImageModal() {
    this.selectedImageFile.set(null);
    this.imagePreviewUrl.set(null);
    this.isUploadingImageModalOpen.set(true);
    this.imageToUploadMetadata.set({
      status: 'published',
      license: 'standard'
    });
  }

  closeAddImageModal() {
    this.isUploadingImageModalOpen.set(false);
  }

  cropToSquare(file: File, panX = 0, panY = 0, scale = 1): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 1080; // High-quality 1:1 format
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        const width = img.width;
        const height = img.height;
        let cropSize = Math.min(width, height) / scale;
        let cropX = 0;
        let cropY = 0;

        // Container preview size estimate (for scaling the pan coordinates)
        const previewSize = 384; 

        if (width > height) {
          cropSize = height / scale;
          const naturalOffsetX = (width - cropSize) / 2;
          const pixScale = height / previewSize;
          cropX = naturalOffsetX - (panX * pixScale);
          cropX = Math.max(0, Math.min(cropX, width - cropSize)); // clamp to edges
        } else {
          cropSize = width / scale;
          const naturalOffsetY = (height - cropSize) / 2;
          const pixScale = width / previewSize;
          cropY = naturalOffsetY - (panY * pixScale);
          cropY = Math.max(0, Math.min(cropY, height - cropSize)); // clamp to edges
        }

        ctx.drawImage(img, cropX, cropY, cropSize, cropSize, 0, 0, size, size);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(img.src);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas crop failed"));
          }
        }, "image/jpeg", 0.9);
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(img.src);
        reject(err);
      };
    });
  }

  handleFileSelection(file: File) {
    this.selectedImageFile.set(file);
    this.imageCropPosition.set({ x: 0, y: 0, scale: 1 });
    
    this.isUploadingImage.set(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreviewUrl.set(e.target?.result as string);
      this.isUploadingImage.set(false);
    };
    reader.onerror = () => {
      this.isUploadingImage.set(false);
    };
    reader.readAsDataURL(file);
  }

  onImageFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  onImageDropped(event: DragEvent) {
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      this.handleFileSelection(file);
    }
  }

  async uploadAndSaveImage() {
    const p = this.profile();
    const originalFile = this.selectedImageFile();
    if (!p || !originalFile) return;

    this.isUploadingImage.set(true);
    try {
      // 1. Process physical crop using the recorded pan coordinates BEFORE upload
      const position = this.imageCropPosition();
      const croppedBlob = await this.cropToSquare(originalFile, position.x, position.y, position.scale);
      const fileExt = originalFile.name.substring(originalFile.name.lastIndexOf('.'));
      const cleanName = originalFile.name.replace(/\.[^/.]+$/, "");
      const croppedFile = new File([croppedBlob], `${cleanName}_cropped${fileExt || '.jpg'}`, { type: 'image/jpeg' });

      // 1. Storage Path MUST be exactly: accounts/{currentUserId}/images/{uniqueFilename}
      const uniqueFilename = `${Date.now()}_${croppedFile.name}`;
      const storageRef = ref(storage, `accounts/${p.uid}/images/${uniqueFilename}`);
      
      // Upload to Storage
      await uploadBytes(storageRef, croppedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      // 2. Firestore Database Path MUST be exactly: accounts/{currentUserId}/images/{autoId}
      const imagesCollectionRef = collection(db, `accounts/${p.uid}/images`);
      const newImage = {
        imageUrl: downloadUrl,
        status: 'pending' as any,
        visibility: 'public' as any,
        verificationStatus: 'pending' as any,
        remark: '',
        remarks: '',
        verifiedBy: '',
        license: this.imageToUploadMetadata().license as any,
        createdAt: serverTimestamp() as any,
        authorUid: this.authService.user()?.uid || p.uid,
        ownerUid: p.uid,
        likedBy: [],
        viewedBy: [],
        ownedBy: p.accountType === 'organization' ? 'Organization' : 'Individual',
        uploadedBy: this.authService.user()?.uid || p.uid,
        likes: [],
        views: []
      } as unknown as UserImage;

      await addDoc(imagesCollectionRef, newImage);
      this.closeAddImageModal();
      this.loadUserImages();
    } catch (err) {
      console.error('Error uploading/saving image:', err);
    } finally {
      this.isUploadingImage.set(false);
    }
  }

  async loadUserImages() {
    const p = this.profile();
    if (!p) return;
    this.isLoadingUserImages.set(true);
    try {
      const imagesCollectionRef = collection(db, `accounts/${p.uid}/images`);
      const snap = await getDocs(imagesCollectionRef);
      const list: UserImage[] = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          imageUrl: data['imageUrl'] || '',
          visibility: data['visibility'] || (data['status'] === 'unpublished' ? 'private' : 'public'),
          status: data['status'] || 'published',
          verificationStatus: data['verificationStatus'] || 'pending',
          caption: data['caption'] || '',
          license: data['license'] || 'standard',
          createdAt: data['createdAt'],
          likes: data['likes'] || [],
          views: data['views'] || [],
          likedBy: data['likedBy'] || data['likes'] || [],
          viewedBy: data['viewedBy'] || data['views'] || [],
          ownedBy: data['ownedBy'] || data['ownerUid'] || p.uid,
          uploadedBy: data['uploadedBy'] || data['authorUid'] || p.uid,
          authorUid: data['authorUid'] || d.ref.parent?.parent?.id || p.uid,
          ownerUid: data['ownerUid'] || d.ref.parent?.parent?.id || p.uid,
        } as unknown as UserImage;
      });
      
      // Sort: newest first
      list.sort((a, b) => {
        const getSeconds = (createdAt: unknown): number => {
          if (createdAt && typeof createdAt === 'object' && 'seconds' in createdAt) {
            return (createdAt as { seconds: number }).seconds || 0;
          }
          return 0;
        };
        const timeA = getSeconds(a.createdAt);
        const timeB = getSeconds(b.createdAt);
        return timeB - timeA;
      });
      this.userImages.set(list);
    } catch (err) {
      console.error('Error loading user images:', err);
    } finally {
      this.isLoadingUserImages.set(false);
    }
  }

  triggerDelete(item: { id?: string; imageUrl?: string; bannerImage?: string; companyLogoUrl?: string; posterUrl?: string; videoUrl?: string; images?: string[]; galleryImages?: string[] }, type: 'images' | 'news' | 'jobs' | 'products' | 'videos') {
    if (!item || !item.id) return;
    this.deleteItemId.set(item.id);
    this.deleteItemType.set(type);
    
    const urls: string[] = [];
    if (item.imageUrl) urls.push(item.imageUrl);
    if (item.bannerImage) urls.push(item.bannerImage);
    if (item.companyLogoUrl) urls.push(item.companyLogoUrl);
    if (item.posterUrl) urls.push(item.posterUrl);
    if (item.videoUrl) urls.push(item.videoUrl);

    if (Array.isArray(item.images)) {
      for (const url of item.images) {
        if (typeof url === 'string') urls.push(url);
      }
    }
    if (Array.isArray(item.galleryImages)) {
      for (const url of item.galleryImages) {
        if (typeof url === 'string') urls.push(url);
      }
    }

    this.deleteItemUrls.set(Array.from(new Set(urls.filter(Boolean))));
    this.isDeleteModalOpen.set(true);
  }

  getStoragePathFromUrl(downloadUrl: string): string | null {
    if (!downloadUrl || !downloadUrl.startsWith("http")) return null;
    try {
      const url = new URL(downloadUrl);
      const pathParts = url.pathname.split('/o/');
      if (pathParts.length > 1) {
        let fullParsedPath = decodeURIComponent(pathParts[1]);
        if (fullParsedPath.includes('?')) {
          fullParsedPath = fullParsedPath.split('?')[0];
        }
        return fullParsedPath;
      }
    } catch (err) {
      console.warn("Failed to parse storage path from url:", downloadUrl, err);
    }
    return null;
  }

  async confirmDelete() {
    const p = this.profile();
    const itemId = this.deleteItemId();
    const type = this.deleteItemType();
    const urls = this.deleteItemUrls();
    if (!p || !itemId || !type) return;

    this.isDeletingProgress.set(true);

    try {
      // 1. Storage Deletion
      for (const downloadUrl of urls) {
        if (downloadUrl && downloadUrl.startsWith("http")) {
          const path = this.getStoragePathFromUrl(downloadUrl);
          if (path) {
            try {
              const storageRef = ref(storage, path);
              await deleteObject(storageRef);
            } catch (storageErr: unknown) {
              const err = storageErr as Record<string, unknown>;
              const code = err['code'];
              const status = err['status'];
              const message = typeof err['message'] === 'string' ? err['message'] : '';
              if (code === 'storage/object-not-found' || message.includes('not found') || status === 404) {
                console.warn("Storage object already missing (404), proceeding with Firestore doc deletion:", path);
              } else {
                console.warn("Could not delete from storage, proceeding with Firestore doc deletion:", path, storageErr);
              }
            }
          }
        }
      }

      // 2. Firestore Deletion
      const collectionPath = `accounts/${p.uid}/${type}`;
      const docRef = doc(db, collectionPath, itemId);
      await deleteDoc(docRef);

      // 3. Update arrays instantly
      if (type === 'images') {
        const currentList = this.userImages();
        const updated = currentList.filter(item => item.id !== itemId);
        this.userImages.set(updated);
      } else {
        const currentList = this.assets();
        const updated = currentList.filter(item => item.id !== itemId);
        this.assets.set(updated);
      }

      const typeLabelMap: Record<string, string> = {
        'images': 'Photo',
        'news': 'News Post',
        'jobs': 'Job Position',
        'products': 'Product',
        'videos': 'Video Feed'
      };
      
      const label = typeLabelMap[type] || 'Item';
      this.showToast(`${label} Successfully Deleted!`);
      this.closeDeleteModal();
    } catch (err: unknown) {
      console.error("Delete operation failed:", err);
      this.showToast("Failed to delete item. Please try again.", "error");
    } finally {
      this.isDeletingProgress.set(false);
    }
  }

  showToast(msg: string, type: 'success' | 'error' = 'success') {
    const currentId = this.toastTimeoutId();
    if (currentId) {
      clearTimeout(currentId);
    }
    this.toastMessage.set(msg);
    this.toastType.set(type);

    const newId = setTimeout(() => {
      this.toastMessage.set(null);
    }, 3000);
    this.toastTimeoutId.set(newId);
  }

  closeDeleteModal() {
    this.isDeleteModalOpen.set(false);
    this.deleteItemId.set(null);
    this.deleteItemUrls.set([]);
    this.deleteItemType.set(null);
  }

  confirmDeleteUserImage(img: UserImage) {
    this.triggerDelete(img, 'images');
  }

  cancelDeleteUserImage() {
    this.closeDeleteModal();
  }

  async executeDeleteUserImage() {
    await this.confirmDelete();
  }

  openEditUserImageModal(img: UserImage) {
    this.openUnifiedModalForEdit('image', img);
  }

  closeEditUserImageModal() {
    this.isUnifiedModalOpen.set(false);
  }

  async saveUserImageMetadata() {
    const p = this.profile();
    const img = this.imageToEdit();
    if (!p || !img || !img.id) return;

    this.isSavingImageMetadataProgress.set(true);
    try {
      const docRef = doc(db, `accounts/${p.uid}/images`, img.id);
      
      const finalStatus = this.imageEditMetadata().status;
      let finalVerificationStatus = this.imageEditMetadata().verificationStatus || img.verificationStatus || 'review';

      // Revert verification status to review if edited by regular user who does not have published access and is not Master Admin.
      if (!this.canManageOrganization() && !this.isOwner() && !this.authService.isMasterAdmin() && finalStatus !== 'unpublished') {
        finalVerificationStatus = 'review';
      }

      await updateDoc(docRef, {
        license: this.imageEditMetadata().license,
        status: finalStatus,
        verificationStatus: finalVerificationStatus
      });

      this.closeEditUserImageModal();
      this.loadUserImages();
    } catch (err) {
      console.error("Error saving photo metadata:", err);
    } finally {
      this.isSavingImageMetadataProgress.set(false);
    }
  }

  async deleteUserImage(img: UserImage) {
    this.confirmDeleteUserImage(img);
  }

  async loadChats() {
    const p = this.profile();
    if (!p || this.authService.user()?.uid !== p.uid) return;

    this.isLoadingChats.set(true);
    if (this.unsubscribeChatsList) {
      this.unsubscribeChatsList();
    }

    try {
      this.unsubscribeChatsList = this.chatService.listenToMyChats(
        p.uid,
        (chats) => {
          this.chats.set(chats);
          this.isLoadingChats.set(false);
        },
      );
    } catch (error) {
      console.error("Error loading chats", error);
      this.chats.set([]);
      this.isLoadingChats.set(false);
    }
  }

  ngOnDestroy() {
    if (this.unsubscribeContactsList) {
      this.unsubscribeContactsList();
      this.unsubscribeContactsList = null;
    }
    if (this.unsubscribeChatsList) {
      this.unsubscribeChatsList();
    }
    if (this.followStatsUnsub) {
      this.followStatsUnsub();
    }
  }

  openAddAssetModal() {
    const tab = this.selectedTab();
    if (tab === 'Images' || tab === 'Music') {
      this.isAddImageComponentOpen.set(true);
      return;
    }
    let type: 'image' | 'news' | 'job' | 'product' | 'video' = 'image';
    if (tab === 'News') type = 'news';
    else if (tab === 'Jobs') type = 'job';
    else if (tab === 'Products') type = 'product';
    else if (tab === 'Videos') type = 'video';
    this.openUnifiedModal(type);
  }

  openBotModal() {
    this.isBotModalOpen.set(true);
  }

  onImageComponentUploaded(newImg: UserImage) {
    this.userImages.update(list => [newImg, ...list]);
  }

  viewNewsDetail(id: string) {
    const ownerId = this.profile()?.uid || 'system';
    this.router.navigate(["/news", ownerId, id]);
  }

  viewAssetDetail(asset: any) {
    const tab = this.selectedTab();
    const ownerId = asset.ownerUid || asset.ownedBy || asset.uploadedBy || this.profile()?.uid || 'system';
    if (tab === 'News') {
      this.router.navigate(["/news", ownerId, asset.id]);
    } else if (tab === 'Jobs') {
      this.router.navigate(["/job", ownerId, asset.id]);
    } else if (tab === 'Products') {
      this.router.navigate(["/product", ownerId, asset.id]);
    } else if (tab === 'Videos') {
      this.router.navigate(["/video", ownerId, asset.id]);
    }
  }

  openEditAssetModal(asset: any) {
    const tab = this.selectedTab();
    let type: 'image' | 'news' | 'job' | 'product' | 'video' = 'image';
    if (tab === 'News') type = 'news';
    else if (tab === 'Jobs') type = 'job';
    else if (tab === 'Products') type = 'product';
    else if (tab === 'Videos') type = 'video';
    this.openUnifiedModalForEdit(type, asset);
  }

  closeAddAssetModal() {
    this.isAddingAsset.set(false);
    this.assetToCreate.set({});
  }

  onFileSelected(event: Event, field: string) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        this.assetToCreate.update((asset) => ({ ...asset, [field]: result }));
      };
      reader.readAsDataURL(file);
    }
  }

  async saveAsset() {
    const profile = this.profile();
    if (!profile) return;
    
    this.isCreatingAsset.set(true);
    try {
      const assetData = { ...this.assetToCreate() };
      const selected = this.selectedTab().toLowerCase();
      
      const finalStatus = assetData.status || 'published';
      let finalVerificationStatus = assetData.verificationStatus || 'review';
      
      // Enforce status review for non-admins if they try to edit and are not Master Admin
      if (!this.canManageOrganization() && !this.isOwner() && !this.authService.isMasterAdmin() && finalStatus !== 'unpublished') {
        finalVerificationStatus = 'review';
      }

      assetData.status = finalStatus;
      assetData.verificationStatus = finalVerificationStatus;

      // Handle custom collection paths
      const collectionPath = `accounts/${profile.uid}/${selected}`;
      let docRef: any;
      
      if (assetData.id) {
        docRef = doc(db, collectionPath, assetData.id);
        await setDoc(docRef, assetData, { merge: true });
      } else {
        assetData.createdAt = serverTimestamp();
        assetData.authorUid = this.authService.user()?.uid;
        assetData.ownedBy = profile.accountType === 'organization' ? 'Organization' : 'Individual';
        assetData.verificationStatus = 'review';
        assetData.status = assetData.status || 'published';
        docRef = doc(collection(db, collectionPath));
        assetData.id = docRef.id;
        await setDoc(docRef, assetData);
      }
      
      this.closeAddAssetModal();
      this.loadSelectedTabAssets();
    } catch (error) {
      console.error("Error saving asset: ", error);
    } finally {
      this.isCreatingAsset.set(false);
    }
  }

  async deleteAsset(asset: { id?: string; imageUrl?: string; bannerImage?: string; companyLogoUrl?: string; posterUrl?: string; videoUrl?: string; images?: string[]; galleryImages?: string[] }) {
    const tabName = this.selectedTab()?.toLowerCase();
    let type: 'images' | 'news' | 'jobs' | 'products' | 'videos' = 'news';
    if (tabName === 'news') type = 'news';
    else if (tabName === 'jobs' || tabName === 'job') type = 'jobs';
    else if (tabName === 'images' || tabName === 'image') type = 'images';
    else if (tabName === 'products' || tabName === 'product') type = 'products';
    else if (tabName === 'videos' || tabName === 'video') type = 'videos';

    this.triggerDelete(asset, type);
  }

  navigateToChat(chat: Chat) {
    this.router.navigate(["/chat", chat.id], {
      queryParams: {
        uid: chat.participantId,
        name: chat.participantName,
        photo: chat.participantPhoto,
      },
    });
  }

  private followStatsUnsub?: () => void;

  async loadFollowData(uid: string) {
    try {
      if (this.followStatsUnsub) {
        this.followStatsUnsub();
      }

      this.followStatsUnsub = this.followService.subscribeFollowStats(uid, (stats) => {
        this.followersCount.set(stats.followers);
        this.followingCount.set(stats.following);
      });

      if (this.authService.user()) {
        const isFollowing = await this.followService.checkIsFollowing(uid);
        this.isFollowing.set(isFollowing);
      }
    } catch (error) {
      console.error("Error loading follow data", error);
    }
  }

  async toggleFollow() {
    const p = this.profile();
    if (!p || this.isFollowLoading()) return;

    this.isFollowLoading.set(true);
    try {
      const currentlyFollowing = this.isFollowing();
      this.isFollowing.set(!currentlyFollowing);
      await this.followService.toggleFollow(p.uid, currentlyFollowing);
    } catch (error) {
      console.error("Error toggling follow", error);
      this.isFollowing.set(!this.isFollowing());
    } finally {
      this.isFollowLoading.set(false);
    }
  }

  async startChat() {
    const p = this.profile();
    if (!p) return;

    try {
      const chatId = await this.chatService.getOrCreateChat(
        p.uid,
        p.displayName,
        p.photoURL,
      );
      this.router.navigate(["/chat", chatId], {
        queryParams: {
          uid: p.uid,
          name: p.displayName,
          photo: p.photoURL,
        },
      });
    } catch (error) {
      console.error("Error starting chat", error);
    }
  }

  goBack() {
    const hasPreviousPage =
      typeof window !== "undefined" &&
      window.history.length > 1 &&
      (window.history.state?.navigationId === undefined || window.history.state.navigationId > 1);
    if (hasPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(["/search"]);
    }
  }

  navigateToSettings() {
    this.router.navigate(["/settings"]);
  }

  openConfirmModal(
    type: "email" | "password" | "signout" | "verify" | "deleteAsset",
  ) {
    this.modal.type.set(type);
    this.modalInput = "";

    switch (type) {
      case "email":
        this.modal.title.set("Change Email");
        this.modal.message.set(
          "Enter your new email address below. You will need to verify the new email to complete the change.",
        );
        this.modal.confirmText.set("Change Email");
        break;
      case "password":
        this.modal.title.set("Reset Password");
        this.modal.message.set(
          "We will send a password reset link to your current email address. Are you sure you want to proceed?",
        );
        this.modal.confirmText.set("Send Link");
        break;
      case "signout":
        this.modal.title.set("Sign Out");
        this.modal.message.set(
          "Are you sure you want to sign out of your Kicgle account?",
        );
        this.modal.confirmText.set("Sign Out");
        break;
      case "verify":
        this.modal.title.set("Verify Email");
        this.modal.message.set(
          "We will send a verification link to your email address. Please check your inbox and follow the instructions.",
        );
        this.modal.confirmText.set("Send Link");
        break;
      case "deleteAsset":
        this.modal.title.set("Delete Asset");
        this.modal.message.set(
          "Are you sure you want to delete this item? This action cannot be undone.",
        );
        this.modal.confirmText.set("Delete");
        break;
    }

    this.modal.isOpen.set(true);
  }

  closeModal() {
    this.modal.isOpen.set(false);
    this.assetToDelete.set(null);
  }

  async confirmAction() {
    const type = this.modal.type();
    this.closeModal();

    try {
      if (type === "deleteAsset") {
        const asset = this.assetToDelete();
        const p = this.profile();
        if (asset && asset.id && p) {
          if (this.selectedTab() === "News") {
            await deleteDoc(doc(db, "accounts", p.uid, "news", asset.id));
          } else {
            await deleteDoc(doc(db, "accounts", p.uid, this.selectedTab().toLowerCase(), asset.id));
          }
          this.assetToDelete.set(null);
          this.loadSelectedTabAssets();
        }
      } else if (type === "email") {
        if (
          !this.modalInput ||
          this.modalInput === this.authService.user()?.email
        ) {
          console.error("Please enter a different email address.");
          return;
        }
        await this.authService.updateEmail(this.modalInput);
      } else if (type === "password") {
        const email = this.authService.user()?.email;
        if (email) {
          await this.authService.resetPassword(email);
        }
      } else if (type === "signout") {
        await this.authService.signOut();
      } else if (type === "verify") {
        await this.authService.sendVerificationEmail();
      }
    } catch (err: unknown) {
      console.error("Action failed", err);
    }
  }

  signOut() {
    this.openConfirmModal("signout");
  }

  openAddAuthorizedPersonModal() {
    this.isAddMemberModalOpen.set(true);
  }

  async removeAuthorizedPerson(index: number) {
    const list = this.authorizedPersons();
    if (list && list[index]) {
      const member = list[index];
      if (member.uid) {
        await this.removeMember(member.uid, 'Removed authorized person');
      }
    }
  }

  async updateProfile(data: Partial<AuthProfile>) {
    const uid = this.authService.user()?.uid;
    if (!uid) return;
    try {
      await this.authService.updateProfile(uid, data);
      this.profile.update((p) => (p ? { ...p, ...data } : null));
    } catch (error) {
      console.error("Error updating profile", error);
    }
  }

  bookAppointment() {
    this.router.navigate(["/date", this.profile()?.uid]);
  }
}
